// ============================================================================
// ui/logo-step.js — Step 1: logo-first (V2 plan §4 + §11)
// ============================================================================
// Logo is the front door. Dropzone (drag-drop + file picker). On upload:
//   1. validate + classify (raster / SVG / non-displayable vector / bad file)
//   2. downscale oversized rasters BEFORE display (§11)
//   3. run extractPalette (palette-extract.js)
//   4. run the ~1.5s CHOREOGRAPHED BEAT (borrowed from the Gym Designer's min-
//      1.5s overlay): "Reading your logo…" → chips visibly pop → land in pickers
//   5. render the FIRST preview already in THEIR colors — never default-gray
// Reduced-motion (§11): the beat degrades to instant.
//
// Edge cases handled (§11): oversized → downscale; confidence 'low' → skip
// prefill (don't guess); decode failure / HEIC that can't decode → friendly
// message + "use original" style keep; EPS/AI/PDF vector → "attach at quote time".
// ============================================================================

import { h, replaceChildren, media } from './dom.js';
import { labelWithDots } from './busy-dots.js';
import { yesNoToggle } from './yes-no-toggle.js';
import { seeThroughChooser } from './see-through-chooser.js';
import { openTapFix } from './logo-tap-fix-sheet.js';
import { openModal } from './modal.js';
/* WD 490b: Ryan's 15 "logos that work" for the photo card (backgrounds cut, 192px; fetched only when the card opens) */
import exDb from './photo-card-examples/db.webp';
import exE from './photo-card-examples/e.webp';
import exLayton from './photo-card-examples/layton.webp';
import exPhillyPd from './photo-card-examples/philly-pd.webp';
import exRidgeView from './photo-card-examples/ridge-view.webp';
import exRigar from './photo-card-examples/rigar.webp';
import exSpartanBirds from './photo-card-examples/spartan-birds.webp';
import exSpringboro from './photo-card-examples/springboro.webp';
import exTigers from './photo-card-examples/tigers.webp';
import exValley from './photo-card-examples/valley.webp';
import exWexmoor from './photo-card-examples/wexmoor.webp';
import exAngryPanda from './photo-card-examples/angry-panda.webp';
import exBears from './photo-card-examples/bears.webp';
import exCherryValley from './photo-card-examples/cherry-valley.webp';
import exCinderwood from './photo-card-examples/cinderwood.webp';
import { carryTapEdits } from '../logo-tap-fix.js';
import { isPhone } from './mobile-launch.js';
import * as artworkHold from './artwork-hold.js';
import { retainOriginalForRep } from './artwork-upload.js';
import { createStepper, attachHoldRepeat } from './stepper.js';
import { rememberWeightNudge, weightNudgeKey } from './state.js';
import { createColorPicker } from './color-picker.js';
import { pickColorWithEyedropper } from './eyedropper.js';
import { extractPalette } from '../palette-extract.js';
import { extractLogoStructure, extractClearSpot, backfillClearSpot, CLEAR_SPOT_VERSION } from '../logo-structure.js';
import { remapLogoDataUrl } from '../logo-outline.js';

/**
 * The FULL-SIZE pixels, attached to a structure verdict so `applyLogoStructure` can ask the peel
 * whether each detected band has anything to act on (Ryan 2026-08-15, the Elite Edge lockup: an
 * outline chip whose ✕ measurably did nothing).
 *
 * ⛔ FULL SIZE, AND THAT IS THE WHOLE POINT — the first cut asked the 220px ANALYSIS copy and got
 * the wrong answer. `extractLogoStructure` works on a nearest-neighbour downscale, where a couple of
 * boundary pixels of an enclosed shell still land on the flood's path; measured on the Elite Edge
 * mark that copy reports the white band as reachable while the 691×200 raster the peel actually
 * edits reports **zero** pixels for it (`tools/probe-band-pixels.mjs`). A question about whether a
 * control can act has to be asked of the image the control edits.
 */
async function attachPeelSource(structure, source) {
  try {
    if (!structure || !source || !Array.isArray(structure.outlines) || !structure.outlines.length) return structure;
    const w = source.naturalWidth || source.width || 0;
    const h = source.naturalHeight || source.height || 0;
    if (!w || !h) return structure;
    const canvas = typeof OffscreenCanvas !== 'undefined'
      ? new OffscreenCanvas(w, h)
      : Object.assign(document.createElement('canvas'), { width: w, height: h });
    const cx = canvas.getContext('2d', { willReadFrequently: true });
    if (!cx) return structure;
    cx.imageSmoothingEnabled = false;
    cx.drawImage(source, 0, 0);
    structure.peelSource = cx.getImageData(0, 0, w, h);
  } catch { /* a peel-source we cannot build just means "assume peelable", i.e. today's behaviour */ }
  return structure;
}

import { LOGO_SCALE_MIN, LOGO_SCALE_MAX, BIG_LOGO_SCALE_MAX, LOGO_SCALE_REACH, paintedLogoWidth, seedDefaultSizes, edgeOf } from '../render-engine.js';
import { chooseBackgroundKey, measurePocketProfile, pocketsReadAsCounters, restoreInkPockets } from '../bg-detect.js';
import { modelFor } from '../products.js';
import { DEFAULT_SIZE_PCT, shownLogoTones, normalizeHex, contrastRatio, DEFAULT_BODY_COLOR, isNearBlackHex, isNudgeableLogo, clampBigNudge, BIG_NUDGE_STEP, BIG_NUDGE_MAX } from '../design-profile.js';
import { tick, thump } from './haptics.js';
import { setLogo, applyPalette, applyLogoStructure, smartStart, applyIdentityText, recolorEverywhere, markStyleUserEdited, applyDefaultPlateColors, restoreBodyColor, recascadeColorDefaults, dropAutoOutlinesWhenFillReads, assignOutlineColors, correctIllegibleFill, effectivePlateHex, seedKeyedFieldSwatch, seatedFieldHex, syncLogoContrastRing, resyncAddedLogoRings, plateDealDeck } from './state.js';
import { wdTrack, WD_EVENTS } from '../analytics.js';
import { rememberTapFixBase, recallTapFixBase } from './tapfix-base-store.js';

// Reveal-choreography timing (Ryan 2026-07-16 — "more dramatic reveal/delay").
// The upload used to fire chips + the who-card almost instantly; these dwell
// times pace it out so each moment reads. Reduced-motion collapses them to ~0.
const REVEAL_CLEAN_MS = 1050;    // "Cleaning up your logo…" dwell before extraction
const REVEAL_CLEANED_MS = 950;   // "Cleaned it up ✓" dwell after a REAL cleanup
const REVEAL_EXTRACT_MS = 900;   // "Extracting your colors…" before the first chip lands
const REVEAL_COLOR_MS = 720;     // dwell as each color chip lands, one at a time
/* THE INTERVAL BETWEEN ONE BLOCK APPEARING AND THE NEXT (Ryan 2026-08-08: *"perhaps things need to
   populate with a more consistent cadence as well"*). Was `REVEAL_SETTLE_MS`, used once, for the
   pause on the full palette — which made it look like a one-off flourish rather than the tempo of
   the sequence, and the beats that had no constant simply had no pause. Renamed for the job it
   does, so the next block anybody adds has an obvious thing to wait. Same 550ms; nothing re-timed. */
const REVEAL_BLOCK_MS = 550;
/* How long the "Who's this for?" card waits, un-engaged, before pointing at its first field.
   Same 5s the pane sheet and the forward button use — one interval for every nudge in the
   tool, so they cannot drift into three different ideas of "stuck". */
const WHO_HINT_IDLE_MS = 5000;
/* ⚠ DESKTOP WAITS LONGER, AND IS THE ONE NUDGE IN THE TOOL THAT DELIBERATELY BREAKS THE 5s RULE
   ABOVE (Ryan 2026-08-15: *"maybe not 5s.. maybe 7 or 8"*). Every other nudge fires at a buyer who
   has been given NO pointer — an unfocused phone card, a collapsed sheet — so 5s is a rescue. Here
   the caret is already sitting in Organization, so the ring is an ESCALATION on top of a signpost
   that is already up, and at 5s it nags somebody who is still reading the two field names.
     It is a nudge toward the FIELD rather than the CTA on purpose: the organization name is what
   makes the design worth looking at, so the extra seconds buy typing time rather than hurrying
   anyone past it. */
const WHO_HINT_IDLE_DESKTOP_MS = 8000;
// Watch the weight render before routing to Customize Design. Sized to the design-initiation
// reveal spin (reveal-3d: 650ms windup + 2000ms whip/settle = 2650ms) + a little for
// the texture build, so the plate has settled FACE-ON with their design before the buyer is
// moved off this tab — never mid-spin/edge-on (Ryan 2026-07-16).
const REVEAL_BUILD_MS = 2900;
const COLOR_MIN_FRACTION = 0.03; // accents below this pixel share aren't shown (§ spec)
// Where the build flow lands (main.js step map: 1 Pick Weights · 2 Add Logo ·
// 3 Customize Design · 4 Request Quote). `STEP_QUOTE` went with the next-step fork —
// the quote step is now reached through the pane's nav footer (ui/step-nav.js).
const STEP_DESIGN = 3;
// Only downscale genuinely huge uploads — the logo is baked into the plate
// texture (up to 4096² on desktop), so a 1024px cap threw away the detail on
// high-def logos (fine features like laces went fuzzy at small slot sizes). At
// this cap a normal high-def logo (e.g. 3840px) passes through UNTOUCHED, keeping
// its original compact bytes; only truly oversized rasters get re-encoded down.
/* ⛔ IT HAS TO ASK THE DEVICE, because the thing it claims to match already does (Ryan 2026-08-08:
   uploading a logo on a phone booted the whole page back to the storefront).
     `main.js`'s `fullTexSize()` is `isPhone() ? 2048 : 4096` — the plate texture on a phone has been
   2048 for as long as the mobile pass has existed. This constant said "matches the max plate-texture
   resolution" and then hard-coded the DESKTOP one, so a phone decoded and held a bitmap at up to
   4096² to feed a 2048² texture: ~67 MB of RGBA for detail that is thrown away on the very next
   step, at the single heaviest moment in the app (source raster + composite + cleanup round-trip +
   the live WebGL context, all at once). That is what gets a tab discarded, and a discarded tab
   reloads to the top of the page — which is the "booted back to the home screen".
     2048 quarters it to ~17 MB and costs nothing visible: the texture it feeds cannot show more.
     ⚠ The exact shape CLAUDE.md §0b warns about — two places needing the same answer, one of them
   wrong. Keep them together: if `fullTexSize()` changes, this changes. */
const MAX_DISPLAY_EDGE = isPhone() ? 2048 : 4096; // = fullTexSize() in main.js. Keep in step.
const NON_DISPLAY_VECTOR = /\.(eps|ai)$/i; // detected → "attach at quote time" (PDF is now rendered)
const MAX_FILE_BYTES = 30 * 1024 * 1024; // reject huge files before decode (WD-06 OOM guard)
const HEIC_RE = /heic|heif/i; // iPhone default — can't decode in most browsers (WD-07)

// Tier-1 server cleanup (Replicate via rfq-worker). The endpoint self-skips when
// its $25/mo cap is hit or on any error, so this is always a graceful enhancement
// over the free client-side white-key — never a hard dependency.
const WD_WORKER_BASE = 'https://rfq-worker.ryan-29b.workers.dev';
// ⚠ SAME-ORIGIN IN DEV, WORKER-DIRECT IN PROD. The worker's CORS allowlist doesn't include
// localhost, so every dev upload logged `Upscale → SKIPPED:network` and the whole pipeline keyed
// un-upscaled sources — which is what shredded Hanover's 1px keyline into the "super broken"
// fringe Live never shows (Ryan 2026-08-18). Vite proxies `/wd-logo-cleanup` to the worker
// server-side (vite.config.js), so dev now exercises the SAME upscale+rembg path as Live.
const CLEANUP_ENDPOINT = import.meta.env.DEV ? '/wd-logo-cleanup' : `${WD_WORKER_BASE}/wd-logo-cleanup`;
const UPSCALE_MIN_EDGE = 400; // shortest source edge below this → request an upscale
/* ── WD 490: "IS THIS A PHOTO?" (Ryan 2026-11-01) ────────────────────────────────────────────────
   2 of the first 15 submissions were photos (a phone screenshot, a building) and each cost a call and
   an email. The worker asks Claude LOGO or PHOTO from a 512px copy (rfq-worker lib/wd-photo-check.js
   has why a model and not a pixel rule: measured, every pixel feature overlaps real logos). The app
   acts ONLY on `show`, which the worker sets only when Admin: Weight Designer Photo Check is on.
   ⛔ IT NEVER BLOCKS: any failure, a slow answer (PHOTO_WAIT_MAX_MS), a closed card, all carry on.
   ⛔ ASKED BEFORE ANYTHING IS CHANGED: the check starts the moment the file is picked (beside the
   decode) and is awaited right after it, before the keyer, the cleanup or the design are touched -
   so "Upload a logo" leaves nothing to undo but the replace's own grey-out. */
const PHOTO_CHECK_ENDPOINT = import.meta.env.DEV ? '/wd-photo-check' : `${WD_WORKER_BASE}/wd-photo-check`;
const PHOTO_WAIT_MAX_MS = 6000;
const SLOW_NOTE_MS = 12000; // WD 491: a logo lands in ~10 s; past this the caption gets its one quiet line
/* The "logos that work" grid on the card - Ryan's 15 picks (2026-11-01), in his order. Empty = no grid. */
const PHOTO_CARD_EXAMPLES = [exDb, exE, exLayton, exPhillyPd, exRidgeView, exRigar, exSpartanBirds, exSpringboro, exTigers, exValley, exWexmoor, exAngryPanda, exBears, exCherryValley, exCinderwood].map((src) => ({ src }));

// ── C2 diagnostic breadcrumb ────────────────────────────────────────────────
// The Tier-1 cleanup used to collapse every outcome (cap / error / unconfigured /
// bad-image / network) into a bare `null`, so a silent no-op was indistinguishable
// from a real skip. wdDebug() logs each cleanup stage to the console ALWAYS, and —
// with `?wd_debug=1` — to a small on-screen panel, so the exact path a given logo
// takes through Tier-0/Tier-1 is visible live on the dev server.
const WD_DEBUG = (() => {
  try { return new URLSearchParams(location.search).has('wd_debug'); } catch { return false; }
})();
let wdDebugPanel = null;
let wdDebugLogEl = null;
function wdDebug(msg) {
  // eslint-disable-next-line no-console
  console.info('[wd-cleanup]', msg);
  if (!WD_DEBUG) return;
  if (!wdDebugPanel) {
    wdDebugPanel = document.createElement('div');
    wdDebugPanel.id = 'wd-debug-log';
    wdDebugPanel.setAttribute(
      'style',
      'position:fixed;left:8px;bottom:8px;z-index:99999;max-width:380px;' +
        'font:11px/1.45 ui-monospace,Menlo,monospace;color:#bfe;background:rgba(0,10,20,.9);' +
        'border:1px solid #0af;border-radius:6px;padding:6px 8px;',
    );
    // Toolbar: Copy (all lines → clipboard) + Clear.
    const bar = document.createElement('div');
    bar.setAttribute('style', 'display:flex;gap:6px;justify-content:flex-end;margin-bottom:5px;');
    const mkBtn = (label, fn) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.textContent = label;
      b.setAttribute('style', 'font:600 11px ui-monospace,Menlo,monospace;background:#0af;color:#012;border:0;border-radius:4px;padding:3px 10px;cursor:pointer;');
      b.addEventListener('click', fn);
      return b;
    };
    const copyBtn = mkBtn('Copy', () => {
      const text = wdDebugLogEl ? wdDebugLogEl.innerText : '';
      const done = () => { copyBtn.textContent = 'Copied!'; setTimeout(() => (copyBtn.textContent = 'Copy'), 1200); };
      if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(text).then(done).catch(() => {});
      else { // fallback for non-secure contexts
        const ta = document.createElement('textarea');
        ta.value = text; document.body.appendChild(ta); ta.select();
        try { document.execCommand('copy'); done(); } catch { /* noop */ }
        ta.remove();
      }
    });
    const clearBtn = mkBtn('Clear', () => { if (wdDebugLogEl) wdDebugLogEl.textContent = ''; });
    bar.append(copyBtn, clearBtn);
    wdDebugLogEl = document.createElement('div');
    wdDebugLogEl.setAttribute('style', 'max-height:38vh;overflow:auto;white-space:pre-wrap;');
    wdDebugPanel.append(bar, wdDebugLogEl);
    document.body.appendChild(wdDebugPanel);
  }
  const line = document.createElement('div');
  line.textContent = msg;
  wdDebugLogEl.appendChild(line);
  wdDebugLogEl.scrollTop = wdDebugLogEl.scrollHeight;
}

export function createLogoStep(ctx) {
  const dropzone = h(
    'div',
    {
      class: 'wd-dropzone',
      role: 'button',
      tabindex: '0',
      // A phone cannot drag and drop, so it is not announced there either — the visible hint drops
      // the same phrase in CSS below (Ryan 2026-07-28). Read once at construction, like the tool's
      // other `isPhone()` calls; the surface does not change under a session.
      'aria-label': isPhone()
        ? 'Upload your logo — press Enter to browse'
        : 'Upload your logo — drag and drop, or press Enter to browse',
      // Engaging the dropzone = "I'm designing my own" → drop the parade immediately,
      // so its overlay isn't left up behind the file picker (Ryan 2026-07-18).
      onClick: () => { ctx.exitShowcase?.(); input.click(); },
      onKeydown: (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          ctx.exitShowcase?.();
          input.click();
        }
      },
      onDragover: (e) => {
        e.preventDefault();
        dropzone.classList.add('is-dragging');
      },
      onDragleave: () => dropzone.classList.remove('is-dragging'),
      onDrop: (e) => {
        e.preventDefault();
        dropzone.classList.remove('is-dragging');
        const file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
        if (file) handleFile(file);
      },
    },
    // Content lives in an inner wrapper so the pulse can COUNTER-scale it: the card
    // box breathes but the text stays a steady, readable size (Ryan 2026-07-16).
    // pointer-events:none on the wrapper so a drag crossing it never fires dragleave
    // on the dropzone → no is-dragging flicker (WD-36).
    h('div', { class: 'wd-dropzone-inner', style: { pointerEvents: 'none' } },
      h('div', { class: 'wd-dropzone-icon', 'aria-hidden': 'true', html: UPLOAD_ICON }),
      // Verb-first + lit-by-default so a non-technical buyer can't miss the first step
      // (Ryan 2026-07-18). "or drag & drop" keeps that affordance discoverable without
      // making the primary action ambiguous; formats reassure their file will be taken.
      h('div', { class: 'wd-dropzone-title' }, 'Click to upload your logo'),
      // Two spans so the phone can drop the drag affordance in CSS rather than this file branching on
      // the viewport. The separator lives INSIDE the drag span, so hiding it leaves "PNG, JPG, …"
      // clean rather than stranding a leading "· " — the same trick the product bar's segments use.
      h('div', { class: 'wd-dropzone-hint' },
        h('span', { class: 'wd-dropzone-hint__drag' }, 'or drag & drop · '),
        h('span', { class: 'wd-dropzone-hint__types' }, 'PNG, JPG, WebP, SVG, or PDF'),
      ),
    ),
  );

  // Hint matches this list (WD-35): PNG/JPG/WebP/SVG/PDF. HEIC is still allowed so
  // iPhone users can pick it — we then show a tailored "export as PNG/JPG" note.
  // PDF is rendered to a raster in handleFile (a designer's vector-as-PDF is common).
  const LOGO_ACCEPT = 'image/png,image/jpeg,image/webp,image/svg+xml,image/heic,image/heif,application/pdf';
  const input = h('input', {
    type: 'file',
    accept: LOGO_ACCEPT,
    class: 'wd-visually-hidden',
    onChange: (e) => {
      const file = e.target.files && e.target.files[0];
      if (file) handleFile(file);
      e.target.value = ''; // allow re-selecting the same file
    },
  });

  // Re-upload (Ryan 2026-07-16): a second file input behind the "Upload different
  // logo" tile that runs the LIGHT swap path — re-clean + re-pull colors + re-dress
  // the plate, WITHOUT replaying the who-card / design-quote fork (already onboarded).
  const replaceInput = h('input', {
    type: 'file',
    accept: LOGO_ACCEPT,
    class: 'wd-visually-hidden',
    onChange: (e) => {
      const file = e.target.files && e.target.files[0];
      if (file) handleFile(file, { replace: true });
      e.target.value = '';
    },
  });
  const replaceTile = h(
    'button',
    {
      type: 'button',
      class: 'wd-reveal-replace',
      'aria-label': 'Upload a different logo',
      onClick: () => replaceInput.click(),
      onDragover: (e) => { e.preventDefault(); replaceTile.classList.add('is-dragging'); },
      onDragleave: () => replaceTile.classList.remove('is-dragging'),
      onDrop: (e) => {
        e.preventDefault();
        replaceTile.classList.remove('is-dragging');
        const file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
        if (file) handleFile(file, { replace: true });
      },
    },
    h('span', { class: 'wd-reveal-replace__icon', 'aria-hidden': 'true', html: UPLOAD_ICON, style: { pointerEvents: 'none' } }),
    h('span', { style: { pointerEvents: 'none' } }, 'Upload different logo'),
  );

  // ── Persistent reveal panel (Ryan 2026-07-16) ────────────────────────────
  // Replaces the dropzone once an upload starts and STAYS mounted through the
  // whole choreography, so the cleaned logo + found colors remain visible while
  // the "Who's this for?" card slides in below them (they no longer get covered).
  const revealImg = h('img', { class: 'wd-reveal-logo', alt: 'Your logo', decoding: 'async' });
  const revealThumb = h('div', { class: 'wd-reveal-thumb' }, revealImg);
  // The caption doubles as the logo block's LABEL: it narrates through the sequence
  // and settles on "Your logo", sitting ABOVE the thumbnail and sharing the exact
  // "YOUR COLORS" heading style so the two section labels read identically (Ryan
  // 2026-07-16).
  const revealCaption = h('div', { class: 'wd-reveal-label wd-reveal-caption', 'aria-live': 'polite' }, '');
  /* ── WD 491: A SLOW UPLOAD SAYS SO, AND NOTHING ELSE (Ryan 2026-11-01) ───────────────────────────────────────
     A photo the buyer kept ("It's my logo, use it") took 20-30 s and looked frozen. ⛔ NEUTRAL ON PURPOSE (Ryan:
     *"wouldn't that go against the previous recommendation not to use photos at all though?"*): it never names a
     photo, so it cannot argue with the buyer's own answer, and it fires for ANY upload past SLOW_NOTE_MS - a very
     detailed logo too. One muted line under the caption (not a pill, not a box), gone when the caption settles. */
  /* Always in the tree, opened and closed by CSS (`is-shown`) so the content below SLIDES rather than snaps (Ryan
     11-01: "the slide down and slide up should be smooth as opposed to a snap"). */
  const slowNote = h('div', { class: 'wd-reveal-slow', 'aria-hidden': 'true' },
    h('p', { class: 'wd-reveal-slow__text', 'aria-live': 'polite' }, 'Still working. Detailed images take a little longer.'));
  const showSlowNote = (on) => { slowNote.classList.toggle('is-shown', on); slowNote.setAttribute('aria-hidden', on ? 'false' : 'true'); };
  let slowTimer = 0;
  const colorsRow = h('div', { class: 'wd-reveal-colors' });
  // ── AN EXPLICIT "EDIT" (Ryan 2026-07-28) ──────────────────────────────────────────────────────
  // "add an Edit button next to the imported logo colour swatches after they load — that way people
  // know they're editable, otherwise people might not click on them."
  //   The chips have been editable since 2026-07-26 and nothing SAYS so. A swatch reads as a readout,
  // not a control: it has no rim, no glyph, and the one hint it carries — a cursor change — does not
  // exist on the device most of these sessions happen on. A labelled button is the only affordance
  // that survives a phone, and it is also where the multi-colour flow belongs, because it is about
  // the SET rather than about whichever chip happened to be tapped.
  //   ⚠ IT IS THE CUSTOMIZE-DESIGN ✎ CHIP, NOT A BUTTON OF ITS OWN (Ryan 2026-07-28: "the Edit button
  // should be just like the Edit buttons on the Customize Design tab that have the pencil logo on
  // them… aligned with the swatches at the end of the list"). Same four classes as the fill and
  // outline row editors, so it is that control rather than a look-alike of it — one place to retune
  // all three. A first pass put a text pill in the heading row; this is better on both counts, since
  // sitting at the END OF THE SWATCH ROW is what ties it to the things it edits, and a buyer who has
  // met the ✎ one tab over already knows what it does.
  //   ⚠ GLYPH OVER WORD, both inside the button — the shape `toolWrap` gives every ✎ and + on the
  // Customize Design tab (Ryan 2026-07-28: "it doesn't say 'Edit' underneath it like they do on the
  // Customize Design tab"). Built inline rather than by calling `toolWrap`, because that helper also
  // returns the column wrapper those rows need for their size steppers, and this row has none — the
  // two spans are the part that matters, and `.wd-cchip--tool` (column, 1px gap) already lays them
  // out because this button wears the same classes.
  const editColorsBtn = h('button', {
    type: 'button', class: 'wd-cchip wd-cchip--tool wd-flat-glass wd-cchip--edit',
    title: 'Edit your logo colors',
    'aria-label': 'Edit your logo colors',
    onClick: (e) => { e.stopPropagation(); editRevealColors(); },
  }, h('span', { class: 'wd-tool__icon' }, '✎'), h('span', { class: 'wd-tool__label' }, 'Edit'));
  const colorsBlock = h(
    'div',
    { class: 'wd-reveal-colorsblock', hidden: true },
    h('div', { class: 'wd-reveal-label' }, 'Your colors'),
    colorsRow,
  );
  /**
   * Keep the ✎ at the END of the swatch row, and only while there is something to edit.
   *
   * `append` MOVES an element that is already in the DOM, so calling this after each chip lands during
   * the reveal keeps the pencil trailing the newest swatch rather than being overtaken by it — and it
   * survives `replaceChildren(colorsRow, [])`, which every rebuild path uses, by simply being put back
   * when the chips return.
   */
  const syncEditColorsBtn = () => {
    if (colorsRow.querySelector('.wd-reveal-chip')) colorsRow.append(editColorsBtn);
    else editColorsBtn.remove();
  };

  // ── editing a extracted colour from THIS tab (Ryan 2026-07-26) ────────────────────────────────
  // The chips used to be a read-only trophy case: the extractor's answer, take it or leave it. If it
  // read your blue a shade off, the only cure was to reach the Customize Design tab and hunt for the
  // surface wearing it. Now a chip opens the same picker the CD tab uses, and "Update color" changes
  // that colour EVERYWHERE the design uses it — see `recolorEverywhere`, which is also what makes the
  // change show up on the CD tab's swatches.
  //   The picker is this tab's OWN instance rather than the design panel's. The panel builds its
  // picker inside a closure with `cpApply` / `render()` wired to its own rebuild, and there is no
  // handle on it from here; a second instance is ~nothing (one detached popover) and the two can
  // never be open at once, because they live on different steps.
  //   Built LAZILY, on the first click: most sessions never edit a swatch, and this way an upload
  // doesn't pay for a popover nobody opens.
  let revealPicker = null;
  const revealPickerFor = () => {
    if (revealPicker) return revealPicker;
    revealPicker = createColorPicker({
      supportsEyedropper: typeof window !== 'undefined' && !!window.isSecureContext,
      // Sample straight off the page — the buyer's own logo is sitting right there above the chips,
      // which is the most likely thing they want to match.
      onEyedrop: () => pickColorWithEyedropper(el.closest('.weight-designer') || document.body)
        .catch(() => null),
      // `live` is false only when a gesture ENDS — see the live-preview block below for why that is
      // the whole design rather than a throttle on the live ones.
      onChange: (hex, live) => { if (!live) previewTo(hex); },
    });
    return revealPicker;
  };
  /**
   * ONE builder for a "Your colors" chip, used by BOTH paths that put them on screen — the upload
   * reveal and `hydrate()`'s resume (Ryan 2026-07-26: "the swatches look clickable… but on click,
   * nothing happens"). The resume path built a plain `<span>`, which the `.wd-reveal-chip` rule
   * styles identically — so a resumed session had chips that hovered like buttons and did nothing.
   * Two builders for one thing is what let that happen; there is now one.
   *   `landed` skips the entry animation for chips that were never revealed, only restored.
   */
  function revealChip(hex, { landed = false } = {}) {
    const chip = h('button', {
      type: 'button', class: 'wd-reveal-chip' + (landed ? ' is-in' : ''),
      style: { background: hex }, title: hex, 'aria-label': `Adjust ${hex}`, 'data-hex': hex,
      // Read the CURRENT hex off the chip, never the one captured at build time: edit it twice in
      // a row and the second open must start from where the first left it.
      // Opens the SAME popover the ✎ opens, already pointed at this swatch — see editRevealColors.
      onClick: (e) => { e.stopPropagation(); editRevealColors(chip); },
    });
    return chip;
  }
  /**
   * Repaint the TAB'S OWN THUMBNAIL in the buyer's colours (Ryan 2026-07-26: "I want the preview
   * image of the logo to update as per the new colors… it's not dynamic like I want").
   *
   * The thumbnail has always shown the uploaded file, which is right up until the moment they start
   * recolouring it — after that it is a picture of a logo they no longer have. It is also the only
   * view of the mark on this tab, so leaving it stale makes the swatch edit look like it did nothing.
   *
   * Built with the same `remapLogoDataUrl` that produces the plate's composite, so the two cannot
   * disagree about what a recolour looks like. It applies the buyer's OWN edits only — the band
   * overrides and the tone swaps — and deliberately not the plate-contextual effects (the conflict
   * peel, the vanishing-ink flatten): those are facts about the plate the mark is sitting on, and
   * this thumbnail is the mark itself, on no plate at all.
   *
   * `previewJob` guards the async: two quick edits must not let the slower remap land last.
   */
  /** The artwork this step is CURRENTLY showing, from the moment it is built until the build beat
   *  hands it to `setLogo`. Before that the profile knows nothing about it — see the note in
   *  `refreshRevealPreview`, which is the only reader. */
  let pendingLogo = null;
  let previewJob = 0;
  /** Declared inks of the CURRENT upload when it is a vector (see `svgDeclaredInks`); null for
   *  rasters. Set per upload in `handleFile`, read by both `extractPalette` call sites. */
  let pendingSvgInks = null;
  async function refreshRevealPreview() {
    const p = ctx.state.designProfile;
    const logoEl = (p.elements || []).find((e) => e.type === 'logo' && e.logo && e.logo.dataUrl);
    /* ⛔ THE PRE-BUILD FALLBACKS ARE NOT BELT-AND-BRACES — THEY ARE THE ADD-LOGO TAB (Ryan
     * 2026-08-12, Ridge View). Until "Design My Weights" there is no logo ELEMENT and no
     * `savedLogo`: `setLogo` is what writes both and it does not run until the build beat. So this
     * function found no source and no swaps for the entire tab it lives on, and returned before
     * doing anything — which is why the chip updated and the mark under YOUR LOGO did not.
     *   `pendingLogo` is this step's own record of the artwork it is showing, and
     * `savedLogoToneSwaps` is where a pre-build recolour now lands (see `recolorEverywhere`). */
    const src = (logoEl && logoEl.logo.dataUrl) || (p.savedLogo && p.savedLogo.dataUrl)
      || (pendingLogo && pendingLogo.dataUrl) || null;
    if (!src) return;
    const swaps = (logoEl && Array.isArray(logoEl.logoToneSwaps) && logoEl.logoToneSwaps.length)
      ? logoEl.logoToneSwaps
      : (Array.isArray(p.savedLogoToneSwaps) ? p.savedLogoToneSwaps : []);
    const bands = (p.logoStructure && Array.isArray(p.logoStructure.bands)) ? p.logoStructure.bands : [];
    const bandColors = {};
    if (logoEl && logoEl.logoBandColors && typeof logoEl.logoBandColors === 'object') {
      for (const k of Object.keys(logoEl.logoBandColors)) {
        const i = k | 0;
        if (i >= 0 && i < bands.length && logoEl.logoBandColors[k]) bandColors[i] = logoEl.logoBandColors[k];
      }
    }
    const hasBands = Object.keys(bandColors).length > 0;
    const job = ++previewJob;
    // Nothing recoloured → don't touch the thumbnail at all. The upload beat owns it (showReveal, and
    // the "we cleaned it up" crossfade), and re-assigning `src` from here would fight them. The one
    // exception is undoing our own work: after a Reset, put the original back.
    if (!swaps.length && !hasBands) {
      if (revealImg.dataset.wdRecolored) { revealImg.src = src; delete revealImg.dataset.wdRecolored; }
      return;
    }
    const url = await remapLogoDataUrl(src, bands, [], null, hasBands ? bandColors : null, swaps, p.logoStructure?.fillHex || null).catch(() => src);
    if (job !== previewJob) return; // a newer edit already owns the thumbnail
    revealImg.src = url || src;
    revealImg.dataset.wdRecolored = '1';
  }
  /**
   * Open the picker on one extracted colour. Commit-only ("Update color"), matching the CD tab's
   * swatch ✎ — a live preview here would re-composite the logo raster on every pointer move.
   */
  // ── LIVE PREVIEW, ON DRAG-STOP (Ryan 2026-07-28) ─────────────────────────────────────────────
  // "when you adjust a colour on the colour menu, nothing happens on the model in real time…
  // maybe not on drags, but on drag and stops?"
  //
  // Exactly the right split, and the picker already draws it: `onChange(hex, live)` passes
  // `live: true` for every frame of a drag and `live: false` once the finger lifts (and on Enter/blur
  // in the hex box). Previewing on the false ones costs one remap per gesture instead of one per
  // frame — which is what made this commit-only in the first place, since each remap re-rasters the
  // buyer's logo and rebuilds the 3D texture.
  //
  // ⚠ A PREVIEW IS A REAL MUTATION, so it has to be reversible, and `recolorEverywhere` is a
  // one-way swap: it replaces hex X with Y wherever it appears and cannot tell which of them was
  // "the one being edited". Two consequences shape everything here:
  //   · every step goes from `cur` (what is on the plate now) rather than from the original, so the
  //     design is walked forward and back rather than accumulating;
  //   · a preview onto a colour the palette ALREADY holds is DECLINED. Landing red exactly on green
  //     would merge the two irreversibly — after that swap nothing in the profile distinguishes them,
  //     so the revert would drag the real green along with it. Confirming that value still works
  //     (a deliberate merge is a legitimate edit); only the speculative preview declines it.
  let livePreview = null; // { chip, from, cur, others:Set<string> } while a picker is open
  const HEX = (v) => String(v || '').toUpperCase();
  /** Arm the preview for chips[idx] — the colours of the OTHER chips are what it must not land on. */
  function startPreview(chips, idx) {
    const chip = chips[idx];
    if (!chip) { livePreview = null; return; }
    const from = HEX(chip.dataset.hex);
    livePreview = {
      chip,
      from,
      cur: from,
      others: new Set(chips.filter((_, i) => i !== idx).map((c) => HEX(c.dataset.hex)).filter(Boolean)),
    };
  }
  /** Put `to` on the plate now. Cheap to call with an unchanged or unusable value — it no-ops. */
  function previewTo(hex) {
    if (!livePreview) return;
    const to = HEX(hex);
    if (!to || to === livePreview.cur || livePreview.others.has(to)) return;
    if (!recolorEverywhere(ctx.state, livePreview.cur, to)) return;
    livePreview.cur = to;
    // ⚠ THE PLATE IS NOW SHOWING SOMETHING NOBODY CONFIRMED, and autosave must not file it as the
    // buyer's design. `state.ui.previewing` is read by main.js's periodic save exactly as
    // `choreographing` already is — the same idea, for the same reason: a timer that fires mid-gesture
    // should not decide what the design IS.
    ctx.state.ui.previewing = livePreview.cur !== livePreview.from;
    void refreshRevealPreview();          // the tab's own thumbnail, in the new colours
    if (hasBuilt) ctx.requestRender?.();  // …and the 3D model, which is the point of the exercise
  }
  /** Keep what is on the plate: the chip catches up, the CD tab syncs, and it becomes undoable. */
  function commitPreview() {
    const lp = livePreview;
    livePreview = null;
    ctx.state.ui.previewing = false; // whatever is on the plate is the buyer's now
    if (!lp || lp.cur === lp.from) return;
    /* THE COMMIT, NOT THE PREVIEW, IS WHERE THE LATCH GOES (Ryan 2026-08-08). Every frame of the
       drag walked the design forward through `recolorEverywhere`, and `revertPreview` walks it back;
       latching on the mutation would make a colour they scrolled past and abandoned permanent as far
       as the holes flip is concerned. Reaching this line means they kept it. */
    markStyleUserEdited(ctx.state.designProfile);
    lp.chip.style.background = lp.cur;
    lp.chip.title = lp.cur;
    lp.chip.dataset.hex = lp.cur;
    ctx.refreshControls?.();
    (ctx.recordHistoryNow || ctx.recordHistory)?.();
    ctx.announce?.(`Color updated to ${lp.cur}.`);
  }
  /** Undo whatever the preview put on the plate. Runs on tab switch, Escape, and light-dismiss —
   *  and, via `ctx.endLivePreview` below, on the way out of the page. */
  function revertPreview() {
    const lp = livePreview;
    livePreview = null;
    ctx.state.ui.previewing = false;
    if (!lp || lp.cur === lp.from) return;
    recolorEverywhere(ctx.state, lp.cur, lp.from);
    void refreshRevealPreview();
    if (hasBuilt) ctx.requestRender?.();
  }
  // The unload path's way to put a provisional colour back — see main.js's `beforeunload`. The
  // periodic autosave is skipped while `previewing` is up, but a buyer who closes the tab mid-drag
  // still gets a save, and it has to be a save of their DESIGN rather than of a colour they were
  // only looking at.
  ctx.endLivePreview = revertPreview;

  function editRevealColors(startChip) {
    const chips = [...colorsRow.querySelectorAll('.wd-reveal-chip')];
    if (!chips.length) return;
    // NO single-chip special case any more. The picker renders a tab strip only for 2+ targets, so a
    // one-colour logo already gets exactly the popover it used to — and routing it through here is
    // what gives it the live preview too, rather than leaving one flow behind on the old commit-only
    // path. The old single-colour `editRevealColor` had no callers left once this took over and was
    // deleted rather than parked — a second, non-previewing route to the same popover is precisely
    // the kind of duplicate that drifts (the two chip BUILDERS did exactly that in July).
    // Numbers, not hexes, for the tab LABEL. The swatch itself already says which colour this is —
    // far better than any name — and six `#1E5AA8`s would not fit the strip at any size.
    const targets = chips.map((c, i) => ({ key: String(i), label: String(i + 1), hex: c.dataset.hex || '' }));
    // ── A CHIP OPENS THE SAME MENU, ON ITS OWN TAB (Ryan 2026-07-28) ──────────────────────────
    // "if someone clicks an individual swatch instead of the Edit button, have it still open the
    // Edit menus but with that swatch's tab already selected."
    //   Which is the right shape: the two entry points differ only in where they START, so they
    // should not differ in what they OPEN. Tapping a chip and then finding no way to reach the others
    // without closing and re-opening was the old flow's real limitation, and this removes it without
    // taking anything away — the chip is still the fastest route to the colour you pointed at.
    //   Anchored to whatever was actually pressed, so the popover points at the swatch when a swatch
    // opened it and at the ✎ when the ✎ did.
    const startIdx = Math.max(0, chips.indexOf(startChip));
    let active = startIdx;
    startPreview(chips, startIdx);
    const cp = revealPickerFor();
    cp.setColor(targets[startIdx].hex);
    cp.open(startChip || editColorsBtn, el.closest('.ds-canvas') || el.closest('.weight-designer') || document.body, {
      targets,
      // Colour-filled glass swatches rather than dot-plus-word tabs: these targets have no names, so
      // the colour is the label (Ryan 2026-07-28). The chosen one takes the same white ring every
      // selected swatch in the tool wears — see `.wd-cchip.is-active`.
      targetStyle: 'swatch',
      // These swatches pick WHICH of the logo's tones you are editing — not a colour to apply. That
      // is the only place in the tool where that meaning is correct, so it is the only place the
      // strip survives (Ryan 2026-08-05); everywhere else it was a second, conflicting reading of an
      // identical-looking row.
      targetsLabel: 'Choose a color to edit',
      // ⚠ `startIdx`, not '0'. Three things have to agree about which tab is live on open — the seeded
      // colour, the index the confirm writes to, and the tab wearing the white ring — and this one was
      // left hard-coded when the other two learned about the starting chip. So tapping red opened on
      // red's colour, wrote to red on confirm, and lit GREEN: correct behaviour under a lit tab that
      // was lying about it, which is worse than being simply wrong.
      activeTargetKey: String(startIdx),
      // Switching tabs PUTS THE OLD ONE BACK first. A preview is a change to the live design, so
      // leaving it standing while you go and look at another colour would quietly commit it — the
      // buyer would have edited a colour they never confirmed, by browsing.
      onTargetChange: (key) => {
        revertPreview();
        active = Number(key) || 0;
        startPreview(chips, active);
      },
      confirm: {
        label: 'Update color',
        // The preview has already put this colour on the plate; confirming only makes it PERMANENT —
        // the chip, the CD tab, and a history entry. `previewTo` first, because a value typed into the
        // hex box and confirmed immediately may never have been previewed.
        // The popover STAYS UP so the tab strip is still there to switch with (Ryan 2026-07-28).
        keepOpen: true,
        onConfirm: (picked) => {
          previewTo(picked);
          commitPreview();
          // ⚠ RE-ARM, because `commitPreview` clears the preview and this popover is still open.
          // Without it the next drag on this same swatch would have nothing to preview against and
          // the live update would silently stop working after the first commit — the one bug this
          // mode invites, and it would only ever show up on the SECOND edit.
          startPreview(chips, active);
        },
        onCancel: revertPreview,
      },
    });
  }
  // ── "Cut holes" toggle (Ryan 2026-07-17) ─────────────────────────────────
  // The bg keyer has to guess whether an enclosed pocket of the background colour
  // is disposable (a letter counter like the "D" hole) or intentional (a white
  // separator stroke). No pixel rule is perfect, so this hands the rare edge case
  // to the buyer: checked = cut the holes through to the plate; unchecked = keep
  // them filled. Only shown when a background key was actually applied. keyCtx
  // carries what's needed to re-key live — bgKey is synchronous, so it's instant.
  // ── "Remove the background color" (Ryan 2026-07-28) ──────────────────────────────────────────
  // The keyer only runs on an OPAQUE source, on the rule that a file carrying alpha has already been
  // cut out. That is right nearly always and wrong for a very ordinary case: a badge exported with
  // ROUNDED CORNERS. A few hundred transparent corner pixels make the file non-opaque, so a solid
  // field covering the other 95% is read as "already handled" — which is why the McDonald's red
  // survived while its corners did not.
  //   The fix is NOT to key those automatically. Plenty of logos genuinely are badges — a crest, a
  // roundel — where the coloured field IS the mark, and stripping it would be wrong with no obvious
  // way to ask for it back. So this offers the choice, and only when there is a choice to make: the
  // border has to sample as one solid opaque colour, and keying it has to remove a meaningful but
  // not absurd share of the image. Unchecked by default, because the conservative reading is right
  // more often and the buyer can see the result of flipping it instantly.
  //   Flipping RE-INGESTS through `handleFile(..., { replace: true })` — the same path the "Upload
  // different logo" tile uses. Nothing about extraction, structure, trimming or the reveal is
  // duplicated here, and the palette is re-read from the keyed art, which matters: with the red gone
  // the logo's colours are genuinely different and every chip downstream has to say so.
  let bgOffer = null; // { file, keyed } while an un-keyed solid field is on offer, else null
  let bgOfferBusy = false;
  /* ⚠ A SEGMENTED YES/NO, NOT A CHECKBOX (Ryan 2026-08-12) — see `ui/yes-no-toggle.js`. The control
     is the only thing that changed: every `.checked` read became `.get()`, every silent write
     `.set()`, and `.el.hidden` is the same property the old <label> carried, so all nine show/hide
     sites below are untouched. */
  const removeBgUI = yesNoToggle({
    label: 'Remove the background color',
    ariaLabel: 'Remove the solid background color from this logo',
    onChange: async (wantKeyed) => {
      if (!bgOffer || bgOfferBusy) return;
      bgOfferBusy = true;
      removeBgUI.setBusy(true);
      try {
        // Yes → the keyed art. No → the file exactly as they gave it to us, so the switch is
        // genuinely reversible rather than a one-way door with an undo-shaped label on it.
        const next = wantKeyed ? await fileFromDataUrl(bgOffer.keyed.dataUrl, bgOffer.file.name) : bgOffer.file;
        if (next) await handleFile(next, { replace: true, fromBgToggle: true });
      } catch { removeBgUI.set(!wantKeyed); } finally { bgOfferBusy = false; removeBgUI.setBusy(false); }
    },
  });
  const removeBgToggle = removeBgUI.el;

  let keyCtx = null; // { source, keyColor, logo } while a keyed logo is live, else null
  const HOLES_LABEL = 'Let weight show through enclosed areas';
  /* WD 482c: the Add Logo tab's heading - same words as Customize -> Logo's heading, same style as
     YOUR LOGO / YOUR COLORS (`HOLES_LABEL` stays the accessible name and the panel's handle). */
  const HOLES_HEADING = 'Logo cutouts'; // Ryan 10-31: "why don't we make it 'Logo Cutouts' instead"
  /* While the reveal sequence is still running the chooser is ON SCREEN but the flip must wait: a flip's
     re-read palette would be overwritten by the sequence (the 2026-08-08 race). A tap in that window is
     KEPT, not ignored - the card answers at once and the flip runs the moment the sequence ends. */
  let holesLocked = false;
  let tapFixLocked = false; // same window as `holesLocked`, released by `armKeyHolesToggle` (keyed or not)
  let tapFixPending = false;
  let tapFixPendingApply = null; // the restyle of an applied edit; the build beat awaits it like a flip
  let holesPending = null;
  // WD 482: the This-or-That - the logo both ways on a weight, same answer and slot as the old Yes/No.
  const keyHolesUI = seeThroughChooser({
    label: HOLES_HEADING,
    labelClass: 'wd-reveal-label',
    blockClass: 'wd-reveal-holesblock',
    ariaLabel: 'Let the weight show through enclosed areas of the logo',
    onChange: (wanted) => { if (holesLocked) { holesPending = wanted; return; } flipHoles(wanted); },
  });
  const keyHolesToggle = keyHolesUI.el;
  /* ── WD 483: "FIX SEE-THROUGH PARTS" - tap a piece to flip it (DB Logo's open gaps, Mojoe's dark box) ──
     Offered for EVERY uploaded logo, keyed or not (Mojoe's never reaches the keyer). Arrives in the same reveal
     beat as the chooser; a tap while the reveal is still running is answered, not swallowed. */
  // Worn like "Reset colors" (full-width flat glass) with the Edit chip's pencil (Ryan 10-31: "isn't on brand").
  const tapFixBtn = h('button', { type: 'button', class: 'wd-reset-btn wd-flat-glass wd-flat-glass--flush wd-tapfix-open', hidden: true, onClick: () => openTapFixFlow() },
    h('span', { class: 'wd-tapfix-open__icon', 'aria-hidden': 'true' }, '✎'), 'Clean up logo'); // Ryan 10-31; renamed WD 498 (it does the background too)
  /* ── WD 489: THE EDIT BUTTON GLOWS WHEN THE APP IS UNSURE (Ryan 11-01) ─────────────────────────────────────────
     On a logo whose Solid and See-through versions differ (the ones that used to show the pair here, `HOLES_MIN_DIFF`),
     "Edit see-through parts" wears the app's ONE "press this" ring - the `is-hinting` rule the Next button, the phone's
     sheet and the Organization box share - never a glow of its own.
     ⛔ ONE GLOW AT A TIME (Ryan 11-01, asked and agreed): it starts when the reveal ENDS (taps are held until then) and on
     this tab it HANDS OVER to the Organization nudge the moment that one fires, or ends when "Design My Weights" is
     pressed, which is where the Next nudge takes its turn. Today's four nudges never overlap because each owns a
     different moment; this one is given a moment rather than a second ring on screen.
     Customize Design -> Logo has no Organization box and no Next nudge, so there it glows until the editor is opened.
     Opening it from either tab ends it for this logo. Never on a reopened design: the buyer saw this logo already. */
  /* ⛔ AND WHILE IT GLOWS, THE CARET STAYS OUT OF ORGANIZATION (Ryan 11-01: "when the edit see-through parts button is
     pulsing, the cursor should not be pre-clicked into the organization name field.. as that's telling people to type,
     and they might miss the pulsing see-through button.. any other time though, the cursor can go straight into it").
     Desktop's autofocus is skipped while Edit glows; the caret goes in when the glow hands over, or when the editor
     closes. `whoFocusName` is the who card's own "put the caret in" (null when no card is up). */
  /* ── WD 501: THE GLOW LASTS UNTIL THE EDITOR HAS BEEN OPENED AND CLOSED (Ryan 11-02) ───────────────────────────
     *"If the 'Clean up logo' button is pulsing, it shouldn't stop until it's clicked ... whether it be on the Add Logo
     tab or Customize Design tab. Once the button is clicked on either one of those tabs, it should then stop pulsing
     once you close the pop-up ... Then, once it's closed, if you still haven't made it off the 'Add Logo' tab ... the
     'Organization' name field can begin pulsing."*
     This replaces WD 489's hand-over (the Organization nudge and "Design My Weights" used to end it on this tab). Still
     one glow at a time, the other way round: while it glows the Organization nudge, the phone's scroll down to it and
     the Next nudge all WAIT, and they get their turn when the editor closes (`afterTapFixClosed`).
     `tapFixDone` is per logo: set on close, cleared by a new upload (not by the editor's own background re-ingest, which
     would otherwise light the glow again for the logo the buyer has just cleaned up). */
  let whoFocusName = null;
  let whoAfterTapFix = null;    // the who card's "your turn now" (scroll down, Organization pulses); null when no card is up
  let forwardHintWaiting = false; // the build finished while the glow was on: the Next nudge is armed on close instead
  let editGlowWanted = false;   // this logo is unsure (or has a removable background)
  let tapFixDone = false;       // ...and the editor has been opened and closed for it
  const glowOn = () => editGlowWanted && !tapFixDone;
  function syncEditGlow() {
    tapFixBtn.classList.toggle('is-hinting', glowOn() && !tapFixLocked && !tapFixBtn.hidden);
    const root = ctx.el || document;
    root.querySelectorAll?.('.wd-tapfix-open--cd').forEach((b) => b.classList.toggle('is-hinting', glowOn()));
  }
  /** The editor closed (either tab, whatever the buyer did in it): the glow is over, and the nudges it held get their turn. */
  function afterTapFixClosed() {
    const wasOn = glowOn();
    tapFixDone = true;
    syncEditGlow();
    if (!wasOn) { whoFocusName?.(); return; }
    whoAfterTapFix?.();
    if (forwardHintWaiting) { forwardHintWaiting = false; ctx.armForwardHint?.(); }
  }
  /** The Solid / See-through starting points for the editor - null when the two versions match (nothing to choose). */
  function editorStarts() {
    if (!keyCtx || !keyCtx.logo || keyHolesToggle.hidden || !holesPreview) return null;
    const kc = keyCtx;
    return {
      value: !!keyHolesUI.get(),
      previews: holesPreview,
      art: async (v) => {
        const r = kc.variants ? kc.variants[v ? 'cut' : 'filled'] : bgKey(kc.source, kc.keyColor, v, kc.keyWhite, kc.keyField);
        return r && r.dataUrl ? decodeImage(r.dataUrl) : null;
      },
    };
  }
  /* ── WD 498: THE BACKGROUND PAIR FOR THE EDITOR ─────────────────────────────────────────────────────────────────
     Only while a solid field is on offer (`bgOffer`, the probe the old switch was armed by). Both versions are drawn in
     the frame of what is on screen where they can be: the logo as it is (`img`), and the field keyed out of THAT image
     and put back at its own offset (`bgKey`'s crop), so a removal is one more step in the editor rather than a new
     canvas. Going back to KEEP from a removed logo needs the original file, which is a different shape - the editor
     re-frames for that one. Previews sit on the plate that contrasts MOST with the field, so the difference shows (on a
     red plate a red badge kept and removed look the same). */
  async function editorBg(img) {
    if (!bgOffer || !bgOffer.keyed) return null;
    const removedNow = !!removeBgUI.get();
    const p = bgOffer.keyed;
    if (bgHolesFor !== bgOffer) { bgHolesFor = bgOffer; bgHolesNow = !!p.keyGlobal; } // a new offer starts on the probe's own answer
    const iw = img.naturalWidth || img.width, ih = img.naturalHeight || img.height;
    const reframe = (r) => {
      if (!r || !r.canvas || !r.crop) return null;
      const c = document.createElement('canvas'); c.width = iw; c.height = ih;
      c.getContext('2d').drawImage(r.canvas, r.crop.x * iw / r.crop.sw, r.crop.y * ih / r.crop.sh, r.crop.w * iw / r.crop.sw, r.crop.h * ih / r.crop.sh);
      return c;
    };
    /* The two INSIDE versions of the removed logo: the field flooded from the border only (Solid - an enclosed pocket of
       the field colour stays) and removed everywhere (See-through). Kept logo -> drawn in its frame; removed logo -> cut
       from the original file, trimmed, which is the shape the removed logo on screen already has. */
    let keepArt = null, removeArt = null, inside = null;
    if (!removedNow) {
      keepArt = img;
      inside = { false: reframe(bgKey(img, p.keyColor, false, p.keyWhite, p.keyField)), true: reframe(bgKey(img, p.keyColor, true, p.keyWhite, p.keyField)) };
    } else {
      try { keepArt = await decodeImage(URL.createObjectURL(bgOffer.file)); } catch { keepArt = null; }
      if (keepArt) {
        const pk = (g) => { const r = bgKey(keepArt, p.keyColor, g, p.keyWhite, p.keyField); return r && r.canvas; };
        inside = { false: pk(false), true: pk(true) };
      }
    }
    removeArt = removedNow ? img : (inside && inside[String(bgHolesNow)]) || reframe(bgKey(img, p.keyColor, p.keyGlobal, p.keyWhite, p.keyField));
    if (!keepArt || !removeArt) return null;
    const thumb = (art) => {
      const aw = art.naturalWidth || art.width, ah = art.naturalHeight || art.height;
      const k = Math.min(1, 220 / Math.max(aw, ah));
      const c = document.createElement('canvas'); c.width = Math.max(1, Math.round(aw * k)); c.height = Math.max(1, Math.round(ah * k));
      c.getContext('2d').drawImage(art, 0, 0, c.width, c.height);
      return c.toDataURL('image/png');
    };
    const field = Array.isArray(p.keyColor) ? `#${p.keyColor.map((n) => Math.round(n).toString(16).padStart(2, '0')).join('')}`.toUpperCase() : null;
    let plates = [];
    try { plates = plateDealDeck(ctx.state.ui.palette, ctx.state.designProfile).seq || []; } catch {}
    plates = [...new Set([...plates, '#FFFFFF', DEFAULT_BODY_COLOR].map((x) => normalizeHex(x)).filter(Boolean))];
    const plate = field ? plates.reduce((a, c) => (contrastRatio(c, field) > contrastRatio(a, field) ? c : a), plates[0]) : DEFAULT_BODY_COLOR;
    /* The inside pair, only when the two inside versions really differ (the same `HOLES_MIN_DIFF` bar the cutouts pair
       uses) - a badge with no enclosed pocket of its field colour has nothing to ask. */
    let starts = null;
    const a = inside && inside.false, b2 = inside && inside.true;
    if (a && b2 && a.width === b2.width && a.height === b2.height) {
      const da = a.getContext('2d', { willReadFrequently: true }).getImageData(0, 0, a.width, a.height).data;
      const db = b2.getContext('2d', { willReadFrequently: true }).getImageData(0, 0, b2.width, b2.height).data;
      let ink = 0, diff = 0;
      for (let i = 3; i < da.length; i += 4) { if (da[i] >= 200) { ink += 1; if (db[i] < 60) diff += 1; } }
      if (ink && diff / ink >= HOLES_MIN_DIFF) {
        starts = { value: bgHolesNow, previews: { solid: thumb(a), see: thumb(b2), plate }, art: async (v) => (v ? b2 : a) };
      }
    }
    return {
      value: removedNow,
      previews: { solid: thumb(keepArt), see: thumb(removeArt), plate },
      art: async (v) => (v ? removeArt : keepArt),
      starts,
    };
  }
  let bgHolesFor = null; // the offer `bgHolesNow` belongs to
  let bgHolesNow = false; // WD 498: Solid (false) / See-through (true) inside a REMOVED background
  /** The finished art of one starting point (unedited), as the flip would make it. */
  function startArt(v) {
    if (!keyCtx) return null;
    return keyCtx.variants ? keyCtx.variants[v ? 'cut' : 'filled'] : bgKey(keyCtx.source, keyCtx.keyColor, v, keyCtx.keyWhite, keyCtx.keyField);
  }
  const liveLogo = () => (keyCtx && keyCtx.logo) || pendingLogo
    || ((ctx.state.designProfile.elements || []).find((e) => e && e.type === 'logo') || {}).logo || null;
  async function tapFixSource(img) {
    // the art BEFORE the key, cropped exactly as the keyer cropped it - so a fill puts back the right pixels
    if (!keyCtx || !keyCtx.source) return null;
    const cur = liveLogo();
    const r = bgKey(keyCtx.source, keyCtx.keyColor, !!(cur && cur.keyGlobal), keyCtx.keyWhite, keyCtx.keyField);
    if (!r || !r.crop) return null;
    const iw = img.naturalWidth || img.width, ih = img.naturalHeight || img.height;
    if (Math.abs(r.crop.w / r.crop.h - iw / ih) > 0.01) return null; // trimmed again since: not aligned
    const c = document.createElement('canvas');
    c.width = r.crop.w; c.height = r.crop.h;
    const sw = keyCtx.source.naturalWidth || keyCtx.source.width, sh = keyCtx.source.naturalHeight || keyCtx.source.height;
    c.getContext('2d').drawImage(keyCtx.source, r.crop.x * sw / r.crop.sw, r.crop.y * sh / r.crop.sh,
      r.crop.w * sw / r.crop.sw, r.crop.h * sh / r.crop.sh, 0, 0, r.crop.w, r.crop.h);
    return c;
  }
  /* The logo BEFORE any tap edit, for "Reset logo" - kept for the session (a second full image on the saved record would
     double its size). Set the first time the editor opens on an unedited logo; dropped by a new logo or a chooser flip.
     A keyed logo with no memory (a later visit after a reload) rebuilds it from the key, as the chooser would show it. */
  let tapFixBase = null;
  async function tapFixOriginal(cur) {
    if (!cur.tapFixed) { tapFixBase = cur.dataUrl; return null; } // unedited: it IS the original
    if (tapFixBase) { try { return await decodeImage(tapFixBase); } catch { return null; } }
    if (keyCtx && keyCtx.source) {
      const r = bgKey(keyCtx.source, keyCtx.keyColor, !!cur.keyGlobal, keyCtx.keyWhite, keyCtx.keyField);
      if (r) { try { return await decodeImage(r.dataUrl); } catch {} }
    }
    return null;
  }
  /** WD 485: `cur` (tap-edited) carried onto the chooser's other version `nextUrl`. The pre-edit art is the session's
      `tapFixBase`, else a re-key of the upload at the CURRENT answer. -> {dataUrl, canvas} or null.
      ⚠ NOT the resumed variants: after a reload they are derived FROM the edited art, so "before" would equal
      "after" and the edits would silently vanish while reporting kept. No pre-edit art -> null -> the old behaviour. */
  async function carryEditsOnto(cur, nextUrl) {
    let baseUrl = tapFixBase;
    if (!baseUrl && keyCtx && keyCtx.source) baseUrl = (bgKey(keyCtx.source, keyCtx.keyColor, !!cur.keyGlobal, keyCtx.keyWhite, keyCtx.keyField) || {}).dataUrl;
    if (!baseUrl) return null;
    const [bi, ei, ni] = await Promise.all([decodeImage(baseUrl), decodeImage(cur.dataUrl), decodeImage(nextUrl)]);
    const px = (im) => {
      const c = document.createElement('canvas');
      c.width = im.naturalWidth; c.height = im.naturalHeight;
      const g = c.getContext('2d', { willReadFrequently: true });
      g.drawImage(im, 0, 0);
      return { c, g, d: g.getImageData(0, 0, c.width, c.height) };
    };
    const b = px(bi), e = px(ei), n = px(ni);
    const res = carryTapEdits(b.d, e.d, n.d);
    if (!res) return null;
    n.g.putImageData(new ImageData(res.data, res.width, res.height), 0, 0);
    return { dataUrl: n.c.toDataURL('image/png'), canvas: n.c };
  }
  async function openTapFixFlow() {
    // a tap while the reveal is still running is KEPT (like the cards'): the editor opens the moment it ends
    if (tapFixLocked) { tapFixPending = true; return; }
    const cur = liveLogo();
    if (!cur || !cur.dataUrl) return;
    let img;
    try { img = await decodeImage(cur.dataUrl); } catch { return; }
    let source = null;
    try { source = await tapFixSource(img); } catch {}
    let original = null;
    try { original = await tapFixOriginal(cur); } catch {}
    let bg = null;
    try { bg = await editorBg(img); } catch (err) { wdDebug(`Clean up logo: background pair failed (${err && err.message})`); }
    const res = await openTapFix({ mount: ctx.el || document.querySelector('.weight-designer'), image: img, source, original, starts: editorStarts(), bg });
    // WD 501: the glow ends on CLOSE, not on open - and the nudges it held go after the result has been applied (a
    // background change re-ingests the logo with the who card greyed; pulsing Organization over that would be noise)
    try { await applyTapFixResult(res, cur, bg); } finally { afterTapFixClosed(); }
  }
  async function applyTapFixResult(res, cur, bg) {
    if (!res) return;
    const out = res.canvas;
    /* WD 498: the buyer changed the BACKGROUND answer. That is the old switch's re-ingest (`handleFile` with
       `fromBgToggle`, which keeps the offer and the original file alive), fed the editor's finished pixels so any taps
       made on top ride along. The re-ingest re-reads the palette from the new art and trims the margins a removed field
       leaves - both right, and both what the switch always did. */
    if ((typeof res.bg === 'boolean' || (bg && bg.starts && typeof res.pick === 'boolean')) && bgOffer && !bgOfferBusy) {
      bgOfferBusy = true;
      try {
        const finished = out.toDataURL('image/png');
        // WD 499 (Ryan 11-01: "the logo preview appears as a broken link.. then after a few seconds it loads"): the
        // finished logo goes into YOUR LOGO now, and stays there while the re-ingest re-reads it.
        await crossfadeThumb(finished, true);
        const next = await fileFromDataUrl(finished, bgOffer.file.name);
        if (typeof res.bg === 'boolean') removeBgUI.set(res.bg);
        if (typeof res.pick === 'boolean') bgHolesNow = res.pick; // the inside answer of a removed background
        if (next) await handleFile(next, { replace: true, fromBgToggle: true });
      } catch (err) { wdDebug(`Clean up logo: background change failed (${err && err.message})`); } finally { bgOfferBusy = false; }
      return;
    }
    /* WD 489: the buyer picked the OTHER starting point in the editor. That is the old chooser's flip, made from inside
       the editor - the same record fields `flipHoles` writes (the answer, final, saved), and the picked version's
       unedited art becomes what Reset logo goes back to. */
    const picked = typeof res.pick === 'boolean' ? res.pick : null;
    const pickedArt = picked !== null ? startArt(picked) : null;
    if (picked !== null && pickedArt) tapFixBase = pickedArt.dataUrl;
    const url = res.atOriginal && tapFixBase ? tapFixBase : out.toDataURL('image/png');
    const logo = {
      ...cur,
      dataUrl: url,
      renderUrl: null, renderAspect: null, _rasterKey: '', // the art changed: no stale composite
      transparentBg: true,
      aspectRatio: out.width / out.height,
      tapFixed: !res.atOriginal, // a "Reset logo" kept with Use this is the original again
      ...(picked !== null && keyCtx ? {
        keyColor: keyCtx.keyColor ?? null, keyWhite: keyCtx.keyWhite ?? null, keyField: keyCtx.keyField ?? null,
        keyGlobal: picked, keyGlobalUserSet: true,
      } : {}),
    };
    if (keyCtx) keyCtx.logo = logo;
    pendingLogo = logo;
    if (logo.tapFixed && tapFixBase) rememberTapFixBase(url, tapFixBase); // WD 505: survives a reload
    if (picked !== null) setHolesUI(picked);
    /* ⛔ THE SAME LOGO KEEPS ITS SIZE (WD 487's rule for the flip, which this path never had): `setLogo` puts every logo
       element back to sizeScale 1, right for a new upload and wrong for an edit of the one on the plate. */
    const sizesBefore = (ctx.state.designProfile.elements || []).filter((e) => e.type === 'logo').map((e) => e.sizeScale);
    setLogo(ctx.state, logo);
    (ctx.state.designProfile.elements || []).filter((e) => e.type === 'logo').forEach((e, i) => { if (Number.isFinite(sizesBefore[i])) e.sizeScale = sizesBefore[i]; });
    crossfadeThumb(url, true);
    wdDebug(`Tap-fix applied (${out.width}x${out.height})`);
    const job = ++holesFlipJob; // the same restyle the chooser's flip runs, and the same "newest wins" counter
    tapFixPendingApply = restyleAfterFlip({ dataUrl: url, canvas: out }, job);
    await tapFixPendingApply.catch(() => {});
    tapFixPendingApply = null;
    if (job !== holesFlipJob) return;
    if (hasBuilt) ctx.requestRender();
    (ctx.recordHistoryNow || ctx.recordHistory)?.();
  }
  /** Guards against a second flip landing while the first is still keying — the newer one wins. */
  let holesFlipJob = 0;
  /** The in-flight flip (re-key + restyle), or null. The build beat AWAITS this: the control is now
   *  live during the who-card wait, so "flip, then immediately press Design My Weights" is an
   *  ordinary sequence rather than a rare race, and without this the restyle's `applyPalette` lands
   *  AFTER `smartStart` has already seeded a design from the pre-flip colours. */
  let holesFlipPending = null;
  /* ⛔ THE ONE IMPLEMENTATION OF THE FLIP, and it has to stay that way. Two surfaces drive it now —
     the Add Logo tab and Customize Design → Logo (Ryan 2026-08-12) — and this function carries a
     rAF-deferred re-key, a job counter, a pending-promise the build beat awaits, a refusal path and
     a full restyle. A second copy of that in the panel would be a second copy of every race
     08-08 and 276 closed. The panel gets `ctx.holesControl` (below), which is a VIEW onto this.
     `wanted` is the argument rather than a re-read of the control: the refusal path puts the control
     back, and a re-read would then report the opposite of what was attempted. */
  function flipHoles(wanted) {
    if (!keyCtx) return;
    /* Both controls show the requested answer IMMEDIATELY, whichever one was pressed. The pressed
       control has already moved itself, so this is a no-op for it and carries the answer to the
       other — the flip is deferred by two frames and a stale second control in that window would be
       two on-screen claims about the same plate. The refusal path below puts BOTH back. */
    setHolesUI(wanted);
    const job = ++holesFlipJob;
    /* ── LET THE TICK PAINT BEFORE THE RE-KEY BLOCKS THE THREAD (Ryan 2026-08-08) ────────────────
     * *"when you check the box, there's a delay… can you speed things up to ensure the box actually
     * gets checked quickly even if the logo is still being processed in the background?"*
     *
     * Nothing was slow to START — `bgKey` is SYNCHRONOUS and heavy (a 1024px canvas, a full flood
     * fill or global pass, then a premultiplied feather over the alpha boundary). It ran inside this
     * handler, so the browser could not paint until it returned: the checkbox's own tick was queued
     * behind the work the tick was asking for. On a phone that is the whole visible delay.
     *
     * ⚠ DOUBLE rAF, NOT `setTimeout(0)`. A rAF callback runs BEFORE that frame paints, so one is not
     * enough — the second guarantees a frame has actually been composited with the checkbox ticked.
     * Same idiom `revealChrome` uses in main.js, and for the same reason.
     *
     * ⚠ The work is unchanged and still synchronous once it starts; this buys a painted frame, not a
     * faster key. `holesFlipJob` covers the window it opens: a fast double-tap now has two flips in
     * flight, and only the newest may write. */
    /* ⚠ THE PENDING FLIP IS CLAIMED HERE, SYNCHRONOUSLY — not inside the rAF that does the work.
       The build beat awaits it, and the two frames the rAF buys are two frames in which a flip has
       been ASKED FOR and nothing records it. A "Design My Weights" landing in that gap would build
       from the pre-flip record and silently discard the answer, which is the whole bug class this
       await exists to close; deferring the claim would leave a smaller version of it. */
    let settle;
    holesFlipPending = new Promise((res) => { settle = res; });
    const flipDone = () => { if (job === holesFlipJob) holesFlipPending = null; settle(); };
    requestAnimationFrame(() => requestAnimationFrame(async () => {
      if (job !== holesFlipJob || !keyCtx) { flipDone(); return; }
    // A RESUMED context has no pre-key source to re-key from — it carries the two finished variants
    // instead (see hydrate). Same outcome, different route, so everything below is shared.
    const r = keyCtx.variants
      ? keyCtx.variants[wanted ? 'cut' : 'filled']
      : bgKey(keyCtx.source, keyCtx.keyColor, wanted, keyCtx.keyWhite, keyCtx.keyField);
    if (!r) {
      /* ⛔ THE REVERT IS RIGHT; DOING IT SILENTLY WAS THE BUG (Ryan 2026-08-08: *"if I check it, it
       * tries to apply it, and then it unapplies the checkbox without me doing anything"*).
       *
       * `bgKey` ends `if (removed === 0 || frac > 0.97) return null` — it refuses a key that removed
       * nothing, and one that ate more than 97% of the frame. The second is the case that bites
       * here: unchecked is a border-connected FLOOD, checked removes that colour EVERYWHERE, so on
       * a logo whose artwork shares its background colour the global pass deletes the mark. Putting
       * the checkbox back is the correct outcome — the alternative is handing the buyer a blank
       * plate — but a control that undoes itself with no explanation reads as broken software.
       *
       * ⚠ NOT A CONTRADICTION OF REMOVING THE "Logo applied" PILL. That one narrated something
       * visible. This reports a refusal with NO visible trace at all: without it the only evidence
       * is a control that moved on its own. Information, not narration.
       *
       * ⚠ PLATFORM-INDEPENDENT. Nothing on this path is phone-gated, so this fires on desktop too. */
      setHolesUI(!wanted);
      ctx.status?.info?.("We couldn't separate this logo's enclosed areas from its artwork, so nothing changed.");
      wdDebug(`Holes toggle REFUSED going ${wanted ? 'fill (global)' : 'keep (flood)'} — bgKey returned null`);
      flipDone();
      return;
    }
    /* WD 485 (queue 4b, Ryan 10-31): a flip AFTER tap edits keeps the edits - every pixel the buyer changed comes
       with it, the pockets they never touched follow the chooser. Not aligned (a different size) -> the old
       behaviour: the chooser's version replaces the art. */
    let art = r;
    let kept = false;
    const edited = keyCtx.logo && keyCtx.logo.tapFixed ? keyCtx.logo : null;
    if (edited) {
      try {
        const merged = await carryEditsOnto(edited, r.dataUrl);
        if (job !== holesFlipJob || !keyCtx) { flipDone(); return; }
        if (merged) { art = merged; kept = true; }
      } catch {}
      wdDebug(`Toggle after tap edits: ${kept ? 'edits kept' : 'not aligned - the chooser version replaces them'}`);
    }
    const logo = {
      ...keyCtx.logo,
      dataUrl: art.dataUrl,
      /* ⛔ DROP THE COMPOSITE — THE ART UNDER IT JUST CHANGED. The spread above carries `renderUrl`
       * (the peeled / recoloured / ringed raster) and `_rasterKey` forward from the PREVIOUS
       * artwork, and `renderUrl` is what the plate actually paints. Leaving them is how the 3D kept
       * showing white pockets while this flip's `dataUrl` was correct.
       *   `syncLogoRaster`'s key now fingerprints the source too, so it would rebuild regardless —
       * but that is the guard's job, and a record should not carry a derived field describing art it
       * no longer holds. Clearing here means the element is never momentarily self-inconsistent.
       * (memory: `wd-renderurl-transient-repeat-offender`) */
      renderUrl: null,
      renderAspect: null,
      _rasterKey: '',
      transparentBg: true,
      aspectRatio: r.canvas.width / r.canvas.height,
      keyColor: keyCtx.keyColor ?? null,
      keyWhite: keyCtx.keyWhite ?? null,
      keyField: keyCtx.keyField ?? null,
      keyGlobal: wanted, // persist the flip, or a resume restores the old answer
      // ⚠ AN EXPLICIT FLIP IS FINAL. Without this the plate-aware rule would undo the buyer's choice
      // the next time they dyed the plate — which is a worse bug than the one it was added to fix.
      keyGlobalUserSet: true,
      tapFixed: kept, // WD 485: the edits ride the flip (483c dropped them)
    };
    keyCtx.logo = logo;
    pendingLogo = logo;
    tapFixBase = kept ? r.dataUrl : null; // the chooser's version (unedited) is the new "original" for Reset logo
    if (kept) rememberTapFixBase(art.dataUrl, r.dataUrl); // WD 505: survives a reload
    /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (WD 487, 2026-11-01) — A FLIP IS THE SAME LOGO, SO IT KEEPS ITS SIZE. `setLogo`
       resets every logo element to `sizeScale = 1` (right for a NEW upload, which the seed then brings to 75%). After
       the build nothing re-seeds, so Solid <-> See-through moved DB Logo's dial 75% -> 90% and drew it 21% bigger -
       Ryan, UX run 1: *"the logo increased in size"*. Measured: sizeScale 0.825 -> 1.0, painted 53.3 -> 64.6. */
    const sizesBefore = (ctx.state.designProfile.elements || []).filter((e) => e.type === 'logo').map((e) => e.sizeScale);
    setLogo(ctx.state, logo);
    (ctx.state.designProfile.elements || []).filter((e) => e.type === 'logo').forEach((e, i) => { if (Number.isFinite(sizesBefore[i])) e.sizeScale = sizesBefore[i]; });
    crossfadeThumb(art.dataUrl, true);
    wdDebug(`Toggle → ${wanted ? 'fill (global)' : 'keep (flood)'}: cut ${(r.removedFrac * 100).toFixed(0)}%`);
    /* Re-read the artwork this flip just made, then paint + file ONE history entry. Awaited so the
       render below shows the corrected styling rather than flashing the stale one and fixing it a
       frame later; the analysis is a 220px pass and the synchronous `bgKey` above it is far heavier.
       Errors are swallowed inside, so the flip itself can never fail on account of the restyle. */
    restyleAfterFlip(art, job).then(() => {
      if (job !== holesFlipJob) return;
      if (hasBuilt) ctx.requestRender();
      (ctx.recordHistoryNow || ctx.recordHistory)?.();
    }).finally(flipDone);
    }));
  }

  /* ── ONE ANSWER, TWO CONTROLS ────────────────────────────────────────────────────────────────
   * The Add Logo toggle and the Customize Design → Logo one are two VIEWS of the same fact, so
   * every write to the answer goes through here and reaches both. Writing one and letting the
   * other drift is the exact failure the layout picker's carousel-vs-grid taught (project
   * CLAUDE.md §0b) — and it would be worse here, because the two would be side-by-side claims
   * about the same plate.
   *   Silent by design: `yesNoToggle.set` never fires `onChange`, so this can never re-enter the
   * flip. Every caller below is restoring a known answer (an ingest, a resume, a refusal), never
   * requesting a new one. */
  const holesMirrors = new Set();
  function setHolesUI(v) {
    keyHolesUI.set(v);
    holesMirrors.forEach((m) => { try { m.set(v); } catch {} });
  }
  /* The panel's handle on all of this. `available` is the same condition that reveals the toggle
     here — a logo the keyer never touched has no `keyCtx` and neither surface offers the control. */
  ctx.holesControl = {
    label: HOLES_LABEL,
    // WD 482: offered only when the two versions differ (the chooser is shown), not on every key
    available: () => !!(keyCtx && keyCtx.logo) && !keyHolesToggle.hidden,
    previews: () => holesPreview,
    get: () => keyHolesUI.get(),
    set: (v) => flipHoles(!!v),
    /** Register a second control to be kept in step. Returns its unsubscribe. */
    mirror: (m) => { holesMirrors.add(m); return () => holesMirrors.delete(m); },
    /* WD 485 (queue 4a): "Edit see-through parts" on Customize -> Logo too - the SAME editor and apply path; offered
       exactly when the Add Logo tab's button is (after the reveal, on every logo, keyed or not). */
    tapFixAvailable: () => !tapFixBtn.hidden && !!liveLogo(),
    editGlow: () => glowOn(), // WD 501: Customize -> Logo's Clean up logo glows until its editor has been closed
    openTapFix: () => openTapFixFlow(),
  };

  /**
   * ── THE AUTO STYLE HAS TO DESCRIBE THE ARTWORK, AND THE FLIP CHANGES THE ARTWORK ──────────────
   * (Ryan 2026-08-08) *"the weight number still renders with a white outline the logo no longer
   * has."* Cut the white out of a single-tone mark and it is gone from the art, gone from the
   * thumbnail, gone from the plate — and still sitting in `palette.secondary`, in
   * `logoStructure.bands`, and therefore in the ring `autoStyle` hands the weight number. The flip
   * only ever called `setLogo`, so everything DERIVED from the pixels kept describing the pre-flip
   * artwork.
   *
   * ⚠ THE STRUCTURE ALONE IS NOT ENOUGH, and stopping there is the trap this was nearly built into.
   * Drop the white band and leave the palette, and `applyAutoStyleToElement`'s no-autoStyle branch
   * simply re-picks white from the roles: it reads a white `secondary` that clears the black plate
   * and clears the black fill, and hands back the same ring the structure had just disowned. White
   * has to leave the PALETTE too, which means re-extracting it — so this is the upload's whole
   * analysis pass (`extractPalette` → `extractLogoStructure` → `applyPalette` → `applyLogoStructure`)
   * over the art the flip just produced, and nothing else. No cleaning, no trim, no choreography.
   *
   * ⛔ AND IT IS LATCHED (Ryan 2026-08-08, unprompted, the same message): *"as soon as someone goes
   * to the Customize Design tab, adds/removes an outline, then they return to the Add Logo tab and
   * check or uncheck that box again, it won't affect any outlines after that point."* Exactly right,
   * and it is what makes re-deriving safe at all — every rule below rewrites the outline stack
   * wholesale, so the only question worth asking is whether that stack is still the app's to rewrite.
   * `styleUserEdited` answers it; see `markStyleUserEdited`.
   *
   * `sameArtwork` keeps the plate dye and its ink memory — a re-cut of the same logo is not a new
   * brand. See `applyLogoStructure`.
   */
  async function restyleAfterFlip(r, job) {
    const state = ctx.state;
    if (state.designProfile.styleUserEdited === true) {
      wdDebug('Holes flip: the design is hand-styled → outlines left exactly as they are');
      return;
    }
    try {
      /* A RESUMED context has no live canvas. `armResumedKeyToggle` stores each variant with a
         stand-in `canvas` carrying only `{width: aspect, height: 1}` for the aspect ratio, and
         handing that to the extractor would analyse a 1-pixel-tall nothing. Ask whether it is a real
         canvas rather than whether one is present. */
      const source = r.canvas && typeof r.canvas.getContext === 'function' ? r.canvas : await decodeImage(r.dataUrl);
      if (job !== holesFlipJob) return;
      // Same options the upload uses — `removedBg` lets the extractor drop the orphaned bg↔logo
      // anti-alias blends the key left behind, which matters more here than there: the pockets this
      // flip cuts are ringed with them.
      const paletteOpts = {
        allowNeutralSecondary: true,
        ...(keyCtx && keyCtx.keyColor ? { removedBg: keyCtx.keyColor } : {}),
        ...(pendingSvgInks ? { paletteAnchors: pendingSvgInks } : {}),
      };
      const palette = await extractPalette(source, paletteOpts).catch(() => null);
      if (job !== holesFlipJob) return;
      /* ⚠ A WEAK READ CHANGES NOTHING. `applyPalette` retires the outgoing tones from the bench and
         zeroes `logoToneCount` before it checks confidence, so handing it a null would strip the
         design's colours to fix an outline. Art we just cut from art that extracted cleanly should
         never land here; if it does, the stale ring is much the smaller fault. */
      if (!palette || palette.confidence !== 'high') {
        wdDebug(`Holes flip: palette came back ${palette ? palette.confidence : 'null'} → auto style left alone`);
        return;
      }
      const structure = await attachPeelSource(await extractLogoStructure(source, palette).catch(() => null), source);
      if (job !== holesFlipJob) return;
      const p0 = state.designProfile;
      applyPalette(state, palette);
      applyLogoStructure(state, structure, { sameArtwork: true });
      /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (WD 487, 2026-11-01) — …AND THE SAME TAIL EVERY OTHER RE-DERIVE RUNS. Ryan, UX run
         1, Drop Bear on ONE black plate: no light outline on the logo or the text, while adding 3 plates gave both the
         cream one. Measured: a fresh upload wears the cream ring (logo + text + number); See-through -> Solid left the
         logo ringless and the text in near-black. This path re-read the art and stopped, so the logo's contrast ring
         (`syncLogoContrastRing`) kept describing the See-through art - and the number mirrors the logo's stack. The
         first build and the logo swap both end with this tail; a plate move re-runs the ring, which is why adding
         plates "fixed" it. Same rules, same order as the swap path (`replaceReveal`). */
      if (!isNearBlackHex(p0.bodyColor)) {
        recascadeColorDefaults(p0);
        dropAutoOutlinesWhenFillReads(p0);
        assignOutlineColors(p0, effectivePlateHex(state));
      }
      correctIllegibleFill(p0, effectivePlateHex(state));
      syncLogoContrastRing(state);
      resyncAddedLogoRings(state);
      paintLandedColorChips();       // "YOUR COLORS" now describes the cut art, not the uncut one
      ctx.updateStageTint?.();
      ctx.refreshControls?.();
      const p = state.designProfile;
      wdDebug(`Holes flip restyle: tones ${shownLogoTones(state.ui.palette).join(' ') || '—'}`
        + ` · structure ${structure ? structure.confidence : 'none'}`
        + ` · text outlines [${(p.autoStyle && p.autoStyle.outlines ? p.autoStyle.outlines : []).map((o) => o.colorRef).join(', ') || '—'}]`);
    } catch (err) {
      // Never let the re-analysis take the flip down with it — the artwork swap already happened and
      // is correct on its own. A stale ring is a blemish; a half-applied flip is a broken control.
      wdDebug(`Holes flip restyle FAILED (${err && err.message ? err.message : err}) — artwork stands, styling unchanged`);
    }
  }

  /**
   * Make the holes toggle live, if a client key ran on this logo. A logo the keyer never touched has
   * no `keyCtx` and the control stays hidden, exactly as it always did.
   *
   * ⛔ CALLED AT TWO BEATS, AND THE EARLIER ONE IS THE POINT (Ryan 2026-08-12, on `DB Logo.jpg`:
   * *"how come when I upload this logo, it's not giving me the option to see through the white
   * section to see the plate?"*). It was called ONLY from `handleFile`'s `finally` — which sits
   * outside `await runBeat(…)`, and `runBeat` blocks on the who-card until the buyer presses "Design
   * My Weights". So on every first upload the control did not exist for the whole of the Add-a-logo
   * step: the buyer looks at their cut-out mark with its pockets still filled, and the one switch
   * that changes that is hidden until after the plate is built. Measured on this mark — the flood
   * takes 42%, the enclosed white is another 8.3% of the frame (14.2% of the MARK, so `eatsInk`
   * correctly declines to cut it automatically) and there was no way to ask for it.
   *
   * ⚠ THE `finally` CALL STAYS. It is the catch-all for every path that never reaches the who-card:
   * `replaceReveal` (a swap, which sets the logo immediately and has no wait), and any throw.
   * Idempotent — the second call finds it already shown and does nothing.
   */
  function armKeyHolesToggle() {
    tapFixLocked = false;
    syncEditGlow(); // the reveal is over: an unsure logo's Edit button lights now, not while taps are still held
    if (tapFixPending) { tapFixPending = false; openTapFixFlow(); }
    // Already brought in by the reveal sequence (WD 482c): unlock it, and run a flip tapped mid-sequence.
    if (holesLocked) {
      holesLocked = false;
      const want = holesPending;
      holesPending = null;
      if (want !== null && keyCtx && keyCtx.logo) flipHoles(want);
      return;
    }
    if (keyCtx && keyCtx.logo && keyHolesToggle.hidden) armHolesChooser();
  }

  /* ── WD 482c: THE CHOOSER IS A BEAT OF THE REVEAL, NOT A LATE POP-IN (Ryan 10-31: "don't appear with quite
     the same timing/cadence/fluidity as ... YOUR COLORS, LOGO SIZE") ──────────────────────────────────────
     It sits between the logo and YOUR COLORS in the tab, so it arrives there in the sequence: built, risen in
     with the size dial's motion, then the same `REVEAL_BLOCK_MS` before the colours start. Locked until the
     sequence ends (`armKeyHolesToggle`). No choice to offer -> no beat, and the sequence runs exactly as before. */
  async function revealHolesBeat() {
    tapFixLocked = true;
    const chose = await revealChooserBeat();
    if (chose || !tapFixBtn.hidden || !liveLogo()) return;
    // no chooser (nothing enclosed, or never keyed - Mojoe's): the tap-fix gets the beat on its own
    easeStep(() => { tapFixBtn.classList.add('is-revealing'); tapFixBtn.addEventListener('animationend', () => tapFixBtn.classList.remove('is-revealing'), { once: true }); tapFixBtn.hidden = false; });
    await dwell(REVEAL_BLOCK_MS);
  }
  async function revealChooserBeat() {
    if (!keyCtx || !keyCtx.logo || !keyHolesToggle.hidden) return false;
    const ctxAt = keyCtx;
    let pv = null;
    try { pv = await buildHolesPreview(keyCtx); } catch (err) { wdDebug(`Holes preview failed (${err && err.message})`); return false; }
    if (keyCtx !== ctxAt || !pv || pv.diff < HOLES_MIN_DIFF) return false;
    holesPreview = pv;
    setHolesPreviews(pv);
    holesLocked = true;
    holesPending = null;
    wdDebug(`See-through chooser: ${(pv.diff * 100).toFixed(1)}% of the mark differs, disc ${pv.plate} (pocket ${pv.pocket}) - in the reveal`);
    editGlowWanted = true; // lit when the reveal releases the button (`armKeyHolesToggle`)
    easeStep(() => {
      keyHolesToggle.hidden = false; // WD 489: not on the tab - its `hidden` is the "two versions differ" flag now
      tapFixBtn.classList.add('is-revealing');
      tapFixBtn.addEventListener('animationend', () => tapFixBtn.classList.remove('is-revealing'), { once: true });
      tapFixBtn.hidden = false;
    });
    await dwell(REVEAL_BLOCK_MS);
    return true;
  }

  /* ── WD 482: THE TWO PREVIEWS, AND WHEN THERE IS NOTHING TO CHOOSE ─────────────────────────────
   * Both versions are built once (the resume already holds them as `keyCtx.variants`; an upload has
   * only the source, so `bgKey` runs both ways - the same call the flip makes). Then:
   *   • the pixels that are ink in SOLID and clear in SEE-THROUGH are the choice itself. Under
   *     `HOLES_MIN_DIFF` of the mark there is no choice to offer, and the chooser stays hidden
   *     (the old Yes/No showed on every keyed logo, pockets or not).
   *   • the disc is the plate colour, from the mark's own deal plus stock white/black, that
   *     CONTRASTS MOST with those pixels - Tigers' white fur reads on navy, Carroll's black mouth on
   *     white. On the wrong plate both pictures are the same picture.
   * Runs after the step has painted (the two keys are synchronous canvas work). A newer logo wins. */
  const HOLES_MIN_DIFF = 0.003;
  let holesPreview = null;
  let holesPreviewJob = 0;
  function armHolesChooser({ glow = true } = {}) {
    const job = ++holesPreviewJob;
    const ctxAt = keyCtx;
    setTimeout(async () => {
      if (job !== holesPreviewJob || keyCtx !== ctxAt || !keyCtx) return;
      let pv = null;
      try { pv = await buildHolesPreview(keyCtx); } catch (err) { wdDebug(`Holes preview failed (${err && err.message}) - chooser shown without pictures`); }
      if (job !== holesPreviewJob || keyCtx !== ctxAt) return;
      if (pv && pv.diff < HOLES_MIN_DIFF) {
        wdDebug(`See-through chooser: both versions match (${(pv.diff * 100).toFixed(2)}% differ) - not offered`);
        return;
      }
      holesPreview = pv;
      setHolesPreviews(pv);
      if (pv) wdDebug(`See-through chooser: ${(pv.diff * 100).toFixed(1)}% of the mark differs, disc ${pv.plate} (pocket ${pv.pocket})`);
      if (pv && glow) editGlowWanted = true;
      easeStep(() => { keyHolesToggle.hidden = false; tapFixBtn.hidden = false; syncEditGlow(); });
    }, 60);
  }
  async function buildHolesPreview(kc) {
    const v = kc.variants;
    const cutUrl = v && v.cut && v.cut.dataUrl
      || (kc.source && (bgKey(kc.source, kc.keyColor, true, kc.keyWhite, kc.keyField) || {}).dataUrl);
    const solidUrl = v && v.filled && v.filled.dataUrl
      || (kc.source && (bgKey(kc.source, kc.keyColor, false, kc.keyWhite, kc.keyField) || {}).dataUrl);
    if (!cutUrl || !solidUrl) return null;
    const [ci, si] = await Promise.all([decodeImage(cutUrl), decodeImage(solidUrl)]);
    const W = 220;
    const s = Math.min(1, W / Math.max(si.naturalWidth || 1, si.naturalHeight || 1));
    const w = Math.max(1, Math.round(si.naturalWidth * s)), hh = Math.max(1, Math.round(si.naturalHeight * s));
    const px = (img) => {
      const c = document.createElement('canvas'); c.width = w; c.height = hh;
      const g = c.getContext('2d', { willReadFrequently: true }); g.drawImage(img, 0, 0, w, hh);
      return { url: c.toDataURL('image/png'), d: g.getImageData(0, 0, w, hh).data };
    };
    const C = px(ci), S = px(si);
    let ink = 0, diff = 0, r = 0, gg = 0, b = 0;
    for (let i = 0; i < S.d.length; i += 4) {
      if (S.d[i + 3] < 200) continue;
      ink += 1;
      if (C.d[i + 3] < 60) { diff += 1; r += S.d[i]; gg += S.d[i + 1]; b += S.d[i + 2]; }
    }
    const hex2 = (n) => Math.round(n).toString(16).padStart(2, '0');
    const pocket = diff ? `#${hex2(r / diff)}${hex2(gg / diff)}${hex2(b / diff)}`.toUpperCase() : null;
    let plates = [];
    try { plates = plateDealDeck(ctx.state.ui.palette, ctx.state.designProfile).seq || []; } catch {}
    plates = [...new Set([...plates, '#FFFFFF', DEFAULT_BODY_COLOR].map((x) => normalizeHex(x)).filter(Boolean))];
    const plate = pocket ? plates.reduce((a, c) => (contrastRatio(c, pocket) > contrastRatio(a, pocket) ? c : a), plates[0])
      : DEFAULT_BODY_COLOR;
    return { solid: S.url, see: C.url, plate, pocket, diff: ink ? diff / ink : 0 };
  }
  function setHolesPreviews(pv) {
    keyHolesUI.setPreviews(pv);
    holesMirrors.forEach((m) => { try { m.setPreviews?.(pv); } catch {} });
  }

  /** WD 490: the "this looks like a photo" card. -> true = carry on with this file, false = upload another. */
  async function keepPhotoAnyway() {
    const body = h('div', { class: 'wd-photocard' },
      // Ryan 11-01, his words (the card copy is his to set)
      h('p', { class: 'wd-photocard__text' }, 'The Weight Designer adds logos to weights: an emblem, a crest, or a wordmark. Photos or screenshots can\'t be printed on a weight.'),
      ...(PHOTO_CARD_EXAMPLES.length ? [h('p', { class: 'wd-photocard__label' }, 'Logos like these work great:'), h('div', { class: 'wd-photocard__examples' },
        ...PHOTO_CARD_EXAMPLES.map((ex) => h('figure', { class: 'wd-photocard__ex' },
          h('img', { src: ex.src, alt: ex.alt || 'An example logo', loading: 'lazy' }))))] : []),
      h('p', { class: 'wd-photocard__text' }, 'Detailed logos are welcome, but photos won\'t work.')); // same size + colour as the first line (Ryan)
    const choice = await openModal({
      mount: ctx.el || document.querySelector('.weight-designer'),
      title: 'This looks like a photo',
      body,
      variant: 'glass photocard',
      actions: [{ id: 'anyway', label: 'It\'s my logo, use it', variant: 'ghost' }, { id: 'upload', label: 'Upload a logo', variant: 'primary' }],
    });
    wdDebug(`Photo card: ${choice || 'closed'}`);
    return choice !== 'upload';
  }

  /** The "YOUR COLORS" chips, already landed — no reveal animation. Shared by the resume (which is
   *  restoring a design, not performing it) and the holes flip (which is correcting one mid-session,
   *  and re-running the one-at-a-time reveal for a checkbox would be theatre). */
  function paintLandedColorChips() {
    const pal = (ctx.state.designProfile || {}).palette || {};
    const shown = shownLogoTones(ctx.state.ui.palette);
    const hexes = shown.length ? shown : [pal.primary, pal.secondary, pal.tertiary].filter(Boolean);
    // The keyed-out school colour is one of "your colors" too (Ryan 2026-08-18: "it's extracting
    // the blue on the Customize Design tab, but not on the Add Logo tab"). Appended, so the mark's
    // own tones keep their extractor order.
    { const f = seatedFieldHex(ctx.state); if (f && !hexes.some((h) => normalizeHex(h) === f)) hexes.push(f); }
    // Eased because the row can change ROW COUNT: the holes flip drops a tone, so a palette that
    // wrapped to two lines comes back on one and the sheet shortens under the buyer's finger.
    easeStep(() => {
      replaceChildren(colorsRow, []);
      colorsBlock.hidden = !hexes.length;
      hexes.forEach((hex) => colorsRow.append(revealChip(hex, { landed: true })));
      syncEditColorsBtn();
    });
  }

  // ── Logo size (Ryan 2026-07-25: moved here from Customize Design → Size & color) ──
  // A MULTIPLIER on the engine's auto-fit, never an absolute: that fit is what keeps the mark
  // inside ring B, balanced against its sibling weight and clear of the grooves, and render-engine
  // re-caps the scaled result at the slot's real room. So this dial cannot push art off the
  // paintable surface, whatever the buyer does with it.
  //   It lives on THIS tab because it acts on the logo, which is what this tab is for — the same
  // argument that put the Organization / Mascot fields here as the identity home.
  /** Is the live mark one of the big-layout marks (Badge / Full Face)? Their ceiling, and therefore
   *  what their dial calls 100%, differs from a slot logo's — see `edgeFraction` below. */
  const bigLogoNow = () => (ctx.state.designProfile.elements || []).some((e) => e && e.type === 'logo' && e.big === true);
  const logoSizeOf = () => {
    const e = (ctx.state.designProfile.elements || []).find((x) => x.type === 'logo');
    const v = Number(e && e.sizeScale);
    return Number.isFinite(v) ? Math.min(LOGO_SCALE_DIAL_MAX, Math.max(LOGO_SCALE_MIN, v)) : 1;
  };
  /* The dial's top: the larger of the badge ceiling and a plain logo's ink reach (Ryan 2026-09-26,
     `LOGO_SCALE_REACH`). Either kind still greys its + at its own geometric wall, well before this. */
  const LOGO_SCALE_DIAL_MAX = Math.max(BIG_LOGO_SCALE_MAX, LOGO_SCALE_REACH);
  const logoProbeOpts = () => ({ product: modelFor(ctx.state.ui.product) });
  const logoSizeStepper = createStepper({
    // Max is the BIG ceiling for everyone; a slot logo greys at its own geometric wall long
    // before it — see `BIG_LOGO_SCALE_MAX`. The dial cannot be per-element: it is built once, at mount.
    value: logoSizeOf(), min: LOGO_SCALE_MIN, max: LOGO_SCALE_DIAL_MAX, step: 0.05,
    fmt: (v) => Math.round(v * 100) + '%', ariaLabel: 'Logo size',
    onInput: (v, { seeded = false } = {}) => {
      // Every logo SLOT — the layouts share one logo across their slots, and syncLogoRaster
      // composites them as one, so sizing must not split them either.
      //   `seeded` is the stepper saying "this is the default arriving, not the buyer". Only a real
      // move may claim the piece is hand-sized; see the note at the seed call below, which used to
      // have to undo this flag afterwards.
      for (const e of ctx.state.designProfile.elements || []) {
        if (e.type !== 'logo') continue;
        e.sizeScale = v;
        if (!seeded) e.sizeUserSet = true;
      }
      // Lean the camera in on the mark being sized (Ryan 2026-07-28) — the Weight tab's move, on the
      // Add Logo tab's dial. `seeded` is why this is here rather than on the buttons: the default is
      // laid in through this same handler at mount, and a zoom fired by the app arriving at a value
      // nobody chose would swing the camera while the buyer is still reading the upload card.
      if (!seeded) ctx.focusLogoZoom?.();
      // The design panel's own `valueCommit` is panel-local (it re-renders the pane); from here the
      // commit is the pair the identity fields use — repaint, then file one history entry.
      // Optional-chained on purpose: this control is BUILT at mount, before main.js has finished
      // assembling ctx, so anything reachable during construction has to tolerate a half-built one.
      // The size is per-layout now, same record as the position — stamp it or a hop away and back
      // restores the size from before this press. See `rememberWeightNudge`.
      if (!seeded) rememberWeightNudge(ctx.state, weightNudgeKey(ctx.state));
      ctx.requestRender?.();
      ctx.refreshDesignThumbs?.();   // …and the same for the size dial, for the same reason
      (ctx.recordHistory || (() => {}))();
    },
    // Grey the −/+ at the wall the buyer can actually see (Ryan 2026-07-25). The dial is a
    // multiplier and the engine re-caps the result at the slot's real room, so on a mark that's
    // already filling its slot every further + moved the number and nothing else. The probe asks
    // the engine what it would paint at the next notch; same answer = the button greys.
    paintedAt: (v) => paintedLogoWidth(ctx.state.designProfile, v, { product: modelFor(ctx.state.ui.product) }),
    // 100% = the mark a hair from the edge of its slot, not "the multiplier the engine happened to
    // pick" (Ryan 2026-07-25). Measured across aspect ratios, today's default lands at 74-87% of
    // that edge — so the dial now opens somewhere in the 80s instead of a misleading 100%, while
    // rendering precisely what it rendered before. Display units only; nothing about the stored
    // scale or the engine changes.
    pctOfMax: true,
    /* ⛔ 100% STILL MEANS WHAT IT ALWAYS MEANT (Ryan 2026-08-09: *"let us increase the size ABOVE
       100% to go to 120%"*).
         This is the part that would have broken quietly. With `pctOfMax` the readout is a share of
       the EDGE — the value at which the art stops growing — which `edgeOf` discovers by scanning to
       `max`. Raising a big logo's ceiling to 1.2 moves that wall to 1.2, so a mark that has always
       read 100% would suddenly read 83%, every saved design's dial would appear to have shrunk, and
       the number Ryan tuned the 75% default against would mean something new. The geometry would be
       right and every readout in the tool would be wrong.
         `edgeFraction` is exactly the lever for this and already exists for the grip's number: it
       re-bases what the dial CALLS 100%. At 1/1.2 the old wall reads 100 again and the grid runs on
       to 120, while `syncEnds` still greys the + at the real geometric wall (`edgeRaw`).
         A FUNCTION, not a constant, because it is only true of a BIG mark — a slot logo's wall is
       its own and must keep reading 100% there. Re-asked on every sync, so a layout change from
       Badge to Fieldhouse re-bases the dial with it. */
    /* ⭐ …AND THE SAME LEVER FOR A PLAIN LOGO'S INK REACH (Ryan 2026-09-26: *"go above 100% in 5%
       increments so long as the logo doesn't expand into the oob area"*). The + now greys where the
       INK meets the band or the arc text, which is past the old wall; without re-basing, the old
       wall would stop reading 100% and every saved design's dial would appear to shrink. So 100%
       stays the OLD wall — the engine's own legacy stop, scanned the same way — and the grid runs
       on above it. Equal walls (no ink answer, a cap face) give exactly 1, i.e. unchanged. */
    edgeFraction: () => {
      if (bigLogoNow()) return 1 / BIG_LOGO_SCALE_MAX;
      const prof = ctx.state.designProfile;
      const o = logoProbeOpts();
      try {
        const now = edgeOf((v) => paintedLogoWidth(prof, v, o), LOGO_SCALE_MIN, LOGO_SCALE_DIAL_MAX, 0.05);
        const old = edgeOf((v) => paintedLogoWidth(prof, v, { ...o, legacy: true }), LOGO_SCALE_MIN, LOGO_SCALE_DIAL_MAX, 0.05);
        return now > 0 && old > 0 ? Math.min(1, old / now) : 1;
      } catch { return 1; }
    },
    // Opens at 80% of that edge (Ryan 2026-07-25) — a mark sized to leave a clear margin, with the
    // remaining fifth of the room there to be claimed deliberately rather than spent by default.
    defaultPct: DEFAULT_SIZE_PCT,
    // NEVER seed at construction (Ryan 2026-07-25: resume went to a white screen). This stepper is
    // created while main.js is still assembling ctx, and on a RESUME the restored profile already
    // has logo elements — so the constructor's seed fired, committed through onInput, and called
    // `ctx.requestRender` before it existed. Mount threw and the whole tool failed to render.
    //   Seeding for this control belongs to `syncLogoSize`, which runs at the beat the design goes
    // live and is the only moment the edge means anything anyway.
    seedDefault: false,
  });
  const logoSizeBlock = h(
    'div',
    { class: 'wd-reveal-sizeblock', hidden: true },
    h('div', { class: 'wd-reveal-label' }, 'Logo size'),
    h('div', { class: 'wd-sizerow' }, logoSizeStepper),
  );

  // ── Logo position: the Badge nudge (Ryan 2026-07-29) ─────────────────────────────────────────
  // "add a toggle of some kind which appears when you choose one of these layout types that allows
  // you to nudge the logo up/down/left/right."
  //
  // BADGE ONLY, and that is a real constraint rather than a scoping shortcut. A plain logo is placed
  // by `pointAnchor` at its slot and steered by the auto-balance drift, with the Advanced angle
  // stepper already there for deliberate placement — a second offset would be a third system arguing
  // over the same coordinates. A badge logo has no anchor point at all: `bigLogoImageBox` places it by
  // a rule, so there was nothing the buyer could do about where it landed. This is that.
  //
  // It sits directly under the Size dial because on these layouts the two do one job — the size
  // shrinks the mark while HOLDING its flush edge (see bigLogoImageBox), and this is how it gets
  // moved off that edge. "Reset logo" at the foot of the CD tab's Logo section clears both.
  const nudgeOf = (axis) => {
    const e = (ctx.state.designProfile.elements || []).find(isNudgeableLogo);
    return clampBigNudge(e && e[axis]);
  };
  // +u right, +v DOWN — the viewBox convention the engine applies it in, so ▲ is negative v. Kept in
  // engine convention on both sides of the wire rather than flipped here: one sign flip in the UI is
  // exactly the kind of thing that reads as correct and renders upside down.
  const NUDGE_DIRS = [
    { key: 'up', glyph: '▲', axis: 'nudgeV', dir: -1, label: 'Nudge logo up' },
    { key: 'left', glyph: '◀', axis: 'nudgeU', dir: -1, label: 'Nudge logo left' },
    { key: 'right', glyph: '▶', axis: 'nudgeU', dir: +1, label: 'Nudge logo right' },
    { key: 'down', glyph: '▼', axis: 'nudgeV', dir: +1, label: 'Nudge logo down' },
  ];
  const nudgeBtns = new Map();
  const bumpNudge = ({ axis, dir }) => {
    // ⚠ NOTHING TO NUDGE → do nothing at all, and this is not paranoia (Ryan 2026-07-30). While the
    // pad's visibility was stale it could be clicked on a layout with no badge logo, and the loop below
    // would then match no element while `focusLogoZoom`, `requestRender` and `recordHistory` all still
    // fired — a camera swing and an undo step for an edit that did not happen. The visibility bug is
    // fixed above, so this is unreachable through the UI; it stays because "the control is showing" and
    // "the control applies" are separate facts, and every other handler here already assumes only one.
    const targets = (ctx.state.designProfile.elements || []).filter(isNudgeableLogo);
    if (!targets.length) { syncNudge(); return; }
    const next = clampBigNudge(nudgeOf(axis) + dir * BIG_NUDGE_STEP);
    if (next === nudgeOf(axis)) return; // already at the cap — the button is greyed, but belt and braces
    // EVERY logo element, exactly as the size dial does: these layouts share one logo across their
    // slots and syncLogoRaster composites them as one, so a position that split them would tear the
    // mark in half. Same loop, same reason.
    for (const e of targets) e[axis] = next;
    /* ⛔ AND STAMP THE RECORD, or this move is forgotten the next time anything restores (Ryan
       2026-08-09). The nudge stopped being carried on the element the moment it became per-LAYOUT
       (`migrateElement`'s note, and `setLayoutPreset`'s), so the store is now the only place a
       position lives across a layout or product change. The weight pad stamps on every press for the
       same reason; this one did not, which made a logo move look like it "did not stick". */
    rememberWeightNudge(ctx.state, weightNudgeKey(ctx.state));
    // Lean in on the mark being moved (Ryan 2026-07-28's rule for the size dial, and it matters more
    // here — a nudge is a few millimetres of a plate that is mostly black rubber at rest).
    /* `refit`, unlike the size dial's plain call: moving the mark off the plate shrinks what there
       is to look at, and Ryan asked the camera to close in on it as that happens. See `runFocusZoom`
       for why this does not re-open the bug the fit-once rule was written for. */
    ctx.focusLogoZoom?.({ refit: true });
    ctx.requestRender?.();
    ctx.refreshDesignThumbs?.();   // the layout cards draw this mark too — see the panel's note
    (ctx.recordHistory || (() => {}))();
    syncNudge();
  };
  const nudgePad = h('div', { class: 'wd-nudge-pad', role: 'group', 'aria-label': 'Logo position' },
    NUDGE_DIRS.map((d) => {
      // `wd-step-btn` is worn deliberately, not decoratively: the glyph sizing, the hover fill, the
      // `:not(:disabled)` hover guard and the 0.4 disabled opacity are all already declared on it, so
      // these arrows are the same control family as every −/+ in the tool and stay that way when the
      // family changes. Copying its look instead would be the mistake that cost five rounds in
      // `match-shared-ui-by-sharing-rules`.
      // Hold to keep going — the travel is up to three plate radii now, which is ~55 taps. Shared
      // with the size dials rather than re-timed here; see `attachHoldRepeat`.
      const btn = attachHoldRepeat(h('button', {
        type: 'button', class: `wd-step-btn wd-nudge-btn wd-nudge-btn--${d.key}`, title: d.label, 'aria-label': d.label,
        onPointerdown: (e) => e.stopPropagation(),
      }, d.glyph), () => bumpNudge(d));
      nudgeBtns.set(d.key, btn);
      return btn;
    }));
  // TWO HEADINGS, ONE OWNER. The size block's travelling `.wd-reveal-label` is hidden inside CD's Logo
  // section (`.wd-dsection--logo .wd-reveal-label`) because CD emits its own "Size" sub-head instead —
  // and that rule catches this block's label too, which is what we want, except CD has no sub-head to
  // emit for a control it appends unconditionally. Rather than have design-panel decide when to emit
  // one — a second place that could disagree with `syncNudge` about whether this is a Badge layout —
  // both wordings live in the block and CSS picks. `wd-subhead` is the real class, not a copy of its
  // look, so "Position" and "Size" cannot drift apart.
  const logoNudgeBlock = h(
    'div',
    { class: 'wd-reveal-nudgeblock', hidden: true },
    h('div', { class: 'wd-reveal-label' }, 'Logo position'),
    h('div', { class: 'wd-subhead wd-nudge-subhead' }, 'Position'),
    nudgePad,
  );
  /**
   * Show the pad only on a Badge layout, and grey each arrow at its own cap.
   *
   * Greyed per DIRECTION, not for the pad as a whole: the two axes are independent and a buyer who
   * has run the mark as far left as it goes must still be able to move it up. Same argument the
   * stepper's `syncEnds` makes for − and + separately, and the same reason it uses `disabled` rather
   * than a class — it stops the click, the tap-and-hold repeat and keyboard activation in one go, and
   * tells a screen reader what the grey tells everyone else.
   */
  function syncNudge() {
    const on = (ctx.state.designProfile.elements || []).some(isNudgeableLogo);
    // Only when it actually MOVES. This runs on every render, and asking the sheet to measure and
    // animate itself for a `hidden` that is already what it was would put a 300ms pin on the sheet
    // several times a second while a buyer drags the size dial.
    if (logoNudgeBlock.hidden !== !on) easeStep(() => { logoNudgeBlock.hidden = !on; });
    if (!on) return;
    for (const d of NUDGE_DIRS) {
      const btn = nudgeBtns.get(d.key);
      if (btn) btn.disabled = Math.abs(nudgeOf(d.axis) + d.dir * BIG_NUDGE_STEP) > BIG_NUDGE_MAX + 1e-9;
    }
  }

  // ── Persistent identity fields (Ryan 2026-07-18: tab 2 = the identity home) ──
  // The Organization / Mascot name lives HERE for good, right under the colours, so
  // there's one obvious place to set/adjust the plate text — and it SURVIVES a logo
  // swap (fixes bug 13). The "Who's this for?" card is just the FIRST-RUN version of
  // these; after the build (or on resume / re-upload) these persist, pre-filled from
  // the profile, and edit the arc text live (applyIdentityText — text only, layout
  // preserved). Hidden until a logo exists.
  // `autocomplete: off` + a neutral `name` keep Chrome's autofill bar (credit card / key / map)
  // off the top of the Android keyboard — see NO_AUTOFILL in design-panel.js for why this field in
  // particular attracted it. Best effort; Chrome does not always honour it.
  const NO_FILL = { autocomplete: 'off', autocorrect: 'off', spellcheck: 'false', name: 'wd-freetext' };
  const orgFieldInput = h('input', { type: 'text', class: 'wd-id-input', placeholder: 'e.g. Arcadia High School', 'aria-label': 'Organization name', maxlength: '40', ...NO_FILL });
  const mascotFieldInput = h('input', { type: 'text', class: 'wd-id-input', placeholder: 'e.g. Anchovies', 'aria-label': 'Mascot or team name', maxlength: '40', ...NO_FILL });
  const identityBlock = h(
    'div',
    { class: 'wd-reveal-identity', hidden: true },
    // "Name on the plate" → "Text on weight" (Ryan 2026-07-25) — the tool sells more than plates
    // now (dumbbells, barbells, grip plates), so the heading names the thing generically.
    h('div', { class: 'wd-reveal-label' }, 'Text on weight'),
    // `wd-keep-typing` = what survives when the phone's keyboard collapses the pane around the field
    // being typed in (main.js, TYPING MODE). Marked on the LABEL rather than the input so the pair
    // stays legible: the sibling that is also a keeper is "the other counterpart text field".
    h('label', { class: 'wd-id-field wd-keep-typing' }, h('span', { class: 'wd-id-label' }, 'Organization'), orgFieldInput),
    h('label', { class: 'wd-id-field wd-keep-typing' }, h('span', { class: 'wd-id-label' }, 'Mascot / team'), mascotFieldInput),
  );
  let identityDebounce = null;
  const commitIdentity = () => {
    // force: this IS the buyer editing these words, so it outranks an earlier edit made on the
    // Customize Design side — the same way an edit there now outranks this one. See applyIdentityText.
    applyIdentityText(ctx.state, { name: orgFieldInput.value, mascot: mascotFieldInput.value }, { force: true });
    ctx.requestRender();
    (ctx.recordHistory || (() => {}))();
    if (ctx.refreshControls) ctx.refreshControls(); // keep Customize Design's text fields in sync
  };
  [orgFieldInput, mascotFieldInput].forEach((i) => i.addEventListener('input', () => {
    clearTimeout(identityDebounce);
    identityDebounce = setTimeout(commitIdentity, 300);
  }));
  /** Show the identity fields, pre-filled from the profile (post-build / resume / swap). */
  function showIdentity() {
    const p = ctx.state.designProfile || {};
    orgFieldInput.value = p.orgName || '';
    mascotFieldInput.value = p.mascot || '';
    // The identity fields and the size dial appear TOGETHER, so the sheet makes ONE movement rather
    // than two 300ms eases racing each other — `showLogoSize`'s own ease nests inside this one and
    // becomes a plain mutation (see `easeStep`).
    easeStep(() => {
      identityBlock.hidden = false;
      // Logo size rides along, SHOWN AND LIVE. Every caller of showIdentity has a rendered design
      // behind it — post-build, finishBeat, or a resume — which is exactly the condition the lock is
      // waiting on. It also has to be re-read from the profile rather than trusted to whatever it was
      // built with: a resumed design arrives with its own scale.
      showLogoSize({ locked: false });
    });
  }

  /**
   * Re-read the logo scale into the stepper. This tab's DOM is built ONCE and persists, unlike the
   * design panel which re-renders on every commit — so anything that changes sizeScale from
   * elsewhere (undo/redo, "↺ Reset all styling", a resume, a layout switch that re-seeds the logo)
   * would otherwise leave this readout showing a percentage the plate no longer has. Wired into
   * main.js's refreshControls, which is the hook every one of those paths already goes through.
   * `setValue` is silent, so syncing can't write the value back and fight the source.
   *
   * Deliberately does NOT touch `hidden`. Visibility belongs to the upload choreography —
   * `state.ui.hasLogo` flips the instant the image lands, several beats before the colours have
   * finished revealing, so keying visibility off it here popped the control out first, ahead of
   * the colour reveal that is the point of the whole sequence (Ryan 2026-07-25).
   */
  /**
   * Re-read the Organization / Mascot fields from the profile. They are the same words the Customize
   * Design → Wording fields edit, from the other end, and editing them THERE now writes back here
   * (Ryan 2026-07-25) — so this tab has to re-read on arrival or it would keep showing the name the
   * buyer already changed. Read-only: it never commits, so it is safe on every tab entry.
   *   Skipped while the field has focus, so it can't overwrite what someone is mid-way through
   * typing on this very tab.
   */
  function syncIdentityInputs() {
    if (identityBlock.hidden) return;
    const p = ctx.state.designProfile || {};
    const active = document.activeElement;
    if (active !== orgFieldInput && orgFieldInput.value !== (p.orgName || '')) orgFieldInput.value = p.orgName || '';
    if (active !== mascotFieldInput && mascotFieldInput.value !== (p.mascot || '')) mascotFieldInput.value = p.mascot || '';
  }

  /**
   * `force` — seed even though the control is still locked, because the CALLER is about to draw.
   *
   * ⛔ THE LOCK IS "DON'T DRAW THE PLATE YET", NOT "DON'T KNOW THE SIZE YET" (Ryan 2026-07-30:
   * *"can you make it so you don't get the logo appearing on the plate then snapping to a larger
   * size? Can't it just render at the right size initially?"*).
   *
   * The logo has TWO seeders and they do not agree. `seedDefaultSizes` writes the engine's number
   * right before the first render; this dial then writes 75% OF ITS OWN EDGE, which is a different
   * calculation — the note on `defaultPct` above measures the engine's default landing anywhere in
   * 74–87% of that edge. Both are defensible; running them in that ORDER is not, because the second
   * commits through `onInput` → `requestRender`, so the buyer watches the plate resolve and then jump.
   *   The build flow already solved this shape for the arc text and the number — "the requestRender
   * on the next line is the one and only draw" — and this dial was the one seeder still firing after
   * it, gated behind `logoSizeLocked` until `showIdentity` un-greys the control. Seeding inside that
   * same pre-render beat makes the first painted frame the final one.
   */
  function syncLogoSize({ seed = false, force = false } = {}) {
    syncIdentityInputs();
    // Seed the 80% default the first time there's actually a design to measure. This stepper is
    // built at mount, before any logo exists, so construction-time seeding had nothing to take a
    // share of — it has to happen here, and only while the buyer hasn't sized the mark themselves.
    const logos = (ctx.state.designProfile.elements || []).filter((e) => e.type === 'logo');
    const untouched = logos.length > 0 && !logos.some((e) => e.sizeUserSet === true);
    // Seeding COMMITS (requestRender + recordHistory), so it must not fire on a mere tab visit —
    // that fans out through refreshControls and rebuilds the panes, which is what flickered the tab
    // through a disabled state on arrival. It is safe to call every sync because `seedTo` returns
    // false without committing when the value is already the seeded one; the guard that matters is
    // `untouched`, which stops it ever overwriting a size the buyer set. A latch was tried here and
    // reverted: it also blocked the RE-seed that "Reset all styling" depends on to get back to 75%.
    // ONLY once the control is live (Ryan 2026-07-25: "the plate is spinning and revealing the
    // design prior to you even adding any text now"). Seeding commits through `onInput`, which calls
    // requestRender — and this runs at the end of the colour reveal, where the control appears
    // greyed. That render dressed the plate and kicked off the reveal spin several beats early,
    // before "Design my Weights" had been pressed. The lock already marks exactly the window where
    // there is no design yet, so it is the right gate for anything that would draw one.
    if (seed && untouched && ctx.state.ui.hasLogo && (force || !logoSizeLocked)) {
      const before = logos[0].sizeScale;
      if (logoSizeStepper.seedTo(DEFAULT_SIZE_PCT)) {
        // The undo that used to live here is gone (2026-07-28): `seedTo` now passes `{ seeded }` and
        // `onInput` above leaves the flag alone, so the seed no longer masquerades as the buyer's own
        // choice. Kept as a note because the failure it prevented — a piece frozen at whatever the
        // first layout happened to allow, with no later geometry able to correct it — is exactly what
        // the weight number and arc text were silently doing, having no such undo of their own.
        if (before !== logos[0].sizeScale) ctx.requestRender();
      }
    }
    logoSizeStepper.setValue(logoSizeOf());
    logoSizeStepper.setEnabled(!logoSizeLocked);
    // The nudge pad rides this refresh too — which covers arriving on the tab, undo/redo, a resume and
    // "Reset logo". Cheap: a `some()` and four `disabled` writes.
    //   ⚠ IT IS NOT ENOUGH ON ITS OWN, and the comment here used to claim it was ("the hook every commit
    // funnels through"). A LAYOUT change commits through design-panel's `structuralCommit`, which never
    // calls `refreshControls`, so this never ran for the one event most likely to change the answer —
    // the pad kept showing on a non-Badge layout and would not come back on returning to a Badge one
    // (Ryan 2026-07-30). `logoLoan.syncNudge` is exported for that path; see the note there.
    syncNudge();
    // The thumbnail catches up too — this is the tab's refresh hook, so it covers arriving on the tab,
    // a resumed design that was already recoloured, an undo, and "Reset all styling" putting the
    // original art back. It no-ops when nothing has been recoloured.
    void refreshRevealPreview();
  }

  // Logo size is SHOWN before it is USABLE (Ryan 2026-07-25). It arrives once the colours have
  // landed — greyed — so the buyer sees it coming while the "Who's this for?" card has their
  // attention, and un-greys at the moment the reveal finishes and the "what next?" fork appears.
  // That instant is the first one where there IS a rendered weight to size a logo against, which is
  // what makes the grey honest rather than decorative.
  let logoSizeLocked = true;
  function showLogoSize({ locked }) {
    logoSizeLocked = locked;
    easeStep(() => {
      // Rise in with the same motion as the colour chips and the who-card, but ONLY when it is
      // actually appearing — `is-revealing` is a one-shot class (see the CSS) so a later tab visit,
      // which re-inserts this element, doesn't replay it.
      if (logoSizeBlock.hidden) {
        logoSizeBlock.classList.add('is-revealing');
        logoSizeBlock.addEventListener('animationend', () => logoSizeBlock.classList.remove('is-revealing'), { once: true });
      }
      logoSizeBlock.hidden = false;
    });
    syncLogoSize({ seed: true });
  }

  // The who-card mounts HERE (below the colors) instead of over them.
  const whoMount = h('div', { class: 'wd-reveal-who' });
  // The safety-net promise, right at the door — only warranted BEFORE a logo lands;
  // hidden once the reveal starts (Ryan 2026-07-16).
  const trustBeat = h('div', { class: 'wd-trust-beat' }, 'Every order is reviewed by our design team before production.');
  const reveal = h(
    'div',
    { class: 'wd-reveal', hidden: true },
    // Logo block mirrors the colors block: label on top, content below. The thumb
    // sits beside the "Upload different logo" tile so a new logo can be swapped in
    // without a page refresh (Ryan 2026-07-16).
    h('div', { class: 'wd-reveal-logoblock' }, revealCaption, slowNote, h('div', { class: 'wd-reveal-logorow' }, revealThumb, replaceTile)),
    /* WD 489 (Ryan 11-01): NO Solid / See-through pair on this tab - it moved into the editor, above the big logo.
       `keyHolesToggle` is still built (its `hidden` is the "two versions differ" flag, and its value the answer the
       editor opens on) but it is never mounted. "Edit see-through parts" sits directly under the logo and the
       Upload tile, and glows on an unsure logo (`syncEditGlow`). */
    tapFixBtn,
    /* WD 498 (Ryan 11-01): "Remove the background color" is NOT on the tab any more - it is the Keep / Remove pair at the
       top of Clean up logo. `removeBgToggle` is still built (its value is the answer the editor opens on) but never mounted. */
    colorsBlock,
    // BELOW the colours, ABOVE the name fields (Ryan 2026-07-25). It first sat directly under the
    // logo art, which made it the first thing to appear on an upload — ahead of the colour reveal
    // that is the moment of the whole beat. The order now matches the order things HAPPEN: the mark,
    // then its colours, then its size, then the words, then what next.
    logoSizeBlock,
    /* ⛔ THE NUDGE PAD IS NOT IN THIS TAB'S TREE, AND THAT IS THE POINT (Ryan 2026-08-09: *"when
       you're on one of these layout styles that makes the joystick buttons eligible for appearance,
       don't add one to the 'Add Logo' tab on desktop or mobile — that's redundant."*)
         It used to sit right here, immediately after the size dial, because on a Badge layout the two
       are one control (`bigLogoImageBox` shrinks the mark while holding its flush edge; the pad moves
       it off that edge). The dial earns its place on both tabs — it is this tab's OWN control, loaned
       up to Customize Design. The pad never was: it only exists for a layout, and choosing a layout
       is what Customize Design is for, so a second copy here is a control at the wrong end of the
       journey.
         It is still BUILT here (`logoNudgeBlock` above) and still handed over through `logoLoan` —
       only its seat in this pane is gone, which leaves the design panel as its single home rather
       than its borrower. `reclaimLoanedNodes` no longer pulls it back; see the note there. */
    identityBlock,
    whoMount,
  );

  // The "Show me what's possible" intro chip mounts HERE, above the dropzone
  // (main.js appends showcase.chipEl). It used to float at the bottom of the canvas
  // and cluttered the band the product bar now owns (Ryan 2026-07-17). Empty until
  // the chip mounts, and collapses to nothing while the chip is hidden (CSS
  // `:empty` / the chip's own `[hidden]`), so it costs no space on other steps.
  const introSlot = h('div', { class: 'wd-logo-intro-slot' });

  const el = h(
    'div',
    { class: 'wd-step wd-step--logo' },
    // Minimal by design (Ryan 2026-07-16): the tool stays distraction-free — the
    // "Required to continue" pill + "Your logo. Your colors. Real equipment." lead
    // were removed. SEO/marketing copy lives above/below the tool + in the FAQ, not
    // on it. The dropzone's own "Drop your logo to start" is enough of a prompt, and
    // the gate is still real (Design/Product tabs stay disabled until a logo lands —
    // see main.js refreshControls). Advanced mode is where extra affordances appear.
    introSlot,
    dropzone,
    trustBeat,
    input,
    replaceInput,
    reveal,
  );

  // ══ THE CUSTOMIZE DESIGN LOAN (Ryan 2026-07-29) ══════════════════════════════════════════════
  // "People who get onto the Customize Design tab easily see the way to adjust the size of the text
  // and numbers, but don't see the ability to adjust the logo size, and don't know they need to go
  // back to the 'Add Logo' tab to do it."
  //   THE SIZE DIAL ONLY. The first pass also lent CD the logo preview and the "Upload different
  // logo" tile; Ryan cut both on sight — "someone on the CD tab has picked a logo they're happy
  // with, so having those 2 features on the CD tab is redundant. But allowing them to change the
  // logo size on the CD tab is practical."
  //
  // ⚠ THE CONTROL IS **MOVED**, NOT COPIED. Two instances of a stepper reading one state object LOOK
  // synced and drift the first time either caches anything — the `SAVE_ITEMS` lesson, and the
  // `sizeUserSet` one. With a single instance there is nothing to sync, by construction, which is
  // exactly what Ryan's "edits on one tab update the other" requirement asks for. It works because
  // only ONE tab body is mounted at a time (`sidePanel.setBody(steps[n].el)`), so a node can simply
  // live in whichever surface is showing — the same trick `designsPanel` already uses.
  //   design-panel appends it into its Logo section (a DOM append RELOCATES), and
  // `reclaimLoanedNodes()` below — run from this step's `refresh()`, which `goToStep` calls on every
  // switch — puts it back on the way in. Both ends are STEP-GATED, because `refreshControls()` fires
  // designPanel.refresh() and logoStep.refresh() back to back on every commit, from whichever tab is
  // showing; ungated they would tug the same node out of each other's pane forever.
  function reclaimLoanedNodes() {
    // Only while THIS tab is the one being shown. On step 3 the design panel legitimately owns it.
    if (ctx.state.ui.currentStep !== 2) return;
    if (logoSizeBlock.parentNode !== reveal) reveal.insertBefore(logoSizeBlock, identityBlock);
    /* ⛔ THE NUDGE PAD IS DELIBERATELY NOT RECLAIMED (Ryan 2026-08-09 — see the note where it used to
       sit in this pane's tree). It is the design panel's control outright now, not a loan, so pulling
       it back on the way into this tab would put the redundant copy straight back. Only the SIZE dial
       travels.
       ⚠ Which means this function no longer round-trips everything `logoLoan` exposes, and a future
       loan must decide for itself: the reclaim is what makes a node belong to THIS tab between
       visits, and leaving it out is what makes a node belong to the panel. */
  }
  // `syncNudge` travels WITH the pad (Ryan 2026-07-30 — the pad kept showing on a non-Badge layout
  // until an arrow was clicked, and would not come back on returning to a Badge one without a reload).
  // A layout change commits through design-panel's `structuralCommit`, which is `requestRender` +
  // `recordHistoryNow` + the panel's own `render()` — it does NOT call `refreshControls`, so nothing
  // reached this step and the pad was re-appended still carrying its stale `hidden`. Exporting the
  // decision rather than the predicate is the point: design-panel calls THIS, so there is still exactly
  // one place that knows what a Badge layout is, now reachable from both paths that can change the
  // answer (this tab's refresh, and the panel's own re-render).
  /**
   * ⛔ THE SIZE DIAL NEEDS THE SAME ESCAPE HATCH THE NUDGE PAD GOT, and for the identical reason.
   *
   * A layout change commits through design-panel's `structuralCommit`, which re-renders that panel
   * but never calls `refreshControls` — so `refresh()`, and the `logoSizeStepper.setValue()` inside
   * it, are not on that path. The stepper caches what 100% MEANS (`refreshEdge`), and that number is
   * a property of the layout: on Full Face the mark ignores the dial entirely, so the edge collapses
   * to the bottom of the range and both ends grey out, correctly.
   *   Switch away to a layout whose logo IS sizeable and the stale edge came with you: the dial sat
   * at a greyed 100% and refused to move, on a design it could size perfectly well (Ryan 2026-07-30:
   * *"the 100% toggle stayed grayed and stuck on 100% and I'm unable to change the size… real bug"*).
   * Nothing was wrong with the geometry; the control had simply never been told to ask again.
   *
   * `setValue` re-probes, so this is one call rather than new logic — the same shape of fix, on the
   * same path, as `syncNudge` two lines above.
   */
  function syncSize() {
    logoSizeStepper.setValue(logoSizeOf());
    logoSizeStepper.setEnabled(!logoSizeLocked);
  }
  const logoLoan = { sizeBlock: logoSizeBlock, nudgeBlock: logoNudgeBlock, syncNudge, syncSize };

  /**
   * One "Who's this for?" text field, with a placeholder that can carry ITALICS.
   *
   * ⛔ `placeholder` IS PLAIN TEXT AND ALWAYS WILL BE. It is an attribute, not
   * markup, so `<em>` inside it renders as literal angle brackets and `::placeholder`
   * styles the whole string or nothing — there is no way to italicise part of it.
   * Ryan asked for exactly that (2026-08-07: "'Organization (optional but
   * recommended)' and make the '(optional but recommended)' part be italics"), so
   * the placeholder becomes a real element painted over the field.
   *
   * HOW IT STAYS IN SYNC WITH THE INPUT, without a single line of JS: the input keeps
   * a real placeholder of one SPACE, which renders as nothing but keeps
   * `:placeholder-shown` matching while the field is empty. The overlay is a sibling,
   * so CSS alone hides it the moment anything is typed. No input listener, no state,
   * nothing to forget to call on a reset — and it cannot desync, because the
   * condition IS the field's emptiness rather than a copy of it.
   *
   * It deliberately does NOT hide on focus. A real placeholder survives focus and
   * disappears on the first character; matching that is why the rule keys off
   * `:placeholder-shown` and not `:focus`.
   *
   * The label is on the INPUT (`aria-label`) and the overlay is `aria-hidden`, the
   * same split the gate's detail bar uses: the visual layer would otherwise read the
   * same words a second time. The parenthetical is part of the accessible name on
   * purpose — "is this required?" is exactly what a screen-reader user needs here,
   * and it is the only place the answer appears.
   *
   * @param {string} label  the field itself, e.g. 'Organization'
   * @param {string} hint   the italic parenthetical, e.g. '(optional)'
   */
  function wtfField(label, hint) {
    const input = h('input', {
      type: 'text',
      class: 'wd-wtf-input',
      placeholder: ' ', // ⚠ ONE SPACE, NOT EMPTY — `:placeholder-shown` needs a placeholder to exist.
      'aria-label': `${label} ${hint}`,
      maxlength: '40',
    });
    /* ⛔ ONE INNER SPAN, NOT TWO SIBLINGS OF THE FLEX BOX. `.wd-wtf-ph` is `display: flex` for
       vertical centring, which makes the text node and the `<em>` two FLEX ITEMS — and flex discards
       the whitespace between items. So "Organization" and "(recommended)" rendered welded together
       no matter how the string was written, twice (Ryan 2026-08-07: *"you keep writing it with no
       space between the word and the ("*). The space was always in the markup; the layout was eating
       it. Wrapping both in a single child puts them back in normal inline flow, where a space is a
       space, and leaves the flex container with exactly one item to centre. */
    const ph = h('span', { class: 'wd-wtf-ph', 'aria-hidden': 'true' },
      h('span', { class: 'wd-wtf-ph__t' }, `${label} `, h('em', null, hint)));
    /* ⛔ `wd-keep-typing` IS THE OPT-IN FOR THE PHONE'S TYPING MODE, AND THIS CARD NEVER HAD IT
       (Ryan 2026-08-10). *"Started from scratch, picked a weight, uploaded a logo, waited for the
       colors to be extracted, went down, clicked on the organization text box, the keyboard slid up
       and covered the box I was trying to type into… as soon as I click 'Design my weights',
       regardless of whether I typed anything into either field or not, should I click on either of
       the 2 fields it'll scroll up appropriately at that point."*

       🔑 THOSE ARE TWO DIFFERENT "ORGANIZATION" FIELDS AND ONLY ONE WAS EVER WIRED UP. The pair
       below the plate (`.wd-id-field.wd-keep-typing`, the identity block) carries the marker and
       has always worked. THIS pair — the "Who's this for?" card, which is the FIRST thing a new
       buyer ever types into — is a different component with different classes, so main.js's
       `if (!t.closest('.wd-keep-typing')) return;` bailed and typing mode never engaged at all. Not
       a timing bug, not a height bug: the handler declined to run. That is exactly why the boundary
       Ryan drew is "Design my weights" — pressing it removes this card and reveals the wired one.

       ⚠ THE OPT-IN IS PER FIELD BY DESIGN (main.js: *"opting in per field is what keeps a screen
       this component has never seen from being broken by default"*), which is a good rule that
       silently makes every NEW text field broken-by-default until someone remembers this line. Any
       future field a buyer types into on a phone needs it. */
    return { input, el: h('div', { class: 'wd-wtf-field wd-keep-typing' }, input, ph) };
  }

  /**
   * Scroll the pane's own scroller to the bottom, so a card just appended there is on screen.
   *
   * ⛔ NOT `scrollIntoView`, and that is the whole reason this function exists. `scrollIntoView`
   * scrolls EVERY scrollable ancestor up to and including the window — and on desktop the designer is
   * embedded in a long Shopify page, so it would yank the storefront around to frame a card inside a
   * pane. Walking up to the FIRST real scroller and moving only that one keeps the effect inside the
   * tool.
   *   It walks rather than taking a handle on the shell because logo-step has no reference to the
   * side panel, and giving it one to scroll a div would be a lot of coupling for one line.
   */
  function scrollPaneToBottom(from) {
    let node = from.parentElement;
    while (node && node !== document.body) {
      const oy = getComputedStyle(node).overflowY;
      if ((oy === 'auto' || oy === 'scroll') && node.scrollHeight > node.clientHeight) {
        try {
          node.scrollTo({ top: node.scrollHeight, behavior: prefersReduce() ? 'auto' : 'smooth' });
        } catch { node.scrollTop = node.scrollHeight; } // older WebKit: no options object
        return;
      }
      node = node.parentElement;
    }
  }

  // STAGE 1 — "Who's this for?" card (Fable §3.5): name (optional) + the single
  // "Design my Weight" CTA that BUILDS + renders the weight. Mounts into `whoMount`
  // (BELOW the persisted colors) and slides down, so the colors stay visible.
  // Resolves { name, mascot }; empty fields → blank strings (skip).
  function askWhoIsThisFor() {
    return new Promise((resolve) => {
      // A tab click during the color reveal already chose the destination → skip the
      // card and build straight away, as if "Design My Weights" had been pressed blank.
      if (forcedDest) { resolve({ name: '', mascot: '' }); return; }
      /* "(recommended)", not "(optional but recommended)" (Ryan 2026-08-07). The longer phrase said
         both things and led with the weaker one — a buyer skimming a placeholder reads the first
         word, and "optional" is permission to skip the field we most want filled in. "Recommended"
         already implies it is not required, and the `*` convention on the quote form is what marks
         a field that genuinely is. */
      const nameField = wtfField('Organization', '(recommended)');
      const mascotField = wtfField('Mascot', '(recommended if you have one)');
      const nameInput = nameField.input;
      const mascotInput = mascotField.input;
      /* Assigned below, once the card exists. Declared here because `finish` is every exit from this
         card — the CTA, Enter, and a tab-3/4 click arriving through `whoResolve` — so it is the one
         place guaranteed to run, and the nudge's timer has to die with the card rather than fire
         into a detached node. */
      let stopWhoHint = () => {};
      // The card LEAVING is a height change too, and the biggest downward one in the flow — two
      // fields and a CTA vanish the instant "Design My Weights" is pressed. Eased, so the sheet
      // settles into the build instead of dropping into it.
      const finish = (val) => { stopWhoHint(); whoFocusName = null; whoAfterTapFix = null; whoResolve = null; whoValues = null; easeStep(() => card.remove()); resolve(val); };
      const commit = () => finish({ name: nameInput.value, mascot: mascotInput.value });
      [nameInput, mascotInput].forEach((i) => i.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); commit(); } }));
      const card = h(
        'div',
        /* ── NO CARD ON THE PHONE (Ryan 2026-08-08) ──────────────────────────────────────────────
         * *"the 'Who's this for?' card is too much… We just need the 'Who's this for' formatted just
         * like the headers above it like 'YOUR COLORS', with the 2 text fields below it, and then the
         * big design my weights button below that."*
         *
         * In a pane where every other section is a heading over its controls, a bordered glass panel
         * reads as a dialog that arrived rather than the next thing to fill in. Desktop keeps the
         * card — there the pane is wider and the card gives the question a boundary.
         *
         * ⚠ THE CLASSES ARE DROPPED, NOT OVERRIDDEN, in both places below. `.wd-flat-glass` supplies
         * fill, rim, directional light, drop shadow AND a ::before gloss dome as ONE recipe, so
         * un-picking it in CSS means fighting five declarations and its pseudo-element; the same
         * reasoning `.wd-stepnav__btn` records for dropping it. And the title takes the REAL
         * `.wd-reveal-label` — the actual class "YOUR COLORS" wears — instead of a copy of its
         * values, so the two headings cannot drift apart. */
        {
          class: 'wd-wtf-card' + (isPhone() ? '' : ' wd-flat-glass wd-flat-glass--flush'),
          role: 'group',
          'aria-label': "Who's this for?",
        },
        h('div', { class: isPhone() ? 'wd-reveal-label' : 'wd-wtf-title' }, "Who's this for?"),
        nameField.el,
        mascotField.el,
        h(
          'div',
          { class: 'wd-wtf-actions' },
          h('button', { type: 'button', class: 'wd-btn wd-wtf-go wd-flat-glass wd-flat-glass--flush', onClick: commit }, 'Design My Weights'),
        ),
      );
      // Expose this card's commit to a tab click (bug 9): the tab acts as the button.
      whoResolve = finish;
      whoValues = () => ({ name: nameInput.value, mascot: mascotInput.value });
      easeStep(() => whoMount.append(card));
      /* ── THE PHONE DOES NOT AUTOFOCUS, AND IS NUDGED INSTEAD (Ryan 2026-08-08) ──────────────────
       * *"the card auto selects the top text input field, which means the keyboard auto expands too.
       * this makes the viewable area of the expanded card pretty small… poor UI and even worse UX if
       * they can't see enough and are confused with how to proceed."*
       *
       * Autofocus is a good default on a desktop, where a caret costs nothing and saves a click. On a
       * phone it summons the keyboard over the card the buyer has just been handed — it hides the
       * Mascot field, the CTA, or both, at the exact moment they are trying to work out what this
       * card is asking. So the phone gets the whole card first.
       *
       * ⚠ THE COST OF NOT FOCUSING IS THAT NOTHING POINTS ANYWHERE, which is why the nudge is part of
       * the same change rather than a follow-up: unfocused, the card has no visible entry point. After
       * `WHO_HINT_IDLE_MS` of the buyer not engaging, Organization wears the same white ring the sheet
       * and the forward button use, so the eye is given the first field without the keyboard taking
       * the screen.
       */
      /* The app's OWN caret must not read as the buyer engaging (that would cancel the nudge): `appFocusing` marks it.
         Never on a phone (the keyboard - see above), never into a card the buyer is already in, never into a typed field. */
      let appFocusing = false;
      const focusName = () => {
        if (isPhone() || !card.isConnected || card.contains(document.activeElement) || nameInput.value) return;
        appFocusing = true;
        try { nameInput.focus({ preventScroll: true }); } finally { appFocusing = false; }
      };
      whoFocusName = focusName;
      if (!glowOn()) focusName(); // WD 489: not while Clean up logo glows
      /* ── BOTH PLATFORMS SCROLL, AND THE OLD NOTE HERE WAS WRONG ABOUT DESKTOP (Ryan 2026-08-15) ──
       * *"the box adds vertical height to the left side-pane, so depending on the size of your
       * monitor, when that box appears, you might not be able to see all of it, and the 'Design My
       * Weights' button might not be visible when it loads."*
       *
       * ⛔ `focus()` DOES BRING ITS ELEMENT INTO VIEW — AND THAT IS EXACTLY WHY DESKTOP WAS BROKEN.
       * The 2026-08-08 note claimed the autofocus made a scroll unnecessary off the phone. It brings
       * ONE element into view, by the MINIMUM distance: the pane stopped the instant Organization
       * cleared the fold, leaving Mascot and the CTA below it on any short viewport. The card is the
       * last child of `whoMount`, which is the last child of `reveal`, which is the last child of
       * this step — so it is genuinely the bottom of the pane, and scrolling there frames all of it.
       *   `preventScroll` above is what keeps this ONE movement: without it the browser's minimal
       * jump lands first and the smooth scroll then starts from somewhere else.
       *
       *   One frame first: the card is appended synchronously but its `margin-top` and the pane's
       * own relayout have to land before `scrollHeight` describes the finished list. The `wtfRise`
       * entrance is opacity + translateY only, so it does not affect the number — no need to wait
       * the animation out, just the layout.
       *   (`scrollPaneToBottom` walks to the first REAL scroller rather than calling
       * `scrollIntoView`, which is what makes it safe to run on the desktop storefront at all — see
       * its own note. On a monitor tall enough that the pane never overflows, its guard means this
       * is a no-op, which is the correct behaviour: nothing is off screen.) */
      requestAnimationFrame(() => {
        /* WD 501 (Ryan 11-02): NOT while Clean up logo glows - on a phone this scroll is what carried the pulsing button
           off the top of the sheet. It runs when the editor closes instead (`whoAfterTapFix`). */
        if (!glowOn()) scrollPaneToBottom(card);
        /* ⛔ `focusin` CANNOT BE ARMED BEFORE THE AUTOFOCUS OR IT CANCELS ITSELF. Desktop focuses
           Organization synchronously above, which fires `focusin` on the card — a listener attached
           first reads the app's OWN caret as the buyer engaging and kills the timer before it ever
           arms, so the nudge would simply never appear. A frame later is the whole fix. The phone
           focuses nothing and could have taken this either way; one path is better than two. */
        card.addEventListener('focusin', onWhoFocusIn);
      });
      const onWhoFocusIn = () => { if (!appFocusing) stopWhoHint(); };
      let whoEngaged = false;
      const startWhoHint = () => {
        if (whoEngaged || !card.isConnected) return;
        nameField.el.classList.add('is-hinting');
        focusName(); // ...and the caret goes in with it (desktop; a no-op if it is already there)
      };
      let whoHintTimer = window.setTimeout(() => {
        whoHintTimer = null;
        if (glowOn()) return; // WD 501: Clean up logo keeps the floor until its editor closes - `whoAfterTapFix` takes over
        startWhoHint();
      }, isPhone() ? WHO_HINT_IDLE_MS : WHO_HINT_IDLE_DESKTOP_MS);
      /* WD 501: the editor closed while this card is still up - bring the card into view and point at Organization NOW
         (the buyer has already had their wait). Not if they have already started on the card. */
      whoAfterTapFix = () => {
        if (!card.isConnected) return;
        if (whoHintTimer) { window.clearTimeout(whoHintTimer); whoHintTimer = null; }
        scrollPaneToBottom(card);
        startWhoHint();
      };
      /* ⚠ CANCELLED BY ENGAGEMENT OF ANY KIND, not just by typing in that one field. `focusin` on the
         CARD covers either input (a buyer who went to Mascot first is not stuck); `input` covers the
         desktop buyer who never moves the caret and just starts typing — the ONE case `focusin`
         cannot see, precisely because the app put the caret there itself; and `finish` covers the
         three ways out — the CTA, Enter, and a tab-3/4 click routed through `whoResolve`. */
      stopWhoHint = () => {
        whoEngaged = true;
        if (whoHintTimer) { window.clearTimeout(whoHintTimer); whoHintTimer = null; }
        nameField.el.classList.remove('is-hinting');
        card.removeEventListener('focusin', onWhoFocusIn);
        card.removeEventListener('input', stopWhoHint);
      };
      card.addEventListener('input', stopWhoHint);
    });
  }

  /* ── STAGE 2 IS GONE — the "Your weight's ready — what next?" fork (retired 2026-08-06) ────────
   * It was a card offering "Customize Design" / "Request Quote", shown once at the end of the first
   * upload. Ryan replaced it with the pane's PERMANENT nav footer (`ui/step-nav.js`), on the
   * argument that a fork existing at exactly one instant is a route, not a control: a buyer who
   * resumed a design, or simply came back to this tab, was never offered a way onward again.
   *
   * ⚠ WHAT THE CARD DID BESIDES NAVIGATE, and where each of those lives now — checked before
   * deleting it, because removing a box must not remove a side effect:
   *   · `showLogoSize({ locked: false })` — un-greying the size dial the moment there is a plate to
   *     size against. Already covered: `showIdentity()` runs on the line above the old call site and
   *     does exactly this itself (see its body).
   *   · `forkScrolled` — a one-time scroll-into-view. Died with the card; nothing else read it.
   *   · `nextResolve` — the bug-9 hook that let a tab click resolve the fork. That window no longer
   *     exists: with no card to wait on, the build routes straight through. `whoResolve` and
   *     `forcedDest` are untouched, so a tab click during the colour reveal or the who-card still
   *     behaves exactly as it did.
   *   · analytics — NONE. `wdTrack(WD_EVENTS.BUILT)` fires before the fork, and the funnel has no
   *     per-branch event (the worker rejects any key outside `WD_EVENTS`), so nothing is lost.
   */

  // `processing` = actively running reveal work (cleaning / colour reveal / build).
  // It guards against overlapping uploads (WD-05) BUT is deliberately released while
  // we're just waiting on the buyer (the "Who's this for?" card), so a re-upload IS
  // allowed then — it was `busy` staying true through the who-card wait that made
  // "Upload different logo" do nothing (Ryan 2026-07-16).
  let processing = false;
  let hasBuilt = false; // the plate has been dressed at least once (post "Design My Weights")
  // Build-flow interception (Ryan 2026-07-18, bug 9): the Customize Design / Request
  // Quote tabs UNLOCK the instant a logo lands, so a click on either mid-upload must
  // act EXACTLY like the "Design My Weights" button — commit whatever's typed, build,
  // and land on that tab. Because the click can arrive at either beat (the color reveal
  // or the who-card), we track whichever card is live and resolve it, and honor a
  // pre-chosen destination so the flow never hangs waiting on a card the buyer bypassed.
  let buildFlowActive = false; // true from logo-land until the build routes to a step
  let forcedDest = null;       // a tab click pre-chose the dest → land there instead of Customize Design
  let whoResolve = null;       // resolver of the live "Who's this for?" card (if shown)
  let whoValues = null;        // () => current { name, mascot } from that card
  function inBuildFlow() { return buildFlowActive; }
  /** A tab (Customize Design=3 / Request Quote=4) was clicked while the build flow is
   *  live → behave like the "Design My Weights" button and route to `dest`. */
  function commitWhoWithDest(dest) {
    if (!buildFlowActive) return false;
    forcedDest = dest;
    if (whoResolve) { const r = whoResolve, v = whoValues; whoResolve = null; whoValues = null; r(v ? v() : { name: '', mascot: '' }); }
    // else: no card up yet (color reveal running) → askWhoIsThisFor honors forcedDest
    return true;
  }
  const setProcessing = (v) => {
    processing = v;
    input.disabled = v;
    dropzone.classList.toggle('is-loading', v);
  };
  // Gray + disable the live "Who's this for?" card while a re-upload
  // processes, then restore it so the buyer can continue (Ryan 2026-07-16).
  function setWhoCardBusy(v) {
    const card = whoMount.firstElementChild;
    if (!card) return;
    card.classList.toggle('is-busy', !!v);
    card.querySelectorAll('input, button').forEach((elm) => { elm.disabled = !!v; });
  }
  // Strip the current logo + colours at the start of a re-upload so the new ones
  // reveal on a clean slate (dramatically, like the first upload).
  function beginReplace({ keepThumb = false } = {}) {
    setWhoCardBusy(true);
    // Stripping the old logo's colours shortens the sheet; the new palette lengthens it again a
    // couple of beats later. Eased both ways, so a swap reads as one exchange rather than a drop
    // and a snap.
    easeStep(() => {
      colorsBlock.hidden = true;
      replaceChildren(colorsRow, []);
      syncEditColorsBtn();
    });
    // WD 499: Clean up logo's own re-ingest already shows its finished logo here - keep it, never a blank (Ryan 11-01)
    if (!keepThumb) revealImg.removeAttribute('src');
  }

  // Narrated processing beat (Fable §3.4) — sets the reveal caption + announces
  // it to assistive tech, so each step of the ~sequence reads as craftsmanship.
  /**
   * The reveal caption under the thumbnail. Sole surface for this flow's progress since 2026-08-08.
   *
   * The trailing "…" is ANIMATED (ui/busy-dots.js — a static ellipsis is indistinguishable from a
   * frozen UI, and it is the one glyph claiming something is still happening). That animation is now
   * load-bearing rather than decorative: it is the whole of the "work is in flight" signal.
   *
   * ⛔ THE CANVAS BUSY PILL WAS REMOVED FROM THIS FLOW — A DELIBERATE REVERSAL, NOT AN OVERSIGHT.
   * Ryan 2026-08-08: *"this pill, broadly speaking on mobile, just adds clutter that most people
   * won't even read."* It read as clutter because `narrate` put the SAME SENTENCE on screen twice at
   * once — here, under the thumbnail the buyer is watching, and again in the pill floating over the
   * model. Only the three server beats ever raised it (background removal + the two upscales).
   *
   * ⚠ IT IS REVERSING A CONSIDERED DECISION, so here is the argument it overrules rather than a
   * deleted comment. The pill was added 2026-07-29 (*"so people don't think nothing is happening and
   * click off"*) precisely BECAUSE this caption "sits under a thumbnail in a slide-out they may have
   * collapsed, while their eyes are on the plate." That objection is still true and is the one thing
   * this trades away: with the pane collapsed there is now no progress indicator at all. Accepted on
   * the grounds that you have to hide the panel yourself to get there and the cleanup finishes
   * regardless — but if "did it freeze?" ever comes back, THIS is the paragraph that predicted it,
   * and the fix is a pill shown only when the caption is not on screen, not a second unconditional
   * copy of the same sentence.
   *
   * ⚠ `status.busy` IS STILL LIVE ELSEWHERE — `ui/project-push.js` raises it for the packet render,
   * which has no caption to fall back on. This removed one CALLER, not the capability. (It is also
   * delay-gated in status-bar.js, so a fast beat never showed it at all.)
   */
  /* ── THE SHEET RISES WITH ITS CONTENT, IT DOES NOT JUMP TO IT (Ryan 2026-08-08) ───────────────
   * *"as you upload a logo, things will get added and populate on the slide up pane, causing the
   * height to jump around a bit… ensure that as things get added and the height adjusts, it slides
   * up/down smoothly and gently."*
   *
   * The bottom sheet is `height: auto` under a `max-height` cap, and `auto` is not interpolable —
   * so every `hidden = false` in this file resized it in one frame. `easeContentChange` in
   * side-panel.js already solves exactly this (measure → pin → mutate → measure → animate); it was
   * built for the design panel's section swap and nothing in the upload choreography had been
   * routed through it.
   *
   * ⚠ WRAPPED INSIDE THE HELPERS, NOT AT THE CALL SITES. `showReveal`, `showLogoSize`,
   * `showIdentity`, `syncNudge` and the chip appends each have several callers — a resume, a swap,
   * the first upload — and wrapping the helper is what makes every one of them smooth rather than
   * whichever ones somebody remembered. It is also the difference between one rule and eleven.
   *
   * ⚠ RE-ENTRANT BY DESIGN. `showIdentity` calls `showLogoSize`, so a naive wrapper would nest one
   * measure/pin dance inside another and the outer would measure a height the inner had pinned.
   * The depth guard makes the OUTERMOST call own the animation and the inner ones plain mutations,
   * which is also the right answer visually: two blocks appearing together are one movement.
   * `mutate()` runs synchronously inside `easeAround`, so the depth is still raised while `fn` runs.
   *
   * Free on desktop and while the sheet is collapsed — `easeContentChange` runs the callback and
   * returns in both cases, so this costs nothing off the phone. */
  let easingDepth = 0;
  const easeStep = (fn) => {
    if (easingDepth > 0 || typeof ctx.easeSheet !== 'function') { fn(); return; }
    easingDepth += 1;
    try { ctx.easeSheet(fn); } finally { easingDepth -= 1; }
  };

  const endSlowNote = () => {
    clearTimeout(slowTimer); slowTimer = 0;
    showSlowNote(false);
  };
  const armSlowNote = () => {
    endSlowNote();
    slowTimer = setTimeout(() => { slowTimer = 0; if (processing) showSlowNote(true); }, SLOW_NOTE_MS);
  };
  const narrate = (msg) => {
    if (msg === 'Your logo') endSlowNote(); // the caption has settled: the work it stood in for is done
    // Eased too: the caption is one line at "Your logo" and two at "Cleaning up your logo…" on a
    // narrow phone, and a beat that only changes the words still moves the sheet when it rewraps.
    easeStep(() => replaceChildren(revealCaption, [labelWithDots(msg)]));
    ctx.announce?.(msg);
    // Unconditional: nothing in this flow raises the pill any more, so the only thing left to do is
    // make sure a stale one from somewhere else can never sit over the reveal.
    ctx.status?.clearBusy?.();
  };
  const prefersReduce = () => media('(prefers-reduced-motion: reduce)').matches;
  // Choreography dwell — a real pause between beats, collapsing to ~0 under
  // reduced-motion so the sequence still lands, just instantly.
  const dwell = (ms) => wait(prefersReduce() ? 0 : ms);
  // Show the reveal panel (hiding the dropzone) with `src` in the thumbnail.
  // `transparent` puts it on the checkerboard so a cut-out logo reads as cut-out.
  function showReveal(src, transparent) {
    // The largest single height change in the flow — a dropzone leaves and a whole reveal panel
    // arrives. Eased as ONE movement rather than as a shrink followed by a growth.
    easeStep(() => {
      dropzone.hidden = true;
      trustBeat.hidden = true; // the review promise belongs on the empty dropzone, not the reveal
      reveal.hidden = false;
    });
    if (src) revealImg.src = src;
    revealThumb.classList.toggle('is-checker', !!transparent);
  }
  // Crossfade the thumbnail to a new source (the "we cleaned it up" moment).
  async function crossfadeThumb(src, transparent) {
    if (prefersReduce()) { revealImg.src = src; revealThumb.classList.toggle('is-checker', !!transparent); return; }
    revealImg.classList.add('is-fading');
    await wait(190);
    revealImg.src = src;
    await decodeImage(src).catch(() => {});
    revealThumb.classList.toggle('is-checker', !!transparent);
    revealImg.classList.remove('is-fading');
    await wait(220);
  }

  // -------------------------------------------------------------------------
  // Upload pipeline
  // -------------------------------------------------------------------------
  async function handleFile(file, opts = {}) {
    const { status, announce } = ctx;
    /* RETAIN THE FILE AS SUPPLIED, before anything in this function touches it
       (spec: LOGO-ARTWORK-RETENTION-SPEC.md). Everything below reassigns one
       `dataUrl` through re-encode → upscale/rembg → trim, so this is the last
       moment the customer's actual bytes exist. It only PARKS them; `commit()`
       after the reveal is what makes them the answer, so an upload that dies in
       `prepareForDisplay` cannot leave a file behind for a logo that never landed.
       ⚠ `fromRender` is the PDF path re-entering with a raster WE generated (see
       the `return handleFile(png, opts)` below) — held as a no-op so the PDF, not
       our PNG of it, stays the retained original. A PDF is frequently the best
       production art we will ever be given. */
    artworkHold.hold(file, { fromRender: opts.fromRender === true });
    // The buyer committed their OWN logo (dropped / picked a file) → RETIRE the
    // showcase for good: tears its stage overlay down AND permanently hides the
    // "Show me what's possible" chip, so nothing pulls them back out of the design
    // flow (Ryan 2026-07-18). Idempotent, so a re-upload is a harmless no-op.
    ctx.retireShowcase?.();

    // PDF → render page 1 to a PNG, then run the SAME pipeline on that raster.
    // Common for school/gym logos (a designer's vector saved as PDF). pdf.js is
    // lazy-loaded only when a PDF actually lands, so it never weighs the bundle.
    if (file.type === 'application/pdf' || /\.pdf$/i.test(file.name)) {
      if (processing) return;
      if (file.size > MAX_FILE_BYTES) {
        status.error('That PDF is over 30 MB — please use a smaller file (or attach the original at quote time).');
        announce('That PDF is too large to preview — use a smaller file, or attach it at quote time.');
        return;
      }
      setProcessing(true);
      status.guide('Reading your PDF…');
      announce('Reading your PDF…');
      let png = null;
      const pdfT0 = perfNow();
      try {
        const { renderPdfToPng } = await import('./pdf-to-image.js');
        const pdfT1 = perfNow();
        png = await renderPdfToPng(file);
        wdDebug(`PDF read: module ${Math.round(pdfT1 - pdfT0)}ms, page -> ${png && png.name} in ${Math.round(perfNow() - pdfT1)}ms`);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[weight-designer] PDF render failed:', err);
      }
      setProcessing(false);
      if (!png) {
        status.guide("Couldn't read that PDF for a preview — attach it at quote time (we use it for production), or drop a PNG or JPG.");
        announce("Couldn't read that PDF — attach it at quote time, or use a PNG or JPG for the preview.");
        return;
      }
      // `fromRender` stops artwork-hold overwriting the PDF with the PNG we just made.
      return handleFile(png, { ...opts, fromRender: true }); // re-enter with the rendered raster
    }

    // Non-displayable vector (EPS/AI): perfect for production, useless in an <img>
    // (PDF is handled above). Guide the buyer to attach it at quote time.
    if (NON_DISPLAY_VECTOR.test(file.name)) {
      status.guide('Vector file? Perfect — attach it at quote time; we use it for production. For the preview, drop a PNG or JPG.');
      announce('Vector file received — attach it at quote time. Use a PNG or JPG for the on-screen preview.');
      return;
    }

    // Oversized file → refuse before decoding (a full-res decode of a 12000px
    // photo can OOM a phone). Cheap byte check, no decode (WD-06).
    if (file.size > MAX_FILE_BYTES) {
      status.error('That image is over 30 MB — please use a smaller file (or attach the original at quote time).');
      announce('That image is too large to preview — use a smaller PNG or JPG.');
      return;
    }

    if (processing) return; // WD-05: ignore concurrent uploads / overlapping re-uploads
    setProcessing(true);
    if (opts.replace) beginReplace({ keepThumb: !!opts.fromBgToggle }); // strip the old logo + colours before the new reveal
    // WD 490: logo or photo? Started now, beside the decode; awaited once the file is read. A vector is a drawing.
    const photoJob = (file.type === 'image/svg+xml' || /\.svg$/i.test(file.name)) ? null : startPhotoCheck(file);

    status.guide('Reading your logo…');
    let prepared;
    try {
      prepared = await prepareForDisplay(file);
    } catch {
      setProcessing(false);
      if (opts.replace) setWhoCardBusy(false); // undo the gray-out on a failed replace
      // HEIC (iPhone default) can't decode in most browsers — say so specifically
      // instead of a generic "bad image" (WD-07).
      if (HEIC_RE.test(file.type) || HEIC_RE.test(file.name)) {
        status.error("That's an iPhone HEIC photo we can't preview here — export it as PNG or JPG (Photos → Share → Options → Most Compatible), or attach it at quote time.");
        announce("HEIC photo can't be previewed — export a PNG or JPG, or attach it at quote time.");
      } else {
        status.error("Couldn't read that image — a PNG or JPG works best.");
        announce("Couldn't read that image. Try a PNG or JPG.");
      }
      return;
    }

    /* WD 490: a photo gets the card BEFORE anything below runs. "Upload a logo" puts the screen back (a replace only
       greyed the card and cleared the thumbnail and chips; the design itself is untouched until much later) and opens
       the picker. "Use it anyway", Escape and a closed card all carry on, exactly as without the check. */
    if (photoJob) {
      const pc = await Promise.race([photoJob, new Promise((res) => setTimeout(() => res(null), PHOTO_WAIT_MAX_MS))]);
      if (pc && pc.show && !(await keepPhotoAnyway())) {
        setProcessing(false);
        ctx.status?.clearBusy?.();
        status.guide('Drop your logo to start.');
        if (opts.replace) {
          setWhoCardBusy(false);
          const cur = liveLogo();
          if (cur && cur.dataUrl) revealImg.src = cur.dataUrl;
          paintLandedColorChips();
        }
        (opts.replace ? replaceInput : input).click();
        return;
      }
    }

    armSlowNote(); // WD 491: counted from here - the photo card's wait is the buyer reading, not the tool working

    const isVector = file.type === 'image/svg+xml' || /\.svg$/i.test(file.name);
    // The declared-ink anchors for this upload (vectors only) — consumed by BOTH extraction
    // sites (the upload pass and the holes-flip re-analysis). Reset per upload so a raster
    // following an SVG never inherits the previous mark's inks.
    pendingSvgInks = isVector ? svgDeclaredInks(await file.text().catch(() => null)) : null;
    if (pendingSvgInks) wdDebug(`SVG declares ${pendingSvgInks.length} ink(s) — palette anchored to them`);

    // A fresh upload starts with no key applied → hide the holes toggle until one is.
    keyHolesToggle.hidden = true;
    keyCtx = null;
    holesLocked = false; holesPending = null;
    tapFixBtn.hidden = true;
    tapFixBase = null;
    editGlowWanted = false; if (!opts.fromBgToggle) tapFixDone = false; syncEditGlow();
    // Same for the background offer: the probe below decides, and it has not run yet. Left alone on
    // the toggle's own re-ingest, which is still describing the logo it was armed for.
    if (!opts.fromBgToggle) removeBgToggle.hidden = true;

    // §3.3 cleanup — free client-side white-key (Tier 0), escalating to the paid
    // server cleanup (Tier 1) only when the free pass isn't enough.
    let dataUrl = prepared.dataUrl;
    // The flat field a SERVER cleanup removed, when no client key ran — measured by diffing
    // pre/post rasters (Ryan 2026-08-18, Michigan-Dearborn: the navy square IS a school colour,
    // but rembg removed it on a path where `keyField` was never measured, so the field-swatch
    // seat had nothing to read). null when the removal wasn't a flat field. Declared at the
    // scope the logo record is built in — the cleanup runs in an inner block.
    let serverField = null;
    let paletteSource = prepared.paletteSource;
    let transparentBg = /png|webp|svg/i.test(file.type) || isVector;
    const smallest = Math.min(prepared.dims.width || Infinity, prepared.dims.height || Infinity);
    if (!isVector && prepared.paletteSource) {
      // Beat 1 (§3.4): "Cleaning up your logo…" — the reveal panel opens with the
      // ORIGINAL in the thumbnail; if a real cleanup lands we crossfade to it so
      // the buyer actually SEES the cleanup happen (Ryan 2026-07-16).
      showReveal(prepared.dataUrl, transparentBg);
      narrate('Cleaning up your logo…');

      const sourceHasAlpha = imgHasAlpha(prepared.paletteSource);
      const opaque = !sourceHasAlpha;
      // A source that ALREADY carries transparency is a finished cut-out — its
      // background is gone. Running a bg key on it samples the logo's own edge as
      // "background" and eats it (the Blue Sky gradient lost its pale-blue top), and
      // esrgan upscaling would flatten its alpha to white. So skip BOTH for alpha art.
      const needUpscale = opaque && Number.isFinite(smallest) && smallest > 0 && smallest < UPSCALE_MIN_EDGE;
      wdDebug(`── ${file.name}  ${prepared.dims.width || '?'}×${prepared.dims.height || '?'}  alpha=${sourceHasAlpha}  min=${Number.isFinite(smallest) ? smallest : '?'}`);

      // ORDER IS LOAD-BEARING: Real-ESRGAN (upscale) FLATTENS alpha to white, so the
      // alpha-producing step (bg removal) must always run LAST. We therefore upscale
      // the OPAQUE original first, then remove the background — never the reverse
      // (the reverse is the West Elk white-rectangle bug; the server's own
      // rembg→esrgan order has the same flaw, so we drive the two ops as separate
      // single-op calls and control the sequence here).
      let workUrl = prepared.dataUrl;
      let workSource = prepared.paletteSource;

      // Step 1 — upscale a small mark FIRST, on the opaque original.
      if (needUpscale) {
        narrate('Enhancing your logo…');
        const up = await serverCleanup(prepared.dataUrl, { removeBg: false, upscale: true });
        wdDebug(`Upscale (original) → ${up.cleaned ? `OK (${up.ms}ms)` : `SKIPPED:${up.reason} [${up.httpStatus ?? '—'}] (${up.ms}ms)`}`);
        if (up.cleaned) {
          workUrl = up.cleaned;
          workSource = await decodeImage(up.cleaned).catch(() => prepared.paletteSource);
        }
      }

      // Step 2 — background removal LAST (this is what produces alpha). Free Tier-0
      // white-key first; escalate to the server rembg only for an opaque, non-white
      // background the key can't touch (e.g. a dark JPEG). Only ADOPT Tier-0 when it
      // truly cut something (removedFrac > 0.04).
      let cleanedUrl = null;
      let cleanedSource = null;
      let bgRemoved = transparentBg; // source already carried its own alpha

      // Only key an OPAQUE source — an alpha source is already cut out (see above).
      const t0 = opaque ? autoKey(workSource) : null;
      const t0Cut = !!(t0 && t0.removedFrac > 0.04);
      if (opaque) wdDebug(`Tier-0 bg-key on ${needUpscale && workUrl !== prepared.dataUrl ? 'upscaled' : 'original'}: cut ${t0 ? (t0.removedFrac * 100).toFixed(0) : '0'}% → ${t0Cut ? 'ADOPTED' : 'ignored'}`);
      else wdDebug('Source already transparent (alpha) → skip bg key (nothing to cut)');
      // ⚠ AN ALPHA SOURCE IS STILL ASKED THE QUESTION — it just is not answered for them. A rounded
      // badge has alpha (its corners) AND a solid field, and only the buyer knows which they meant.
      // `autoKey` is the probe as well as the tool: it skips transparent border pixels, so it reads
      // the field colour straight through the rounding, and its `removedFrac` says whether keying
      // would do anything worth offering.
      //   The 0.10 floor is higher than the 0.04 the automatic path adopts at — an offer should be
      // about a real background, not a hairline. The 0.90 ceiling is the same guard the cut-holes
      // default uses: past that the "background" is most of the mark and removing it leaves nothing.
      //   ⚠ EVERY INGEST RE-ASKS, EXCEPT THE TOGGLE'S OWN. Flipping it re-enters here with the keyed
      // art, whose border IS transparent — so the probe would find nothing, the offer would clear and
      // the switch would vanish with no way back. `fromBgToggle` keeps the offer (and therefore the
      // original file) alive across its own round trip. A genuinely new upload takes the other branch
      // and re-decides from scratch, which is what stops a previous logo's offer outliving it.
      if (!opts.fromBgToggle) {
        bgOffer = null;
        if (!opaque) {
          const probe = autoKey(workSource);
          if (probe && probe.keyColor && probe.removedFrac > 0.10 && probe.removedFrac < 0.90) {
            bgOffer = { file, keyed: probe };
            wdDebug(`Alpha source with a solid rgb(${probe.keyColor.join(',')}) field covering ${(probe.removedFrac * 100).toFixed(0)}% → offering "Remove the background color"`);
          }
        }
        removeBgUI.set(false);
      }
      if (t0Cut) {
        // DEFAULT TO SHOWING THE WEIGHT THROUGH (Ryan 2026-07-28: "think this should be checked as a
        // default setting on both mobile and desktop"). `autoKey` picks per background — a solid
        // colour cuts the holes, near-WHITE keeps interior fills, on the reasoning that a white
        // pocket might be an intentional separator stroke. On a plate that call is the wrong way
        // round more often than not: the pockets read best as plate showing through, which is the
        // whole point of the control.
        //   The box cannot simply be ticked — that would claim a state the IMAGE does not have,
        // since `t0` was already keyed the other way. So the flip is re-keyed for real here, using
        // exactly the path the toggle itself uses.
        //
        // ⛔ AND THE INK TEST BELOW HAS TO RUN IN BOTH DIRECTIONS — it used to sit behind
        // `if (!t0.keyGlobal)`. `chooseBackgroundKey` returns `global: !white`, so a COLOUR
        // background arrives with the holes ALREADY cut, and the one measurement written to stop a
        // global key from eating the artwork was unreachable on exactly the marks that needed it.
        //   Ryan 2026-08-17, on the CSI eagle: a BLACK backdrop, keyed globally, takes the eagle's
        // black linework with it — flood cuts 47.2%, global 56.9%, and the extra is **18.3% of the
        // mark**, more than twice the bar this test already sets. Every outline, the eye and the
        // counters inside CSI go, the remainder flattens to one tone, and the plate shows a
        // featureless cream silhouette. Same file with See-through NO: perfect.
        //   So both variants are built whichever way the pick landed, and the same corpus-calibrated
        // test chooses between them. Nothing about the near-white path moves: there `floodKeyed`
        // IS `t0` and `globalKeyed` IS the `g` this block always computed.
        const tag = (r, global) => (r ? Object.assign(r, {
          keyColor: t0.keyColor, keyWhite: t0.keyWhite, keyField: t0.keyField, keyGlobal: global,
        }) : null);
        const floodKeyed = t0.keyGlobal ? tag(bgKey(workSource, t0.keyColor, false, t0.keyWhite, t0.keyField), false) : t0;
        const globalKeyed = t0.keyGlobal ? t0 : tag(bgKey(workSource, t0.keyColor, true, t0.keyWhite, t0.keyField), true);
        // The conservative variant is the resting answer; the block below promotes it to global
        // unless that would cost artwork. `|| t0` covers `bgKey` refusing the flood outright
        // (it returns null when a key removes nothing), which leaves the global as the only image.
        let keyed = floodKeyed || t0;
        {
          // ── HOW MUCH MORE DOES GLOBAL TAKE? (Ryan 2026-07-28) ─────────────────────────────────
          // The flood removes the background. Global removes the background AND every other pixel of
          // that colour anywhere in the image. The DIFFERENCE between them is therefore exactly the
          // enclosed same-coloured area — and whether flipping the default is right or catastrophic
          // depends entirely on what that area IS.
          //   On most marks it is incidental: the counter inside a D, the gap in an A, a few percent
          // that reads better as plate showing through. That is the case this default was added for
          // and it still gets it.
          //   On the Denver Broncos badge it was the lettering and the horse — a white-on-navy
          // wordmark and the mark's whole focal point. Global keyed all of it away, so the art lost
          // its most prominent feature and white vanished from the extracted palette with it (Ryan:
          // "there's lots of white in the logo… hmm"). The old 0.95 guard was written for a mostly-
          // white MARK and nowhere near tripping at ~25%.
          //   So the test is the extra — but AGAINST WHAT? (Ryan 2026-08-03, on the wolf badge: the
          // box auto-ticked and *"that turned out really patchy and bad too… shouldn't it be smart
          // enough to not touch the logo colors?"*)
          //
          // ⚠ THE OLD PAIR OF TESTS MEASURED THE EXTRA AGAINST THE FRAME, AND THE FRAME IS NOT A
          // FIXED QUANTITY — it is however much white margin the upload happens to carry. That is the
          // SAME defect bg-detect.js's header describes in the old border-ring rule ("a proxy that
          // assumes every upload has a margin"), reappearing one function later in a new disguise.
          // Two concrete failures, both measured on Ryan's own files:
          //   • `global >= flood * 1.5` is UNSATISFIABLE once the background passes ~40% of the
          //     frame, because the extra would have to be half the flood again and there is not that
          //     much image left. At the wolf's 70.9% flood it needed 35.5% extra out of 29.1%
          //     available. **Any logo with a generous margin could never keep its holes**, however
          //     much artwork the cut destroyed.
          //   • Cinderwood shipped a hole-cut that ate **29.4% of the mark** — nearly a third of the
          //     artwork — precisely because that ratio test cannot pass at a 63% flood.
          //
          // So the denominator is now THE MARK, which is what the rule was always reaching for: of
          // the ink the buyer actually uploaded, how much would showing the plate through take away?
          // That question is independent of the margin, so the two frame-relative tests collapse into
          // one honest test and the ratio term is gone rather than re-tuned.
          //
          // 8% is calibrated against the corpus, not chosen: the correct KEEP answers land at 9.7%
          // (the wolf's face), 13.4%, 19.5% (the Broncos horse + wordmark) and 29.4%, and the correct
          // CUT answers at 0.5%, 1.0% and 1.4%. Nothing sits between 1.4% and 9.7%, so the bar is set
          // in open space — if a future file lands near it, re-measure rather than nudging it.
          //
          // ⛔ NOT A PALETTE-IDENTITY RULE, though that is the intuitive fix. "Is the key colour one
          // of the extracted 'Your colors'?" is true for a large share of marks where cutting the
          // holes is exactly right, because any logo with white anywhere in its art extracts white.
          // AREA is the axis that separates the cases; identity is not.
          const GLOBAL_EXTRA_OF_MARK = 0.08;
          const both = !!(globalKeyed && floodKeyed);
          const extra = both ? globalKeyed.removedFrac - floodKeyed.removedFrac : 0;
          // The flood removed the background, so what it left IS the mark. Guarded against a
          // degenerate all-background frame, which the 0.95 test below catches anyway.
          const markFrac = Math.max(1 - (floodKeyed ? floodKeyed.removedFrac : t0.removedFrac), 1e-6);
          const extraOfMark = extra / markFrac;
          const eatsInk = both && extraOfMark >= GLOBAL_EXTRA_OF_MARK;
          // ── AND WHEN THE BITE IS BIG, ASK WHAT SHAPE IT IS (2026-08-18) ───────────────────────
          // `eatsInk` alone cannot decide this: CSI needs KEEP at 18.3% of the mark and Hanover's
          // letter counters need CUT at 24.6% — the sizes are in the wrong order, so no threshold
          // fixes it (Ryan's hand-test of the 326 ship; the fourth size-predicate to die here).
          // What separates them is the SHAPE of the enclosed pockets: several moderate holes with
          // none dominant are letter counters and cutting them is what the artwork intends; one
          // huge pocket is a shape somebody drew (Coastal's coyote face); hundreds of slivers are
          // anti-aliased linework (CSI, Webster). measurePocketProfile measures it with bgKey's
          // exact match semantics on the same 1024px working frame — construction drift here flips
          // verdicts (EAC Monsters reads 3 big pockets at 256px smoothed and 2 at bgKey's own
          // scale; only the latter agrees with the rendered plate).
          //   Layered UNDER eatsInk, not replacing it: small bites keep today's cut default
          // untouched, and the shape question is only asked of the big bites eatsInk would refuse.
          // Corpus (probe-pocket-shape.mjs --all, 17 client-keyed): exactly ONE default moves —
          // Hanover, keep → cut, the reported fault.
          const counterPockets = both && eatsInk && (() => {
            try {
              const iw = workSource.naturalWidth || workSource.width;
              const ih = workSource.naturalHeight || workSource.height;
              if (!iw || !ih) return false;
              const s = Math.min(1, 1024 / Math.max(iw, ih));
              const pw = Math.max(1, Math.round(iw * s));
              const ph = Math.max(1, Math.round(ih * s));
              const cnv = document.createElement('canvas');
              cnv.width = pw; cnv.height = ph;
              const pcx = cnv.getContext('2d', { willReadFrequently: true });
              pcx.imageSmoothingEnabled = false; // bgKey keeps pixels real; so must this
              pcx.drawImage(workSource, 0, 0, pw, ph);
              const pd = pcx.getImageData(0, 0, pw, ph).data;
              const profile = measurePocketProfile(pd, pw, ph, t0.keyColor || t0.keyField,
                t0.keyColor ? COLOR_KEY_STRICT : WHITE_BG_RADIUS);
              const verdict = pocketsReadAsCounters(profile);
              if (profile) {
                wdDebug(`Pocket shape: ${profile.pockets} pockets, ${profile.bigCount} ≥1% of mark (${profile.bigBgTone} background-coloured), `
                  + `largest ${(profile.largestShare * 100).toFixed(1)}% `
                  + `→ ${verdict ? 'LETTER COUNTERS (cut anyway)' : 'artwork or slivers (keep holds)'}`);
              }
              return verdict;
            } catch { return false; }
          })();
          // The original guard stays: even when the extra looks incidental, a global key that leaves
          // almost nothing standing is never the right default.
          // ⚠ NO PLATE-AWARE "KEEP THE FILL" HERE — TRIED ON 2026-07-29 AND REVERTED SAME DAY.
          // The idea was that showing a BLACK plate through a counter inside BLACK ink turns the mark
          // to mush, so the fill should be kept. The premise was wrong, and Ryan found it in one
          // upload: **this app's remedy for a logo that merges with the plate is to INVERT THE INK**
          // (`e.logoCenterColor = ink` in ui/state.js), not to fill the pockets. Which means the
          // merge resolves itself — the ink goes white, the counter shows black plate, and the mark
          // reads perfectly.
          //   Keeping the fill actively broke it, because it turned a ONE-TONE mark into a TWO-tone
          // one. The structure detector then saw a fill plus a band, the flatten recoloured only the
          // fill, and RIGAR came out as white letters with white counters (invisible) plus a ragged
          // tail of misclassified glyphs — "PERFORMANCE all white except CE, and an O with a white
          // interior". Ryan's own read was the correct design: *"just extract all the white to leave
          // JUST a black logo, then invert it to white since it'd be a black logo on a black plate."*
          //   The genuine keep-the-fill cases — a pocket that is intentional artwork rather than a
          // letter counter — are already caught by `eatsInk` above and by the 0.95 guard, both of
          // which ask about the LOGO and neither of which needs the plate. Do not re-add a plate
          // term here without first re-reading how the inversion works.
          /* ── ⭐ AND A KEY COLOUR THAT IS MOSTLY INSIDE THE MARK IS NOT A BACKGROUND (WD 440) ────
           * Ryan, three flags on `www-cmlions-org-athletics`: *"the logo isn't showing the white
           * bits on it (the letters) and it should"*. Its C M C P are WHITE GLYPHS knocked out of a
           * navy/red checkerboard that fills the frame; the key picks white, the pocket shape reads
           * four moderate holes and calls them letter counters, and the global cut deletes the
           * lettering. The mark reaches the palette as two tones with no white anywhere in it.
           *
           * ⛔ THE TEST WD 433 PROPOSED — "would cutting remove a colour the mark has?" — WAS BUILT
           * AND IT DOES NOT SEPARATE. It fires on `athletics-hanover-edu` too, the deliberate CUT:
           * Hanover's counters are filled with the same blue as its background, so the cut removes
           * that blue from the flood palette exactly as cmlions' cut removes white. In BOTH cases
           * the pixels removed are the key colour, which is what a palette diff can see and what it
           * cannot tell apart. `app/tools/probe-cut-drops-a-tone.mjs` is that measurement, kept.
           *
           * ⭐ WHAT DOES SEPARATE THEM IS WHERE THE KEY COLOUR LIVES. A background is by definition
           * mostly OUTSIDE the mark: the border flood reaches it. Measured over every mark in the
           * corpus that reaches this decision (`app/tools/score-hole-cut.py`, 37 of them), against
           * the enclosed extra as a share of the background the flood actually found:
           *
           *     www-cmlions-org   flood  8%  extra 14.3%   ratio 1.79   <- his flag, the only one over 1
           *     tigers            flood 33%  extra 16.6%   ratio 0.50   <- the next CUT down
           *     drop-bear-logo    flood 50%  extra  7.9%   ratio 0.16
           *     athletics-hanover flood 65%  extra  8.8%   ratio 0.14   <- the deliberate CUT
           *     ...28 more cuts, every one at 0.12 or less
           *
           * The bar is **1.0** and it is not fitted: it is the point where a colour stops being
           * mostly around the mark and starts being mostly inside it. Nothing sits between 0.50 and
           * 1.79, so anywhere in that 3.6x gap flips exactly ONE decision — the one he reported.
           * ⚠ The two marks above 0.5 that are NOT cut (120081000896 at 0.83, coastalcoyotes at
           * 0.52) already keep their holes, so the gate cannot move them either way.
           *   A flood that removes NOTHING is the extreme of the same statement — a key colour with
           * no outside at all — and refuses for the same reason rather than dividing by zero. */
          const insideOutweighsOutside = both
            && (floodKeyed.removedFrac <= 0 || extra >= floodKeyed.removedFrac);
          if (globalKeyed && globalKeyed.removedFrac < 0.95 && (!eatsInk || counterPockets)
              && !insideOutweighsOutside) {
            keyed = globalKeyed;
          }
          if (both) {
            wdDebug(`Hole default: flood cut ${(floodKeyed.removedFrac * 100).toFixed(0)}%, global ${(globalKeyed.removedFrac * 100).toFixed(0)}% `
              + `(extra ${(extra * 100).toFixed(1)}% of frame = ${(extraOfMark * 100).toFixed(1)}% of the MARK, `
              + `${(extra / (floodKeyed.removedFrac || 1)).toFixed(2)}x the background) `
              + `→ ${insideOutweighsOutside ? 'KEEP HOLES (that colour is mostly INSIDE the mark - it is not a background)'
                : (eatsInk ? (counterPockets ? 'cut holes (big extra, but it is letter counters)' : 'KEEP HOLES (that extra is the logo)') : 'cut holes')}`);
          } else {
            wdDebug(`Hole default: only one variant survived (flood=${floodKeyed ? 'ok' : 'null'}, global=${globalKeyed ? 'ok' : 'null'}) → keeping ${keyed.keyGlobal ? 'global' : 'flood'}`);
          }
        }
        cleanedUrl = keyed.dataUrl; cleanedSource = keyed.canvas; bgRemoved = true;
        // A key was applied → arm the "cut holes" toggle at the mode's default so the
        // buyer can flip the enclosed-pocket call. Source is the keyed image (upscaled
        // or original); logo is attached once it's built below.
        keyCtx = { source: workSource, keyColor: keyed.keyColor, keyWhite: keyed.keyWhite, keyField: keyed.keyField, logo: null };
        setHolesUI(!!keyed.keyGlobal);
        /* ⛔ THE TOGGLE IS NOT REVEALED HERE ANY MORE — IT WAS LIVE MID-FLIGHT (Ryan 2026-08-08:
         * *"if I check it, it tries to apply it, and then it unapplies the checkbox without me doing
         * anything"* — and, decisively, *"there's no issues when I check the box after clicking
         * 'Design my weights'"*).
         *
         * That second observation is the whole diagnosis. This line ran here, several `dwell` beats
         * before the upload flow finishes — so the buyer could flip a control while the flow that
         * OWNS the logo was still running. The flow then builds its own logo record further down
         * (`keyGlobal` re-read, `keyGlobalUserSet: false`, `dataUrl` from the auto-key) and hands it
         * to `setLogo`, wiping the flip. After "Design My Weights" there is no flow left to clobber
         * it, which is exactly why it worked there.
         *
         * ⚠ FIXED BY REMOVING THE RACE, NOT BY WINNING IT. Guarding `keyGlobalUserSet` or reordering
         * the writes would leave a control that is live during a window where another writer is
         * authoritative — the same class of bug, one flag further on. The state is still seeded here
         * (the line above), so the checkbox shows the right answer the moment it appears; only the
         * REVEAL moves, to the point where the flow hands the finished logo over. */
      }

      const needBg = opaque && !t0Cut;
      if (needBg) {
        narrate('Enhancing your logo…');
        const bg = await serverCleanup(workUrl, { removeBg: true, upscale: false });
        wdDebug(`Tier-1 rembg on ${workUrl !== prepared.dataUrl ? 'upscaled' : 'original'} → ${bg.cleaned ? `OK (${bg.ms}ms)` : `SKIPPED:${bg.reason} [${bg.httpStatus ?? '—'}] (${bg.ms}ms)`}`);
        if (bg.cleaned) {
          cleanedUrl = bg.cleaned; cleanedSource = null; bgRemoved = true;
          serverField = await removedFieldColor(workUrl, bg.cleaned);
          if (serverField) wdDebug(`Server cleanup removed a flat field rgb(${serverField.join(',')}) — candidate school colour`);
        }
      }

      // The offer goes up with the art it is about, so the buyer is looking at the background while
      // deciding whether to keep it. `checked` reflects which version is actually live rather than
      // being reset — on the toggle's own re-ingest it has already been set by the buyer, and on any
      // other ingest the gate above put it back to false.
      easeStep(() => { removeBgToggle.hidden = !bgOffer; });
      /* WD 498: a removable background is a choice the buyer should see, so Clean up logo glows for it the same way it
         does for an unsure cutout (Ryan: "yes, same glow") - same one-glow-at-a-time rules, released by the reveal. Not on
         the editor's own re-ingest: the buyer has just answered it. */
      if (bgOffer && !opts.fromBgToggle) { editGlowWanted = true; syncEditGlow(); }

      // If we upscaled but removed no background (an opaque non-white logo the key
      // couldn't touch and rembg skipped), still adopt the sharper upscaled art.
      if (!cleanedUrl && workUrl !== prepared.dataUrl) {
        cleanedUrl = workUrl;
        cleanedSource = workSource;
      }
      await dwell(REVEAL_CLEAN_MS); // let the "cleaning…" beat read

      if (cleanedUrl && cleanedUrl !== prepared.dataUrl) {
        // Auto-accept the cleaned art (§3.3) and SHOW it: crossfade the thumbnail
        // to the result, on the checkerboard only if a background was truly removed.
        dataUrl = cleanedUrl;
        transparentBg = bgRemoved; // honest — never claim transparent on an opaque upscale
        wdDebug(`Adopted cleaned art · transparentBg=${transparentBg}`);
        // Palette from the cleaned art — the Tier-0 canvas, or decode the Tier-1
        // data URL (so a removed background never pollutes the extracted colors).
        paletteSource = cleanedSource || await decodeImage(cleanedUrl).catch(() => prepared.paletteSource);
        await crossfadeThumb(cleanedUrl, transparentBg);
        narrate('Cleaned it up ✓');
        await dwell(REVEAL_CLEANED_MS);
      } else {
        wdDebug('No cleaned art adopted — keeping original');
      }
    }

    // Auto-trim transparent margins on a cut-out logo so the mark FILLS its slot —
    // the same crop keyed logos already get in bgKey, but an ALREADY-transparent
    // upload skips the keyer, so its designer padding survived and rendered the mark
    // small/short beside the weight (the E Logo carried ~20% top/bottom padding, so
    // the "E" rendered ~60% of its box). Runs on the chosen paletteSource so the
    // palette + structure read the trimmed mark too; a keyed (already-trimmed) or
    // tightly-cropped source just no-ops (trimmed = null). Vectors keep their viewBox.
    let trimmed = null;
    if (!isVector && transparentBg && paletteSource) {
      trimmed = trimTransparent(paletteSource);
      if (trimmed) {
        dataUrl = trimmed.dataUrl;
        paletteSource = trimmed.canvas;
        if (keyCtx) keyCtx.logo = null; // rebuilt below with the trimmed art
        wdDebug(`Trimmed transparent margins → ${trimmed.width}×${trimmed.height} (aspect ${(trimmed.width / trimmed.height).toFixed(2)})`);
      }
    }

    // Hopeless-file safety net (§3.3): tiny/pixelated art still proceeds (upscaled
    // above when possible), with the remaster promise attached — never a dead end.
    if (!isVector && Number.isFinite(smallest) && smallest > 0 && smallest < 200) {
      ctx.status.guide("This file is small — we'll remaster your logo from your official artwork before anything is made. Keep going.");
    }

    // ── THE ASPECT MUST DESCRIBE THE RASTER BEING PLACED ────────────────────────────────────
    // Second half of Ryan's 2026-07-29 report, and a quieter fault than the trim. The engine
    // reserves the logo's box from `aspectRatio`, so that number has to describe `dataUrl` — the
    // art it is about to paint — and nothing else.
    //   It didn't. `trimmed` is the SECOND trim (`trimTransparent`), and it is null in the common
    // keyed case, because bgKey already cropped and this one correctly no-ops on an already-tight
    // source. The fallback then reached for `prepared.dims` — the ORIGINAL FILE. So a keyed upload
    // was described by its pre-key dimensions: the Wexmoor badge painted at 0.667 (768×1152, the
    // untouched JPEG) while the raster in hand was 665×1024, i.e. 0.649. Small next to the trim
    // bug, but the same species — a measurement of something other than the thing being drawn.
    //   So: prefer the adopted raster's real dimensions, and keep `prepared.dims` only for the
    // paths where no raster exists to ask (vectors, or an untouched original whose display copy
    // was downscaled — there the pre-downscale source dims are still the honest answer, per WD-20).
    const placed = trimmed
      || (!isVector && paletteSource && (paletteSource.width || paletteSource.naturalWidth)
        ? { width: paletteSource.naturalWidth || paletteSource.width, height: paletteSource.naturalHeight || paletteSource.height }
        : null);
    // Deliberately NARROW: only the two paths where WE cropped the raster ourselves — the second
    // trim, and an adopted Tier-0 key (bgKey always crops). An already-transparent PNG that no
    // keyer touched keeps `prepared.dims`, because there the pre-downscale SOURCE dimensions are
    // still the honest answer and they feed the quality bucket (WD-20).
    const placedIsCrop = !!trimmed || !!keyCtx;
    if (placed && placedIsCrop && Math.abs((placed.width / placed.height) - (prepared.aspect || 0)) > 0.005) {
      wdDebug(`Aspect from the PLACED raster: ${placed.width}×${placed.height} = ${(placed.width / placed.height).toFixed(3)}`
        + ` (source file was ${(prepared.aspect || 0).toFixed(3)})`);
    }
    const logo = {
      dataUrl,
      fileName: file.name,
      mimeType: file.type || (isVector ? 'image/svg+xml' : 'image/png'),
      width: placedIsCrop && placed ? placed.width : prepared.dims.width || null,
      height: placedIsCrop && placed ? placed.height : prepared.dims.height || null,
      aspectRatio: placedIsCrop && placed ? placed.width / placed.height : prepared.aspect || (isVector ? 1 : null),
      isVector,
      transparentBg,
      // Carried so a RESUMED session can rebuild the holes toggle (design-profile.js serializes
      // both). null/null when no client key ran — the toggle stays hidden, as before.
      keyColor: keyCtx ? keyCtx.keyColor ?? null : null,
      // The near-white cutoff this key ran at. Carried for the same reason as `keyColor`: a resume
      // re-derives the OTHER variant with bgKey, and re-deriving it at a different cutoff than the
      // one that produced the image would make the toggle flip to something that never existed.
      keyWhite: keyCtx ? keyCtx.keyWhite ?? null : null,
      // The client key's measured field when one ran; else the flat field a SERVER cleanup
      // removed (Michigan-Dearborn's navy — see `removedFieldColor`). Feeds the field-swatch
      // seat only; `keyColor`/`keyGlobal` stay null on the server path so the holes toggle
      // behaves exactly as before.
      keyField: keyCtx ? keyCtx.keyField ?? null : serverField ?? null,
      keyGlobal: keyCtx ? keyHolesUI.get() : null,
      // ⚠ AUTO UNTIL TOUCHED. Kept from the reverted plate-aware experiment because it is correct
      // on its own terms and step 3 (the A/B choice) needs it: the moment the buyer answers the
      // show-through question themselves, nothing may overrule them. Same family as `sizeUserSet`
      // and `textUserSet`.
      keyGlobalUserSet: false,
      // Set a few lines below, once the artwork has been measured — see `extractClearSpot`. Declared
      // here so the record's shape is one object rather than a base plus a later mutation.
      clearSpot: null,
    };
    /* Hand the built logo to the holes toggle so a live flip can update it in place.
       ⛔ AND ONLY THAT — THE REVEAL MOVED AGAIN, TO `armKeyHolesToggle` IN THE `finally` BELOW
       (2026-08-08, second pass). 247 moved it here on the reasoning that "nothing downstream
       rewrites the record from the auto-key any more". That is not true yet at this line, and the
       counter-example is three statements away: `runBeat` awaits the colour reveal and then the
       who-card, and calls `setLogo(state, logo)` at the build beat — with THIS captured object,
       not with whatever the toggle has since made. A flip during that wait therefore built a new
       record, pointed `keyCtx.logo` at it, and then watched "Design my Weights" put the pre-flip
       artwork back while the checkbox stayed ticked. The same race 247 described, surviving in the
       one window it did not measure — which is exactly why Ryan's *"there's no issues when I check
       the box AFTER clicking Design my weights"* was the whole diagnosis and not half of it.
         Same remedy as before, applied where it actually bites: the control is not live while
       another writer owns the logo. `runBeat` and `replaceReveal` are both awaited inside the try,
       so the `finally` IS "the flow is finished with it" for every path including the failing ones.
       (And it is not only the record: that window also re-runs `applyPalette` /
       `applyLogoStructure` from pre-flip data and repaints the chips one at a time from the
       pre-flip palette. Deferring the reveal closes all four at once; guarding the record alone
       would have left three.) */
    if (keyCtx) keyCtx.logo = logo;
    pendingLogo = logo; // …and the tab's own handle on it, for the pre-build recolour preview

    // Extract palette from the chosen source (cleaned canvas if kept, else the
    // original decoded img/canvas) so a removed white bg doesn't pollute colors.
    // For a COLOUR key, tell the extractor which colour was removed so it can drop
    // the orphaned bg↔logo anti-alias blends (greys/bricks) it would otherwise leave.
    // Background is always removed (keyed to transparent, or the source was already
    // transparent) before this point, so any remaining white/black is foreground →
    // allow a neutral to fill the secondary slot (fixes a "colour + white" logo like
    // Blue Sky leaking the design's default yellow secondary onto the outline).
    const paletteOpts = {
      allowNeutralSecondary: true,
      ...(keyCtx && keyCtx.keyColor ? { removedBg: keyCtx.keyColor } : {}),
      ...(pendingSvgInks ? { paletteAnchors: pendingSvgInks } : {}),
    };
    const palette = await extractPalette(paletteSource || file, paletteOpts).catch(() => null);
    if (palette && Array.isArray(palette.all)) {
      const tot = palette.all.reduce((a, c) => a + (c.population || 0), 0) || 1;
      const fmt = (s) => `${s.hex} ${((100 * (s.population || 0)) / tot).toFixed(1)}%`;
      wdDebug(`Palette (${palette.confidence}): ${palette.all.map(fmt).join('  ')}`);
      wdDebug(`  primary=${palette.primary ? palette.primary.hex : '—'} secondary=${palette.secondary ? palette.secondary.hex : '—'} shown≥${(0.03 * 100).toFixed(0)}%`);
    }

    // B1 structure-aware auto-styling: read the CUT-OUT logo as a fill + ordered
    // inner→outer outlines and auto-assign the text lockup's fill/outline order +
    // thickness to MATCH (Elite "E" = teal fill / white / rust). Only on a cut-out
    // source (bg removed / alpha art) with a confident palette — the silhouette is
    // what makes the layering readable. A crest (no clean nesting) comes back
    // low-confidence and we keep the plain palette styling.
    //
    // ⚠ KNOWN GAP, deliberately not patched here (Ryan 2026-07-28). This call is also the only thing
    // that measures `colorBorneDetail` — the verdict that stops the app flattening a badge to a disc —
    // so a logo that never reaches it is a logo the flatten is offered on. Widening the gate does not
    // help: `analyzePixels` returns `all: []` on its low-confidence exit, so there is literally no
    // palette to analyse a structure against. Closing it properly means having that exit return its
    // colours, which would move the reveal chips and every other `all` consumer. Left as-is until a
    // real logo demonstrates the case.
    // WHERE THE ARTWORK ISN'T (Ryan 2026-07-30) — measured once, here, because the render path is
    // DOM-free and can never look at pixels. Full Face bleeds the mark across the whole cap, so the
    // weight number has to go ON it, and this is what tells the zone where it will do least harm.
    // Unconditional: unlike structure analysis it needs no palette, only an alpha channel.
    const clearSpot = paletteSource ? await extractClearSpot(paletteSource).catch(() => null) : null;
    if (clearSpot) {
      // Stamped with the scan's version so `backfillClearSpot` can tell a current answer from one
      // an older build left behind — see CLEAR_SPOT_VERSION.
      logo.clearSpot = { u: clearSpot.u, v: clearSpot.v, ver: CLEAR_SPOT_VERSION };
      wdDebug(`Clear spot: u=${clearSpot.u.toFixed(2)} v=${clearSpot.v.toFixed(2)} coverage=${(clearSpot.coverage * 100).toFixed(0)}%`);
    }

    let structure = null;
    if (transparentBg && paletteSource && palette && palette.confidence === 'high') {
      structure = await attachPeelSource(await extractLogoStructure(paletteSource, palette).catch(() => null), paletteSource);
      if (structure) {
        wdDebug(
          structure.confidence === 'high'
            ? `Structure: ${structure.fill.hex} fill + [${structure.outlines
                .map((o) => `${o.hex}·${o.thickness}`)
                .join(', ')}] → auto-styled`
            : `Structure: low (${structure.reason || '—'})${structure.colorBorneDetail ? ' · colour-borne detail → no flatten offered' : ''} → plain palette styling`,
        );
      }
    }

    // Replacement → dramatic in-place swap (strip → reveal → keep going, no fork
    // replay); first upload → the full reveal choreography incl. who-card + fork.
    // Flag the choreography so the periodic autosave can't capture a TRANSIENT
    // mid-upload design (the who-card wait can run past the 30s interval, and
    // smartStart's pre-name logo-led layout is not what the buyer ends on) — the
    // likely cause of a resumed design showing a different layout (Ryan 2026-07-18,
    // bugs 5/6). The interval checks this flag; the explicit saves (finishBeat,
    // beforeunload) do NOT, so the FINISHED design is always captured.
    /* INGEST SUCCEEDED — promote the parked file to the retained original. Here and
       not at the top of `handleFile`, because everything above this line can bail
       out (an undecodable image, a PDF that would not render) AFTER `beginReplace`
       has already stripped the previous logo. Committing on entry would leave the
       app with no logo and the holder full of bytes for a file it could not read,
       and submit would then ship an "original" with no enhanced art beside it. */
    artworkHold.commit();
    /* ⭐ AND, IN REP MODE, THE FILE GOES SOMEWHERE THE DESIGN CAN POINT AT (2026-08-27). The holder
       above is memory: it is cleared the moment another design is opened, so a rep who reopens last
       week's design has no original and the Vendor Design Sheet had nothing to link to. Fire and
       forget — the upload must never delay the reveal choreography that runs on the next line, and
       a failure costs a link, not a design. Buyers are unaffected; see `retainOriginalForRep`. */
    void retainOriginalForRep(ctx.state);
    try {
      ctx.state.ui.choreographing = true;
      if (opts.replace) await replaceReveal({ logo, palette, structure });
      else await runBeat({ logo, palette, structure });
    } finally {
      ctx.state.ui.choreographing = false;
      endSlowNote(); // WD 491: never left behind by an early return or a throw
      buildFlowActive = false; // safety net: never leave the flow armed after it ends/errors
      forcedDest = null;
      setProcessing(false);
      // The busy pill is normally taken away by the next narrate(), but an early return or a throw
      // between the server call and that line would leave "Enhancing your logo…" pinned to the canvas
      // forever — a permanent lie about work that has stopped. Cleared here so the guarantee is
      // structural rather than dependent on every path remembering to narrate again.
      ctx.status?.clearBusy?.();
      // The choreography is over, so the logo is the toggle's now — see the note at `keyCtx.logo`.
      armKeyHolesToggle();
    }
  }

  /** The dramatic "Extracting… → Found N colors" swatch reveal, one chip at a time.
   *  Clears any prior swatches first so it re-runs cleanly on a re-upload. */
  async function revealColorSequence(palette) {
    await revealHolesBeat(); // WD 482c: the chooser's beat, in its slot above YOUR COLORS
    replaceChildren(colorsRow, []);
    syncEditColorsBtn();
    const colors = buildRevealColors(palette);
    if (!colors.length) { narrate('Your logo'); return; } // couldn't read colors (WD-22)
    // The heading + its empty row is a block arriving, so it eases in like every other one — and
    // the caption change rides the same movement rather than nudging the sheet a second time.
    easeStep(() => { colorsBlock.hidden = false; narrate('Extracting your colors…'); });
    await dwell(REVEAL_EXTRACT_MS);
    for (let i = 0; i < colors.length; i++) {
      const n = i + 1;
      /* ⚠ A CHIP CAN COST A WHOLE ROW. Most land beside their neighbours and change nothing, but the
         one that wraps to a second line grows the sheet by ~40px in a single frame — and it is the
         same beat as all the others, so it reads as the sequence hiccupping rather than as a
         layout. Eased with the caption inside it, so a wrap and its "Found 4 colors" are one move. */
      easeStep(() => {
        // A BUTTON, not a span (Ryan 2026-07-26) — tap it to adjust that colour. Built by the shared
        // `revealChip` so this path and the resume can't drift apart again.
        const chip = revealChip(colors[i]);
        colorsRow.append(chip);
        requestAnimationFrame(() => chip.classList.add('is-in'));
        /* One light thump per colour as it lands (Ryan 2026-08-09). They arrive 720ms apart, which
           is what makes this a sequence you can feel counting rather than a buzz — see the rate
           limit in `ui/haptics.js`, which is there for the day that dwell is shortened. */
        tick();
        narrate(n === 1 ? 'Found 1 color' : `Found ${n} colors`);
        syncEditColorsBtn(); // the affordance arrives with the first chip, not after the last
      });
      if (i < colors.length - 1) await dwell(REVEAL_COLOR_MS);
    }
    await dwell(REVEAL_BLOCK_MS);
    // Logo size slides in UNDER the finished palette, greyed (Ryan 2026-07-25) — the next beat in
    // the same sequence, before the who-card lands below it. Locked because there's no rendered
    // weight yet; `showIdentity` un-greys it once there is.
    //   `!hasBuilt` because this same sequence re-runs on a logo SWAP, and by then the weight is
    // already dressed — re-locking a control the buyer has been using would be a regression, not a
    // beat. Only the first upload has nothing behind it.
    // Lay the default sizes in BEFORE the control is shown, so its first readout already says 75%
    // rather than the engine's raw 1.0 (Ryan 2026-07-25). Silent on purpose — no render, no history
    // — because the plate must not be drawn until "Design my Weights".
    seedDefaultSizes(ctx.state.designProfile, { product: modelFor(ctx.state.ui.product), pct: DEFAULT_SIZE_PCT });
    easeStep(() => {
      showLogoSize({ locked: !hasBuilt });
      // Settle on the LOGO — the "YOUR COLORS" heading already labels the swatches.
      narrate('Your logo');
    });
    /* ⛔ AND THE SIZE DIAL GETS ITS OWN BEAT BEFORE THE WHO-CARD (Ryan 2026-08-08, the cadence half
       of the report). This function used to END here, and `runBeat`'s very next statement mounts
       "Who's this for?" — so the last two blocks of the whole choreography arrived in consecutive
       ticks with no pause between them, after five beats that were all 550ms or longer apart. Two
       overlapping eases on one frame is also the one case the easing cannot smooth: the second
       re-targets the first mid-flight, which is correct and still reads as a stumble.
         `REVEAL_BLOCK_MS` and not a new number — the interval between one block appearing and the
       next is one decision, and it is made in one place. */
    await dwell(REVEAL_BLOCK_MS);
  }

  /**
   * Re-upload reveal (Ryan 2026-07-16). The old logo + colours were already stripped
   * and the who-card grayed (beginReplace), and the cleaning beat has played. Now land
   * the NEW logo, re-pull its colours with the SAME dramatic one-at-a-time reveal as
   * the first upload, then un-gray the card so the buyer continues. Keeps their design
   * / customisations — only the logo image + palette change. If the plate was already
   * built (came back post-onboarding), re-dress it; otherwise the pending build picks
   * up the new logo.
   */
  async function replaceReveal({ logo, palette, structure }) {
    const { state, status, announce, requestRender, refreshControls } = ctx;
    showReveal(logo.dataUrl, logo.transparentBg); // ensure the new thumb is up (covers the SVG path)
    setLogo(state, logo);
    applyPalette(state, palette);
    // B1: re-style the existing text/weight in place — this path keeps the design
    // and never calls smartStart. applyLogoStructure resets to a clean slate first
    // (so a flat/replacement logo drops the previous logo's outline stack + stale
    // 3rd colour), then matches the new logo if it's a confident layered mark.
    applyLogoStructure(state, structure);
    // Same seeding as the first upload: a keyed-out chromatic field is a school colour.
    seedKeyedFieldSwatch(state, logo.keyField);
    /* ⛔ AND THE MULTI-CONFIGURATION DEAL RUNS AGAIN (Ryan 2026-08-23: a red/blue 4× set kept its
       red and blue plates after a yellow/green logo replaced the artwork). The deal only ever ran
       on the FIRST build (`runBeat`); this path kept the design — and with it the old brand's
       `savedBodyColor`, which every config hop restores. `applyLogoStructure` above has just
       cleared that memory for a new artwork, so this reseeds it from the NEW palette — after
       `seedKeyedFieldSwatch`, because the seated field is one of the deal's candidates, the same
       order the first upload uses. `restoreBodyColor` lands the live config's new plate so the
       re-dress below draws it. */
    if (applyDefaultPlateColors(state, { seed: true })) restoreBodyColor(state, state.ui.product);
    /* Same post-dye re-derive as the first build (`runBeat`): the styling above was seeded
       against the default black, and the restore may have just dyed the live plate. Order is
       `masterSwitchCascade`'s; the rescue after it is the WD 334 rule — after every re-derive. */
    if (!isNearBlackHex(state.designProfile.bodyColor)) {
      recascadeColorDefaults(state.designProfile);
      dropAutoOutlinesWhenFillReads(state.designProfile);
      assignOutlineColors(state.designProfile, effectivePlateHex(state));
    }
    correctIllegibleFill(state.designProfile, effectivePlateHex(state));
    // Same first-configuration ring gap as `runBeat` (WD 337) — the swap path re-deals and
    // restores the live config, which likewise never "moves" through the config-switch branch.
    syncLogoContrastRing(state);
    ctx.updateStageTint?.();
    await revealColorSequence(palette);           // dramatic, same as the first upload
    refreshControls();                            // land the new colours in the pickers
    if (hasBuilt) requestRender();                // re-dress the existing plate (crossfade)
    setWhoCardBusy(false);                        // un-gray → the buyer can continue
    status.success('Logo swapped — preview updated.');
    announce('Logo swapped — preview updated.');
    (ctx.recordHistoryNow || ctx.recordHistory)?.();
  }

  /**
   * Curated reveal palette (Ryan's 2026-07-16 spec): primary (most pixels), then
   * secondary (2nd-most, perceptually distinct), then any accent owning ≥3% of the
   * pixels. Near-duplicate strays (anti-alias / JPEG halo pixels that are really
   * part of primary/secondary) are already collapsed by the ΔE merge in
   * palette-extract, so what's left is genuinely distinct. Empty on low confidence.
   */
  //   ⚠ THE RULE MOVED TO `shownLogoTones` (Ryan 2026-07-28). It was only ever used here, so it lived
  // here — until the Customize Design tab's "Original logo colors" swatch had to draw the SAME set and
  // reached for a different function to do it, one that merges at ΔE 24 and needs 6% where this one
  // takes anything over 3%. A three-tone logo could therefore show three chips here and two bands
  // there. Shared now, so the two cannot disagree again; returns hexes, which is all either caller
  // ever wanted.
  function buildRevealColors(palette) {
    const out = shownLogoTones(palette, COLOR_MIN_FRACTION);
    // The keyed-out school colour joins the reveal as the LAST chip — it is part of "your colors"
    // (Ryan 2026-08-18, Hanover's navy) but not part of the drawing, so the drawing's tones land
    // first in extractor order and the field closes the drum-roll.
    const f = seatedFieldHex(ctx.state);
    if (f && !out.some((h) => normalizeHex(h) === f)) out.push(f);
    return out;
  }

  /** Reveal: land logo → drum-roll the colors ONE AT A TIME (they persist) → ask
   *  who it's for BELOW them → first design. Paced for drama (Ryan 2026-07-16). */
  async function runBeat({ logo, palette, structure }) {
    const { state, status, announce, requestRender, refreshControls, goToStep } = ctx;

    if (ctx.exitDemo) ctx.exitDemo(); // clear the demo example before their logo lands
    /* ⛔ `setLogo` MOVED to the build beat below (Ryan 2026-08-07). It looks like a stash and is not:
       with no logo element on the profile yet it CREATES one (`state.js` ~2404, the "no logo slot"
       branch), which put the artwork on the plate the instant the file was read — before the buyer
       had answered "Who's this for?". Ryan: *"the plate should be completely black up until I click
       'Design My Weights' — it doesn't know if I'm going to enter text or not, so it has no way of
       knowing how to design the plate."*
         Nothing between here and there needs it: `showReveal` draws the PANEL thumbnail from the raw
       `logo` object, and `applyPalette` / `applyLogoStructure` take their data as arguments and only
       write colour ROLES. */
    // The front door of the whole flow. On the FIRST upload only — `replaceReveal` handles a swap,
    // and a buyer trying three logos is still one buyer who uploaded a logo.
    wdTrack(WD_EVENTS.LOGO);
    let prefill = applyPalette(state, palette); // false on low-confidence (§11)
    // B1: a confident layered logo OVERRIDES the palette roles (primary=fill,
    // secondary=inner outline, tertiary=outer outline) and stamps the matching
    // outline stack onto the text — smartStart below re-applies it to any freshly
    // built arc-text. Counts as a prefill (we DID set their colours from the logo).
    const struct = applyLogoStructure(state, structure);
    if (struct.applied) prefill = true;
    // A keyed-out CHROMATIC field is a school colour — bench it (Ryan 2026-08-18, Hanover's navy).
    // AFTER applyLogoStructure, so the duplicate test judges against the roles the design will
    // actually wear; the ladders (roles → bench → neutrals) then prefer it over house black
    // everywhere a ring or plate is derived. See seedKeyedFieldSwatch for the gates.
    {
      const seated = seedKeyedFieldSwatch(state, logo.keyField);
      if (seated) wdDebug(`Keyed field ${seated} seated on the bench as a school colour`);
    }
    /* ⭐ THE MULTI-CONFIGURATION PLATE DEFAULTS LAND HERE (Ryan 2026-08-09) — the colours come off
       the logo, and the picks were made on the gate one step earlier, so this is the first moment a
       palette exists to derive them from. It is also the LAST moment that lands before the reveal
       spin, which is the point: the plate is already dyed when the logo turns around, so the mark
       and its colour arrive as one move.
         ⚠ The live plate is re-read because `applyDefaultPlateColors` writes the per-configuration
       MEMORY; the design on screen only moves when that memory is restored onto `bodyColor`. */
    /* ⛔ STORED, AND DELIBERATELY NOT DRAWN (Ryan 2026-08-10, second pass). The plate must still be
       BLACK while the chips land and the buyer answers the who-card, and it must turn colour on the
       exact frame the logo arrives:
         *"I want it to sit tight and wait in black, then let the user enter the organization and/or
         mascot name, then click Design My Weights. Once they click that it starts spinning. Then the
         exact same moment the logo appears on the spinning plate, the colour should also change."*

       ⚠ AN EARLIER FIX PUT A `requestRender()` HERE and it is the thing that broke this. It was
       added to cure the half-dyed spin (rim dyed, canvas still black) by repainting the blank plate
       with its dyed ground before the reveal — which worked, and made the plate change colour the
       instant the upload finished. Ryan is right that it is the worse trade: it spends the reveal's
       biggest beat before the reveal starts.
         THE HALF-DYED SPIN IS ALREADY FIXED WITHOUT IT. `reveal-3d.setBodyColor` HOLDS the rim's dye
       for the whole spin and flushes it inside `applyRevealTexture` (`revealPendingBody`), so the rim
       and the ring-B ground — which rides inside the design texture — land on the SAME frame, at
       edge-on, where the face is a zero-width edge and nothing is visible. That is exactly the beat
       Ryan is asking for, and it was already in place; this line was belt to its braces.

       ⚠ NO LAG, AND THAT IS ARCHITECTURAL RATHER THAN LUCK — his own worry ("if the colour needs to
       load at the same time it does now in the background, that's probably a potential solution").
       The dyed texture is still BUILT and GPU-uploaded during the windup (`playLayoutReveal` calls
       `renderer.initTexture` while the plate is at rest, precisely so the swap is a free pointer
       change), and the rim's dye is one material colour write. Only the REVEAL of both is deferred;
       none of the work is. */
    if (applyDefaultPlateColors(state, { seed: true })) restoreBodyColor(state, state.ui.product);
    ctx.updateStageTint?.(); // wash the studio backdrop toward the extracted secondary

    // Make sure the reveal panel is up (the SVG/already-transparent paths skip the
    // cleanup block above, so they arrive here without it shown yet).
    showReveal(logo.dataUrl, logo.transparentBg);

    /* ⛔ THE BASELINE `smartStart(state, {})` MOVED TOO, for the same reason. It builds a COMPLETE
       design — logo AND weight number — and the old comment here promised "we DON'T render", which
       was true of this line and beside the point: the stage draws from state, so populating the
       profile dressed the plate whether or not anyone asked for a frame. The design is now assembled
       once, at the build beat, from the answer the buyer actually gave. */

    // A logo is on FILE now → UNLOCK Request Quote and arm the
    // build-flow interception (Ryan 2026-07-18, bug 9). From here a click on either tab
    // routes through commitWhoWithDest() instead of hanging on the who-card.
    buildFlowActive = true;
    state.ui.hasLogo = true;
    refreshControls();

    // Colors reveal — "Extracting…" then each chip lands under "Your colors" with a
    // running "Found N color(s)" count. The chips STAY (colorsBlock is never hidden).
    await revealColorSequence(palette);

    /* ⭐ THE SHOW-THROUGH SWITCH GOES LIVE HERE — the first beat at which nothing else is still
       writing the design (see `armKeyHolesToggle`). Everything the flip would collide with has now
       run: `applyPalette` and `applyLogoStructure` are above, the chips have just landed, and the
       only remaining write to the artwork is the `setLogo` at the build beat, which reads the LIVE
       record rather than the one captured before the choreography.
         ⚠ NOT EARLIER, and the boundary is exactly those two calls. Arming at the moment the key
       lands would put the control up during the cleaning beat, where a flip's re-derived palette and
       structure are overwritten by this function a moment later — the window the 2026-08-08 pass
       described. This closes the buyer-facing gap without reopening it. */
    armKeyHolesToggle();

    // STAGE 1: "Who's this for?" — name (optional) + "Design my Weight". A name
    // re-seeds an arched design. This does NOT route yet; it just gates the build.
    // Release `processing` while we wait on the buyer so "Upload different logo" works.
    setProcessing(false);
    const who = await askWhoIsThisFor();

    /* THE DESIGN IS ASSEMBLED HERE, AND ONLY HERE — this is the beat "Design My Weights" buys.
       `setLogo` first (it is what puts the artwork on the plate), then `smartStart`, which is
       logo-led and needs it. An empty answer still gets the baseline logo-led design; a name or
       mascot upgrades it to the arched layout. Either way the plate goes from black to finished in
       the single spin below, instead of being half-dressed while the buyer is still typing. */
    /* ⛔ THE LIVE RECORD, NOT THE CAPTURED ONE. `logo` was built before the choreography started;
       the holes toggle is live during the who-card wait and a flip REPLACES that record (new
       `dataUrl`, new aspect, `keyGlobal`, `keyGlobalUserSet`), pointing `keyCtx.logo` at the new
       one. Handing the stale object to `setLogo` is what used to put the pre-flip artwork back on
       the plate while the checkbox stayed ticked — the race the 2026-08-08 pass closed by hiding the
       control instead. With the read fixed at its one seam, the control can be live.
         `keyCtx.logo` IS `logo` when nothing was flipped (set at the end of the build above), so the
       fallback only covers a logo the keyer never touched, i.e. no `keyCtx` at all.
         Awaited first: a flip's restyle re-derives the palette, and `smartStart` two lines down
       seeds the whole design from it. */
    if (holesFlipPending) await holesFlipPending.catch(() => {});
    if (tapFixPendingApply) await tapFixPendingApply.catch(() => {});
    /* ⛔ AND `pendingLogo` BEFORE THE CAPTURED `logo` (WD 483d, Ryan: Mojoe's edges). "Edit see-through parts" works on
       logos the keyer never touched (no `keyCtx`), and it replaces the record through `pendingLogo`. Falling straight to
       the captured `logo` put Mojoe's dark field back on the plate at "Design My Weights". `pendingLogo` IS `logo` when
       nothing was edited, so this changes nothing else. */
    setLogo(state, (keyCtx && keyCtx.logo) || pendingLogo || logo);
    const named = !!(who && (String(who.name).trim() || String(who.mascot).trim()));
    smartStart(state, named ? who : {});

    /* ⛔ AND THE CONTRAST RE-DERIVES AGAINST THE DYED PLATE, which is a SEPARATE beat from the dye
       and has to be (Ryan 2026-08-09: the Angry Panda mark came out with cream text on a cream
       outline, *"not a good look"*).
         The dye happens up at palette time, because the reveal spin needs it. The DESIGN, though, is
       not built until here — `smartStart` runs when the buyer confirms the who-card, a user action
       later — so at dye time there is nothing yet to re-colour. Cascading up there would cascade
       over an empty profile; cascading only down here would leave the reveal undyed. Both, in their
       own places.
         ⭐ WHY IT MATTERS ON THIS LOGO: outline assignment avoids the plate colour. With the plate
       black, the panda's purple was free and the cream text got a purple ring. With the plate now
       purple, purple is spoken for, and the only other strong tone left is the cream the text is
       already wearing — so the ring silently matched its own fill. `recascadeColorDefaults` is what
       `design-panel` runs on every manual dye for exactly this reason; an automatic dye is the same
       event and owes the same re-derive.
         ⚠ Gated on the plate actually being dyed, so a single black-plate design — the overwhelming
       majority — keeps the behaviour it has always had. */
    /* ⛔ AND IT IS THE WHOLE CASCADE, NOT JUST THE RE-DERIVE (Ryan 2026-08-10, the Wallabies mark).
     * *"start with ONE comp bumper, upload the 2-tone Wallabies logo: on the black plate you get
     * yellow text with a green outline — it's perfect. As soon as you change to the green or yellow
     * plate, the text and logo go to the opposite and they DROP the outline, which is perfect. That
     * same logic needs to apply when 2+ comp bumpers are selected from the onset."*
     *
     * He is describing the MANUAL dye, which runs `design-panel.masterSwitchCascade` — recascade,
     * then `dropAutoOutlinesWhenFillReads`, then `assignOutlineColors`. This path ran only the first
     * of the three, so the automatic dye kept a ring the manual dye would have removed: on the
     * yellow plate the green fill reads perfectly, the ring has no contrast job left, and the only
     * colour still free for it was the HOUSE WHITE — a tone measured as absent from their artwork
     * (`swatches` is empty; the extractor sees two tones, 71% yellow and 29% green). So a two-colour
     * school got a white ring nobody in the palette had asked for.
     *
     * ⭐ NOTHING NEW IS DECIDED HERE. `dropAutoOutlinesWhenFillReads` already encodes Ryan's rule
     * that a one- or two-tone mark should be restrictive about outlines — its own "3 is the line"
     * threshold — and this mark reports `logoToneCount: 2` with `logoStructure: null`, so both of its
     * exemptions correctly decline. The rule was right and simply was not being run.
     *
     * ⚠ ORDER IS `masterSwitchCascade`'s, and it matters: drop the rings the new plate does not need
     * BEFORE colouring them, or the colour pass spends its search on a ring that is about to go. */
    if (!isNearBlackHex(state.designProfile.bodyColor)) {
      recascadeColorDefaults(state.designProfile);
      dropAutoOutlinesWhenFillReads(state.designProfile);
      assignOutlineColors(state.designProfile, effectivePlateHex(state));
    }
    /* ⛔ THE FIRST BUILD IS THE FOURTH HOME OF THE "BLACK CAN'T BITE" PREMISE (Ryan 2026-08-23,
       Angry Panda: *"the text is purple with no outline"* on the default black plate). WD 332's
       rule — rescue AFTER every re-derive — reached both adopt branches; `smartStart` above is
       a re-derive too (it BUILDS the elements and stamps the template's `fillRef='primary'`),
       and on the near-black default nothing after it ever asked whether primary reads. The
       panda's #200B40 sits at ~1.2:1 on black, past every ΔE test — `readsOn` is the honest
       operator and `correctIllegibleFill` already carries it. After the gated cascade so a
       dyed-plate build is judged post-recascade, same as the manual-dye order. A fill that
       reads is a no-op. */
    correctIllegibleFill(state.designProfile, effectivePlateHex(state));
    /* The LOGO's contrast ring for the FIRST configuration (WD 337, benuredhawks' hollow hawk):
       every other route runs `syncLogoContrastRing` — the plate-chip cascade, the config-switch
       `plateMoved` branch — but the config the reveal LANDS ON never "moves", so the one plate a
       buyer is guaranteed to see never got the interior-merge ring. Same call, same order:
       after the fill's final state, which is what the ring must judge against. */
    syncLogoContrastRing(state);

    // BUILD: "Design my Weight" → render the weight with their logo/colors/text and
    // let the design-initiation spin play, so the buyer WATCHES it come to life
    // before being offered anything else (Ryan 2026-07-16). Active work → re-guard.
    setProcessing(true);
    ctx.dismissShowcase?.(); // the design process has begun — fade the intro chip away for good
    narrate('Building your first design…');
    reveal.classList.add('is-building');
    // Re-seed right before the FIRST render. The who-card's name ran through smartStart, which
    // re-seeds the arc text (and can change the layout), so the sizes settled above may describe
    // pieces that no longer exist. Still silent; the requestRender on the next line is the one and
    // only draw, and it now draws the finished sizes — no reveal-then-snap.
    seedDefaultSizes(ctx.state.designProfile, { product: modelFor(ctx.state.ui.product), pct: DEFAULT_SIZE_PCT });
    // …and the LOGO DIAL's seed joins that same beat (Ryan 2026-07-30). It was the last seeder still
    // running AFTER the first draw — `showIdentity` un-greys the control a few lines below, which
    // fires `syncLogoSize({ seed: true })`, which writes 75% of the dial's own edge over the engine's
    // number and re-renders. `force` because the lock exists to stop a seed DRAWING the plate early,
    // and here the draw is the very next statement. See `syncLogoSize`.
    syncLogoSize({ seed: true, force: true });
    requestRender();
    /* ⭐ THE ONE MOMENT WORTH FEELING — the finished plate arriving. `force`, so it cannot be eaten
       by the rate limit if the buyer's press on "Design My Weights" was still within the window.
       Deliberately the ONLY firmer pulse in the app: if the reveal feels like everything else, it
       is not a reveal. */
    thump();
    await dwell(REVEAL_BUILD_MS);
    reveal.classList.remove('is-building');
    hasBuilt = true; // the plate is now dressed — a later re-upload should re-render it
    ctx.state.ui.hasBuilt = true; // …and the size dials may now seed their defaults (see state.js)
    wdTrack(WD_EVENTS.BUILT); // "Design my Weights" — the first dressed plate
    narrate('Your logo'); // settle the caption back to the resting label

    // The build is done, so the tab returns to its resting state — a re-upload is allowed again.
    setProcessing(false);
    // Reveal the persistent Organization / Mascot fields, pre-filled with what they just typed, so
    // the buyer keeps a clear path to editing the words (Ryan 2026-07-23). This ALSO un-greys the
    // logo size dial, which is what the retired fork used to do at this beat — see the STAGE 2 note.
    showIdentity();
    if (refreshControls) refreshControls();
    /* ⛔ NO AUTOMATIC ROUTING (Ryan 2026-08-07: *"keep you on the Add Logo tab until you actually
       click to navigate off of it by clicking Back, Next, a different tab at the top, etc."*). This
       used to jump to Customize Design the moment the plate was dressed, which moved the buyer off
       the tab in the same instant their design appeared — they never got to look at it.
         `forcedDest` is preserved and still honoured: that is set by `commitWhoWithDest` when a TAB
       CLICK commits the card, which IS the buyer navigating, so it stays a destination. Only the
       silent default is gone. The forward button unlocks at the same moment (`stepGate` reads
       `state.ui.hasBuilt`), so the way onward is offered rather than taken. */
    const dest = forcedDest;
    forcedDest = null;
    buildFlowActive = false; // routing now → tab clicks are plain navigation again
    finishBeat({ prefill, dest, status, announce, refreshControls, goToStep });
  }

  function finishBeat({ prefill, dest, status, announce, refreshControls, goToStep }) {
    ctx.state.ui.hasLogo = true;
    showIdentity(); // the persistent Organization / Mascot fields now live on this tab
    if (refreshControls) refreshControls(); // land chips in the color pickers
    /* ⛔ NO SUCCESS PILL HERE — IT NARRATED THE VISIBLE (Ryan 2026-08-08: *"People can clearly see
     * that, don't need it to appear on mobile or desktop for that matter, it's redundant"*). The
     * plate has just been dressed and the extracted swatches have landed one by one in the pane; a
     * toast saying so is a caption on a photograph the buyer is already looking at.
     *
     * ⚠ THE ANNOUNCEMENT STAYS, and it is not the same thing. A screen-reader user gets NONE of what
     * makes the pill redundant — no dressed plate, no chips arriving — so the live region is the
     * only place that moment exists for them. Removing it would be deleting the information rather
     * than the duplication. (`status.success` announced internally via `flash`, so this line is now
     * the only announcement on this path, which is exactly one.)
     *
     * ⚠ THE `else` BRANCH IS UNCHANGED AND WAS NEVER VISIBLE. `status.guide` only writes the live
     * region — see its own doc comment: "the pill element only becomes visible during transient
     * success/error flashes". It also says something the buyer genuinely CANNOT see: that colour
     * extraction failed and they will have to pick colours themselves. That is information, not
     * narration, so it is deliberately left alone. */
    if (prefill) {
      announce('Logo applied and colors extracted — preview updated.');
    } else {
      status.guide("Logo applied. We couldn't confidently read colors from this art — pick them in the next step.");
      announce('Logo applied — preview updated. Choose your colors next.');
    }
    // Snapshot the assembled design → autosaves it (Fable §3.2 resume) and makes
    // the upload a clean undo baseline.
    (ctx.recordHistoryNow || ctx.recordHistory)?.();
    // Route ONLY where the buyer actually asked to go (a tab click during the reveal/card). A null
    // dest means stay exactly here — see the note at the call site.
    if (dest && goToStep) goToStep(dest);
    /* ⚠ ONLY WHEN THEY STAYED. A null `dest` is the case Ryan is describing — the build finished and
       left the buyer on Add Logo looking at a done plate with no signposted next step. When `dest`
       is set they asked for a tab DURING the build and are already moving, so nudging them toward a
       button they just bypassed would be nonsense. Armed after the routing decision, not before. */
    // WD 501: one pulse at a time - while Clean up logo glows, the Next nudge waits for its editor to close
    if (!dest) { if (glowOn()) forwardHintWaiting = true; else ctx.armForwardHint?.(); }
  }

  /**
   * Re-hydrate the pane from RESTORED state on RESUME (Ryan 2026-07-18, bug 7). No
   * upload choreography runs on a resume, so without this the Add Logo tab shows the
   * empty dropzone instead of the design the buyer is resuming — logo + colours gone.
   * Static (no reveal animation): show the reveal panel with the restored logo thumb
   * and the palette swatches. Text fields live in the Customize Design tab, which
   * already reads element.text, so they're covered. No logo in state → normal dropzone.
   */
  function hydrate() {
    const state = ctx.state;
    const p = (state && state.designProfile) || {};
    // The live logo image: a logo element's image, else the remembered savedLogo.
    const logoEl = (p.elements || []).find((e) => e.type === 'logo' && e.logo && e.logo.dataUrl);
    const logo = (logoEl && logoEl.logo) || (p.savedLogo && p.savedLogo.dataUrl ? p.savedLogo : null);
    if (!logo || !logo.dataUrl) { reset(); return; }
    hasBuilt = true; // the plate is already dressed → a re-upload should re-render it
    ctx.state.ui.hasBuilt = true;
    showReveal(logo.dataUrl, logo.transparentBg !== false);
    showIdentity(); // persistent Organization / Mascot fields, pre-filled from the resumed design
    narrate('Your logo');
    // ── THE SAME TONES A FRESH UPLOAD SHOWS (Ryan 2026-07-28) ─────────────────────────────────
    // "on Resume, the Add Logo tab only shows 2 extracted swatches from the McDonald's logo again —
    // red and yellow, no white."
    //   The comment that stood here said the raw extraction palette "isn't persisted", and it has not
    // been true since the autosave envelope started carrying `state.ui.palette` — so this fell back to
    // the design's ROLES, and roles are only primary and secondary. A third tone was therefore
    // guaranteed to disappear across a refresh, and had been all along; it took a logo whose third
    // tone anyone would miss to notice.
    //   That makes THREE places that had their own idea of "the logo's colours" — the reveal, the CD
    // swatch, and this. All three now ask `shownLogoTones`, and this is the last of them.
    //   The roles remain the fallback and still earn it: an autosave written before the palette was
    // persisted, or one from a low-confidence extraction, has nothing else to draw.
    //   Built by `paintLandedColorChips`, which the holes-flip restyle also uses — a fourth idea of
    // "the logo's colours" was the last thing this row needed (2026-08-08).
    paintLandedColorChips();
    // WD 485 (queue 4c): the editor is offered on EVERY logo, keyed or not - a resume has no reveal to arm it, and a
    // never-keyed logo (Mojoe's) never reaches `armHolesChooser`, so a reopened design showed no button
    tapFixLocked = false;
    tapFixBtn.hidden = false;
    armResumedKeyToggle(logo);
  }

  /**
   * Bring the "let weight show through enclosed areas" toggle back after a resume (Ryan 2026-07-28:
   * "I just refreshed the tab and clicked the resume button, and now when I go to tab 2 I can't see
   * the checkbox at all").
   *
   * It used to stay hidden because the control re-keys the ORIGINAL image and that original lives
   * only in memory. Rather than persist it — which would double the autosave and every share link —
   * both variants are derived here from the keyed logo we DO have, using the fact that each is
   * recoverable from the other:
   *   cut  -> filled   `fillEnclosedPockets`: flood the alpha from the border; what it cannot reach
   *                    is enclosed, so re-fill exactly that with the removed colour.
   *   filled -> cut    `bgKey(…, global)`: the background is already transparent, so the only
   *                    remaining pixels that match the key ARE the enclosed pockets.
   * Async because it needs the image decoded; failure is silent and simply leaves the toggle hidden,
   * which is the old behaviour rather than a broken control.
   */
  async function armResumedKeyToggle(logo) {
    keyHolesToggle.hidden = true;
    keyCtx = null;
    // ⚠ NEVER OFFERED ON A RESUME. The switch is reversible only because it holds the buyer's
    // ORIGINAL file, and a restored session has the finished art and nothing else. A one-way
    // "remove the background" with no way back is worse than not offering it at all.
    removeBgToggle.hidden = true;
    bgOffer = null;
    /* WD 505 (Ryan 11-02): an EDITED logo's unedited art, if this browser kept it (tapfix-base-store.js). It becomes
       Reset logo's target, and the two starting points are derived from it - derived from the edited art, "before"
       would equal "after" and a Solid / See-through pick would drop the edits (WD 485b's known gap). */
    let base = null;
    if (logo && logo.tapFixed && logo.dataUrl) {
      base = await recallTapFixBase(logo.dataUrl);
      if (base) tapFixBase = base;
      wdDebug(`Resume: pre-edit logo ${base ? 'found' : 'not kept'} in this browser`);
    }
    if (!logo || typeof logo.keyGlobal !== 'boolean' || !logo.dataUrl) return; // never client-keyed
    let img;
    try { img = await decodeImage(base || logo.dataUrl); } catch { return; }
    const key = Array.isArray(logo.keyColor) ? logo.keyColor : null;
    // Re-derive at the cutoff the original key ran at. A logo saved before `keyWhite` existed has
    // none, and falls back to the legacy constant — which is exactly what produced it.
    const white = typeof logo.keyWhite === 'number' ? logo.keyWhite : WHITE_THRESH;
    // …and the FIELD it was keyed against. A logo saved before this existed has none, and the
    // fallback is true white — the same answer the keyer itself gives when nothing measured it.
    const fld = Array.isArray(logo.keyField) ? logo.keyField : null;
    const asIs = { dataUrl: base || logo.dataUrl, canvas: null };
    let cut; let filled;
    if (logo.keyGlobal) {
      cut = asIs;
      filled = fillEnclosedPockets(img, key);
    } else {
      filled = asIs;
      cut = bgKey(img, key, true, white, fld);
    }
    // No counterpart means this logo has no enclosed pockets at all — the toggle would be inert, so
    // it stays hidden. That is a real state, not a failure.
    if (!cut || !filled) return;
    // `canvas` is only needed for the aspect ratio on flip; fall back to the image's own.
    const aspect = logo.aspectRatio || (img.naturalWidth / img.naturalHeight) || 1;
    const wrap = (v) => (v.canvas ? v : { ...v, canvas: { width: aspect, height: 1 } });
    keyCtx = { source: null, keyColor: key, keyWhite: white, keyField: fld, logo, variants: { cut: wrap(cut), filled: wrap(filled) } };
    setHolesUI(!!logo.keyGlobal);
    // (WD 482: shown through `armHolesChooser`, which builds the two previews first.)
    // A resume lands the whole tab at once, but this arm is ASYNC (it decodes the logo and derives
    // the counterpart variant), so it always arrives a beat after everything else — the one part of
    // a resume that really does appear on its own.
    armHolesChooser({ glow: false }); // WD 489: a reopened design was already looked at - no glow
  }

  /** Reset the step UI (e.g. after "start fresh"). */
  function reset() {
    // "Start fresh" takes a populated tab back to an empty dropzone — the biggest collapse the
    // sheet ever makes, and one movement, not eleven. The whole teardown goes inside one ease.
    easeStep(() => {
      dropzone.hidden = false;
      trustBeat.hidden = false;
      dropzone.classList.remove('is-loading', 'is-dragging', 'is-pulsing');
      reveal.hidden = true;
      reveal.classList.remove('is-building');
      colorsBlock.hidden = true;
      identityBlock.hidden = true; // back to the blank dropzone → no persistent fields
      // Logo size goes with them, re-locked: "start fresh" means the next upload replays the whole
      // choreography from the top, and a control left showing would pre-empt the colour reveal again.
      logoSizeBlock.hidden = true;
      logoNudgeBlock.hidden = true; // and its other half — same reason
      logoSizeLocked = true;
      replaceChildren(colorsRow, []);
      syncEditColorsBtn();
      replaceChildren(whoMount, []);
      revealCaption.textContent = '';
      revealImg.removeAttribute('src');
      revealImg.classList.remove('is-fading');
      revealThumb.classList.remove('is-checker');
      keyHolesToggle.hidden = true;
      removeBgToggle.hidden = true;
    });
    keyCtx = null;
    holesLocked = false; holesPending = null;
    tapFixBtn.hidden = true;
    tapFixBase = null;
    editGlowWanted = false; tapFixDone = false; syncEditGlow();
    bgOffer = null;
    pendingLogo = null;
  }

  // Draw the eye to the dropzone (called when the showcase's "Design your own" is
  // clicked — Ryan 2026-07-16: that's the "here's what you do next" cue). It loops
  // (~3.6s, see .is-pulsing) and keeps going UNTIL the buyer engages the dropzone —
  // a pointer-down or a drag over it stops it. Restart-safe.
  const stopPulse = () => dropzone.classList.remove('is-pulsing');
  dropzone.addEventListener('pointerdown', stopPulse);
  dropzone.addEventListener('dragenter', stopPulse);
  function pulseDropzone() {
    dropzone.classList.remove('is-pulsing');
    void dropzone.offsetWidth; // reflow so the animation replays even if just fired
    dropzone.classList.add('is-pulsing');
  }

  // `refresh` is the name main.js's goToStep already looks for on a step controller, so landing on
  // this tab re-syncs the size readout for free; refreshControls calls it too, for the changes that
  // happen while you're on another tab (undo, Reset all styling, a resume).
  // `refresh` runs on EVERY entry to this tab (main.js goToStep) — and before 2026-07-25 this
  // controller had no refresh at all, so nothing ran on arrival. It must therefore be inert:
  // read the profile, update the readout, and commit NOTHING. Seeding is passed `seed: true` only
  // from the choreography beats that own it (the colour reveal, and the design going live), because
  // seeding commits, and a commit fans out through refreshControls and rebuilds the panes — which
  // is what makes a tab look like it arrives half-dressed and then snaps.
  // `refresh` RE-ADOPTS first: on the way back from Customize Design the three loaned nodes are still
  // parented over there, and `goToStep` calls this on every switch — which is what makes the move safe.
  // ── THE CLEAR-SPOT BACKFILL, hung off `refresh` because that is the one path EVERY design comes
  // through: a resume, a shared link, an undo, a layout change, a product hop. An upload measures
  // its own mark (above); this is for every mark that arrived before the measurement existed.
  //   `pending` is not an optimisation — `refresh` runs on every commit, and writing the spot IS a
  // commit, so without it a successful backfill would re-enter itself for ever.
  let clearSpotPending = false;
  function backfillSpot() {
    if (clearSpotPending) return;
    clearSpotPending = true;
    backfillClearSpot(ctx.state.designProfile)
      .then((changed) => { if (changed) ctx.requestRender?.(); })
      .catch(() => {})
      .finally(() => { clearSpotPending = false; });
  }
  // `refresh(opts)` forwards to `syncLogoSize`, so a caller that is ABOUT TO RENDER can ask for the
  // seed as well — see the note there. A bare `refresh()` still only re-displays, which is what a
  // tab visit needs and what the flicker note demands.
  /* `repaintColors` is for the CD tab's "Reset colors" (Ryan 2026-08-12: everything reset *"EXCEPT
     FOR the swatch back on the Add Logo tab"*). That button commits through `structuralCommit`,
     which re-renders the panel and never calls `refreshControls` — the same reachability gap the
     nudge pad and the size dial each hit (project CLAUDE.md §0b) — so nothing on this tab was on the
     path. Deliberately NOT folded into `refresh()`: that runs on every commit and every step change,
     including mid-choreography, where re-landing the chips would stomp the one-at-a-time reveal. */
  return { el, introSlot, reset, hydrate, inBuildFlow, commitWhoWithDest, pulseDropzone, stopPulse, logoLoan, repaintColors: () => { paintLandedColorChips(); void refreshRevealPreview(); }, refresh: (opts) => { reclaimLoanedNodes(); syncLogoSize(opts); backfillSpot(); } };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Read a file to a display-ready data URL, downscaling oversized rasters first
 * (§11). Returns `{ dataUrl, dims, aspect, paletteSource }` where:
 *  - `dims` are the NATURAL (pre-downscale) pixels so quality/aspect reflect the
 *    source, not the 1024px display copy (WD-20);
 *  - `aspect` is the true width/height (real SVG intrinsic ratio too — WD-21);
 *  - `paletteSource` is an already-decoded <img>/<canvas> the caller hands to
 *    extractPalette, avoiding a second full-res decode (WD-06) and making
 *    viewBox-only SVGs extractable (WD-08).
 * Throws on a decode failure so the caller can message.
 */
/**
 * The inks a VECTOR source DECLARES — parsed from the SVG text so palette extraction can anchor
 * to the artwork's own truth instead of re-discovering it from rasterized pixels (Ryan
 * 2026-08-23, Abbey Athletics: white #FEFEFE and gray #B5B6B3 sit ~ΔE 19 apart, and whether the
 * pixel clusterer fused them flipped with each browser's anti-aliasing — 3 tones on one machine,
 * 4 on another, and the whole band structure diverged downstream). Fill + stroke attributes and
 * inline `fill:` styles; named colours mapped; `none`/`currentColor`/url() ignored. Deduped by
 * exact hex, capped — `extractPalette` still drops any declared ink the raster barely wears.
 */
const SVG_NAMED_INKS = { white: [255, 255, 255], black: [0, 0, 0], red: [255, 0, 0], gray: [128, 128, 128], grey: [128, 128, 128], silver: [192, 192, 192], gold: [255, 215, 0], navy: [0, 0, 128], blue: [0, 0, 255], green: [0, 128, 0], yellow: [255, 255, 0], orange: [255, 165, 0], purple: [128, 0, 128], maroon: [128, 0, 0] };
function svgDeclaredInks(text) {
  if (typeof text !== 'string' || !text) return null;
  const out = [];
  const seen = new Set();
  const push = (rgb) => {
    const k = rgb.join(',');
    if (!seen.has(k)) { seen.add(k); out.push(rgb); }
  };
  const raw = [
    ...[...text.matchAll(/(?:fill|stroke)="([^"]+)"/g)].map((m) => m[1]),
    ...[...text.matchAll(/(?:fill|stroke):\s*([^;"'}]+)/g)].map((m) => m[1]),
  ];
  for (let v of raw) {
    v = String(v).trim().toLowerCase();
    if (!v || v === 'none' || v === 'transparent' || v === 'currentcolor' || v.startsWith('url(')) continue;
    let m = v.match(/^#([0-9a-f]{6})$/);
    if (m) { push([0, 2, 4].map((i) => parseInt(m[1].slice(i, i + 2), 16))); continue; }
    m = v.match(/^#([0-9a-f]{3})$/);
    if (m) { push([...m[1]].map((c) => parseInt(c + c, 16))); continue; }
    if (SVG_NAMED_INKS[v]) push(SVG_NAMED_INKS[v]);
  }
  return out.length ? out.slice(0, 12) : null;
}

async function prepareForDisplay(file) {
  const isVector = file.type === 'image/svg+xml' || /\.svg$/i.test(file.name);
  const rawUrl = await readAsDataURL(file);

  if (isVector) {
    const aspect = await svgIntrinsicAspect(file); // real ratio, not forced 1:1 (WD-21)
    // Rasterize the SVG onto a fixed analysis canvas for palette extraction.
    // Drawing via <img> (not createImageBitmap) works for viewBox-only SVGs and
    // in Firefox, where createImageBitmap rejects SVG blobs (WD-08).
    let paletteSource = null;
    try {
      const img = await decodeImage(rawUrl);
      const boxW = 256;
      const boxH = Math.max(1, Math.round(boxW / (aspect || 1)));
      const canvas = document.createElement('canvas');
      canvas.width = boxW;
      canvas.height = boxH;
      canvas.getContext('2d').drawImage(img, 0, 0, boxW, boxH);
      paletteSource = canvas;
    } catch {
      paletteSource = null; // tainted / undrawable SVG → caller degrades to file/null
    }
    return { dataUrl: rawUrl, dims: { width: 0, height: 0 }, aspect, paletteSource };
  }

  const img = await decodeImage(rawUrl); // throws on HEIC/corrupt → caught upstream
  const { naturalWidth: w, naturalHeight: h } = img;
  const aspect = w > 0 && h > 0 ? w / h : null;
  const longest = Math.max(w, h);
  if (longest <= MAX_DISPLAY_EDGE) {
    return { dataUrl: rawUrl, dims: { width: w, height: h }, aspect, paletteSource: img };
  }

  const scale = MAX_DISPLAY_EDGE / longest;
  const dw = Math.max(1, Math.round(w * scale));
  const dh = Math.max(1, Math.round(h * scale));
  const canvas = document.createElement('canvas');
  canvas.width = dw;
  canvas.height = dh;
  canvas.getContext('2d').drawImage(img, 0, 0, dw, dh);
  const mime = file.type === 'image/jpeg' ? 'image/jpeg' : 'image/png';
  /* dims stay NATURAL (w,h); the display dataUrl is the downscaled copy.
   *
   * ⛔ THE PALETTE SOURCE IS THE DOWNSCALED CANVAS ON A PHONE, NOT THE FULL-RES `img`
   * (Ryan 2026-08-08: uploading a logo on a phone reloads the page — *"right before it extracts the
   * colors I get kicked out"*, 3 for 3). That beat is where this object's `paletteSource` is finally
   * consumed, and until now it was a deliberately retained FULL-RESOLUTION decode: the old comment
   * said "already in memory", which is true and is exactly the problem — it kept it there.
   *
   * ⚠ IT BUYS THE ANALYSIS NOTHING. Every consumer immediately caps it far below this: palette
   * extraction runs at `maxEdge: 128` (palette-extract.js), and logo-structure at 220 and 160. The
   * only consumer that wants real pixels is `trimTransparent`, whose output becomes the PAINTED
   * art — and the painted art is capped at `MAX_DISPLAY_EDGE`, i.e. this very canvas. So analysing
   * above it was resolution that nothing downstream could ever use.
   *
   * ⚠ PHONE ONLY, deliberately. The desktop path is proven and has the headroom; a 4096-edge decode
   * there is not what breaks anything. Narrowing the change keeps the blast radius on the platform
   * that is actually failing.
   *
   * ⚠ The extractor's own nearest-neighbour rule is UNTOUCHED — it still draws with
   * `imageSmoothingEnabled = false` at 128px, which is where the muddy-blend-swatch fix lives. This
   * only changes what it is handed: a ≤2× smoothed pre-reduction, against the ~16× reduction the
   * extractor performs itself. */
  return {
    dataUrl: canvas.toDataURL(mime, 0.92),
    dims: { width: w, height: h },
    aspect,
    paletteSource: isPhone() ? canvas : img,
  };
}

/** Best-effort intrinsic aspect (w/h) of an SVG from width/height or viewBox. */
async function svgIntrinsicAspect(file) {
  try {
    const text = await file.text();
    const tag = (/<svg\b[^>]*>/i.exec(text) || [''])[0];
    const w = parseFloat((/\bwidth="?([\d.]+)/i.exec(tag) || [])[1]);
    const h = parseFloat((/\bheight="?([\d.]+)/i.exec(tag) || [])[1]);
    if (w > 0 && h > 0) return w / h;
    const vb = /\bviewBox="?\s*[-\d.]+[\s,]+[-\d.]+[\s,]+([\d.]+)[\s,]+([\d.]+)/i.exec(tag);
    if (vb) {
      const vw = parseFloat(vb[1]);
      const vh = parseFloat(vb[2]);
      if (vw > 0 && vh > 0) return vw / vh;
    }
  } catch {
    /* unreadable — fall through to square */
  }
  return 1;
}

function readAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error('read failed'));
    reader.readAsDataURL(file);
  });
}

function decodeImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('decode failed'));
    img.src = src;
  });
}

const wait = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * What flat colour did a server cleanup REMOVE? Diffs the pre/post rasters at ≤256px: pixels
 * opaque before and transparent after are "the removed background"; when ≥10% of the frame was
 * removed AND one 16-step colour bin holds ≥50% of it, that bin's mean is a FLAT FIELD — a
 * candidate school colour for `seedKeyedFieldSwatch` (Ryan 2026-08-18, Michigan-Dearborn's navy:
 * *"I know the blue is a part of their colors… similar to how we've done with Hanover"*). A photo
 * background fails the flat test; a trimmed/misaligned result fails the aspect check; both return
 * null and nothing seats — exactly today's behaviour. Never throws.
 */
async function removedFieldColor(beforeUrl, afterUrl) {
  try {
    const [b, a] = await Promise.all([decodeImage(beforeUrl), decodeImage(afterUrl)]);
    const bw = b.naturalWidth || b.width, bh = b.naturalHeight || b.height;
    const aw = a.naturalWidth || a.width, ah = a.naturalHeight || a.height;
    if (!bw || !bh || !aw || !ah) return null;
    if (Math.abs(bw / bh - aw / ah) > 0.02) return null; // result was trimmed — diff would lie
    const w = Math.min(256, bw);
    const h = Math.max(1, Math.round(w * (bh / bw)));
    const cv = document.createElement('canvas');
    cv.width = w; cv.height = h;
    const cx = cv.getContext('2d', { willReadFrequently: true });
    cx.drawImage(b, 0, 0, w, h);
    const bd = cx.getImageData(0, 0, w, h).data;
    cx.clearRect(0, 0, w, h);
    cx.drawImage(a, 0, 0, w, h);
    const ad = cx.getImageData(0, 0, w, h).data;
    const bins = new Map();
    let removed = 0;
    for (let i = 0; i < w * h * 4; i += 4) {
      if (bd[i + 3] >= 128 && ad[i + 3] < 8) {
        removed += 1;
        const k = ((bd[i] >> 4) << 8) | ((bd[i + 1] >> 4) << 4) | (bd[i + 2] >> 4);
        const e = bins.get(k) || { n: 0, r: 0, g: 0, b: 0 };
        e.n += 1; e.r += bd[i]; e.g += bd[i + 1]; e.b += bd[i + 2];
        bins.set(k, e);
      }
    }
    if (removed < w * h * 0.10) return null; // barely removed anything — no field to speak of
    let top = null;
    for (const e of bins.values()) if (!top || e.n > top.n) top = e;
    if (!top || top.n < removed * 0.5) return null; // removal wasn't a flat field (a photo)
    return [Math.round(top.r / top.n), Math.round(top.g / top.n), Math.round(top.b / top.n)];
  } catch {
    return null;
  }
}

// Does the decoded image carry real transparency? A few transparent pixels ⇒ the
// mark is already cut out — so we never pay the server to remove a background
// that isn't there.
function imgHasAlpha(source) {
  try {
    const w = Math.min(64, source.naturalWidth || source.width || 64);
    const h = Math.min(64, source.naturalHeight || source.height || 64);
    const c = document.createElement('canvas');
    c.width = w; c.height = h;
    const cx = c.getContext('2d', { willReadFrequently: true });
    cx.drawImage(source, 0, 0, w, h);
    const px = cx.getImageData(0, 0, w, h).data;
    for (let i = 3; i < px.length; i += 4) if (px[i] < 240) return true;
    return false;
  } catch {
    return false;
  }
}

/** WD 490: ask the worker whether `file` is a photo. -> {verdict, show} or null. Never throws, never waits past 15 s. */
async function startPhotoCheck(file) {
  try {
    if (typeof createImageBitmap !== 'function') return null;
    const bmp = await createImageBitmap(file);
    const s = Math.min(1, 512 / Math.max(bmp.width || 1, bmp.height || 1));
    const c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(bmp.width * s)); c.height = Math.max(1, Math.round(bmp.height * s));
    const g = c.getContext('2d');
    g.fillStyle = '#808080'; // mid grey: a white mark and a black mark both read on it (a cut-out has no background)
    g.fillRect(0, 0, c.width, c.height);
    g.drawImage(bmp, 0, 0, c.width, c.height);
    if (bmp.close) bmp.close();
    const image = c.toDataURL('image/jpeg', 0.85);
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 15000);
    try {
      const r = await fetch(PHOTO_CHECK_ENDPOINT, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ image }), signal: ctrl.signal });
      if (!r.ok) return null;
      const d = await r.json();
      wdDebug(`Photo check: ${d.verdict}${d.show ? ' -> card' : ''} (${d.model || '-'})`);
      return d && typeof d === 'object' ? d : null;
    } finally { clearTimeout(timer); }
  } catch (err) {
    wdDebug(`Photo check skipped (${err && err.message})`);
    return null;
  }
}

// Tier-1 cleanup via the rfq-worker (Replicate rembg + Real-ESRGAN). Never throws.
// Returns a STRUCTURED result so the caller can log WHY a cleanup didn't land
// instead of guessing (C2 — surface skips, don't swallow them):
//   { cleaned, skipped, reason, httpStatus, ms }
// `cleaned` is the data URL on success, else null. `reason` is the server's own
// skip reason ('unconfigured' | 'cap' | 'error' | 'noop'), an 'http_NNN' for a
// non-200, 'rejected' for a 4xx body, or 'network' for a fetch failure.
async function serverCleanup(dataUrl, ops) {
  const t0 = perfNow();
  const done = (o) => ({ cleaned: null, skipped: true, reason: 'skipped', httpStatus: null, ms: Math.round(perfNow() - t0), ...o });
  try {
    const res = await fetch(CLEANUP_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ image: dataUrl, ops }),
    });
    if (!res.ok) return done({ reason: res.status >= 400 && res.status < 500 ? 'rejected' : `http_${res.status}`, httpStatus: res.status });
    const j = await res.json().catch(() => null);
    if (j && j.ok && !j.skipped && typeof j.cleaned === 'string') {
      return { cleaned: j.cleaned, skipped: false, reason: null, httpStatus: res.status, ms: Math.round(perfNow() - t0) };
    }
    return done({ reason: (j && (j.reason || (j.ok === false ? 'rejected' : 'skipped'))) || 'skipped', httpStatus: res.status });
  } catch {
    return done({ reason: 'network' });
  }
}

// perf clock for the cleanup timings; falls back to 0 if unavailable.
function perfNow() {
  try { return performance.now(); } catch { return 0; }
}

// ── client-side white-key cleanup (Fable §3.3, the $0 launch floor) ───────────
// Removes a connected near-white BACKGROUND (a classic white-bg JPG/PNG) by
// flood-filling from the borders, so interior white in the mark is preserved.
// Returns { dataUrl, canvas, removedFrac } or null when there's nothing to key
// (already transparent, or no white border). The paid API tier (Cloudflare
// Images / Replicate) is deliberately NOT wired here — it's blocked on cost sign-off.
const WHITE_THRESH = 238; // channel value at/above which a pixel reads "white" on a TRUE-WHITE field
// ── THE CUTOFF IS RELATIVE TO THE FIELD, NOT ABSOLUTE (Ryan 2026-08-03) ─────────────────────────
// On `Ridge-View-Gray-Wolves-Logo.jpg`: *"why is the cut-out job so bad? never seen this happen on
// any logos for a LONG time."* The background LOOKS white and is not. Measured: the far field is
// **244.8 and never reaches 255**, and the mark sits on a soft drop shadow baked into the file that
// spans 20–30px. So a constant 238 lands INSIDE the background's own distribution — at 8px from the
// ink only 36% of pixels clear it, at 20px only 74%.
//   The result is not a halo the cleanup missed, it is a cut DITHERING through JPEG noise: it takes
// ~30% of the shadow and leaves ~70%, which is exactly the ragged, speckled rim Ryan saw. No
// defringe can rescue it either — instrumented on the real file, 5,016 frontier pixels stopped on
// MAX_FRINGE_DEPTH and the band immediately past them averages 185, under HALO_MIN. Both limits bind.
//   `chooseBackgroundKey` had already measured that field at 243 and thrown the number away. So the
// cutoff is now placed a fixed margin BELOW the measured field, which makes it self-calibrating: a
// true-white 255 field yields exactly 238 and every clean file is byte-for-byte unchanged, while a
// dimmer field gets a cutoff that clears its own noise.
//   ⛔ Do NOT "fix" this by lowering WHITE_THRESH instead. Measured over the corpus, a global drop to
// 222 costs a uniformly light mark ~10% of its artwork, because a light mark connected to the border
// is eaten by the flood itself. The margin is safe precisely BECAUSE it is relative.
const WHITE_FIELD_MARGIN = 17; // 255 − 17 = 238, i.e. today's constant on a true-white field
const WHITE_THRESH_FLOOR = 200; // never key this dark by brightness alone, whatever the field reads
/**
 * The near-white cutoff for a given measured background field.
 * @param {number[]|null|undefined} field `[r,g,b]` from `chooseBackgroundKey`; anything else → the
 *   legacy constant, so a caller with no measurement behaves exactly as before.
 */
function nearWhiteThresh(field) {
  if (!Array.isArray(field) || field.length < 3) return WHITE_THRESH;
  // The MINIMUM channel, not the mean: the cutoff is applied per channel, so a field that is dimmer
  // in one channel (a warm or cool white) has to clear on its dimmest one or it never floods at all.
  const lo = Math.min(field[0], field[1], field[2]);
  return Math.max(WHITE_THRESH_FLOOR, Math.min(WHITE_THRESH, Math.round(lo - WHITE_FIELD_MARGIN)));
}
// Defringe (C1): the strict flood leaves a ring of near-white, low-saturation
// JPEG-compression pixels just UNDER the white threshold (the "splatter" halo).
/**
 * The background NEIGHBOURHOOD on the white path — Euclidean RGB radius around the measured field,
 * replacing the old per-channel `whiteThresh` corner test. A strict superset of it: passing every
 * channel at `field − WHITE_FIELD_MARGIN` puts a pixel at most √3·17 ≈ 29 units out, so anything
 * the old rule removed this removes too. The extra reach is what gets into a tight crevice, whose
 * neck is sealed by pixels a level or two short in one channel.
 *   `whiteThresh` is still computed and still passed — it is what makes this SELF-CALIBRATING on a
 * dim field, and the reveal toggle and the debug line both report it.
 */
const WHITE_BG_RADIUS = 45;
/**
 * …and where that path's anti-aliased ramp is taken to end, i.e. where the mark's own ink starts.
 * The colour path has always used `COLOR_KEY_HALO` (90) for the same purpose and measures clean;
 * white sits further from most ink than a mid-tone field does, so the ramp it has to cross is
 * longer. Only ever applied within `MATTE_DEPTH` pixels of the cut.
 */
const WHITE_RAMP_END = 235;
/**
 * How much darker than the background edge an edge's own ink must be before a pixel between them is
 * treated as a BLEND of the two. Below it, "half-covered dark ink" and "fully-covered pale ink" are
 * the same measurement and the matte guesses — which cost Cherry Valley's dusty-pink leaves 21.5% of
 * the artwork before this existed. A low-contrast mark keeps its (low-contrast) halo instead.
 */
const MIN_RAMP_CONTRAST = 55;
const EDGE_SMOOTH = 0.5; // 0–1 anti-jag feather strength on the cut silhouette (0 = off)
// Any-solid-colour background key (C1): when the border is a uniform NON-white
// colour (e.g. Elite Edge's charcoal), flood it out by RGB distance the same way.
const COLOR_KEY_STRICT = 42; // Euclidean RGB radius for the solid-colour flood core
const COLOR_KEY_HALO = 90; // outer radius for the colour-key defringe band
/** A data URL back into a File, so a derived image can re-enter `handleFile` as an ordinary upload
 *  rather than needing a second, parallel ingest path. Name is carried over so the quote still shows
 *  the buyer's own filename. */
async function fileFromDataUrl(dataUrl, name) {
  const blob = await (await fetch(dataUrl)).blob();
  return new File([blob], name || 'logo.png', { type: blob.type || 'image/png' });
}

// Auto-dispatch: decide HOW to key the background, then hand off to bgKey.
//  - a large edge-connected NEAR-WHITE field → white key (keeps interior fills)
//  - a large edge-connected SOLID COLOUR field → colour key (cuts holes through)
//  - nothing dominant / a photograph  → null (escalate to the server)
//
// ⚠ THE DECISION MOVED OUT to `src/bg-detect.js` (Ryan 2026-07-29) and CHANGED SHAPE while it went.
// It used to sample the 1-pixel border ring and test it for near-whiteness + uniformity, which is a
// proxy that assumes every upload has a margin — so a logo trimmed flush to its canvas had its own
// letters counted as "the border" and was rejected outright (the RIGAR bug; 6 of Ryan's 40 real logos).
// It now asks the question directly: is there a large flat region of one colour connected to the frame
// edge? Read the long note at the top of bg-detect.js before touching any of it.
//   This function keeps its whole contract — same return shape, same `keyColor`/`keyGlobal` tags, same
// two bgKey modes — so every caller and the reveal toggle are untouched.
export function autoKey(img) {
  try {
    const iw = img.naturalWidth || img.width;
    const ih = img.naturalHeight || img.height;
    if (!iw || !ih) return null;
    const s = Math.min(1, 256 / Math.max(iw, ih));
    const sw = Math.max(1, Math.round(iw * s));
    const sh = Math.max(1, Math.round(ih * s));
    const c = document.createElement('canvas');
    c.width = sw; c.height = sh;
    const cx = c.getContext('2d', { willReadFrequently: true });
    cx.drawImage(img, 0, 0, sw, sh);
    const d = cx.getImageData(0, 0, sw, sh).data;
    const pick = chooseBackgroundKey(d, sw, sh);
    if (!pick) { wdDebug('No dominant edge-connected field → no client key (escalate)'); return null; }
    // The near-white cutoff is derived from the field this very call measured; the colour path keys
    // by RGB distance and never consults it.
    const whiteThresh = pick.color ? WHITE_THRESH : nearWhiteThresh(pick.field);
    wdDebug(`Background: ${pick.why} → ${pick.color ? 'colour key' : `white key @${whiteThresh}`} `
      + `(${pick.global ? 'fill holes' : 'keep holes'})`);
    const r = bgKey(img, pick.color, pick.global, whiteThresh, pick.field);
    return r ? Object.assign(r, { keyColor: pick.color, keyGlobal: pick.global, keyWhite: whiteThresh, keyField: pick.field }) : null;
  } catch {
    return null;
  }
}

/**
 * Rebuild the HOLES-FILLED variant from a holes-CUT image — the inverse of `bgKey(…, true)`, for a
 * resumed session where the pre-key original no longer exists (Ryan 2026-07-28).
 *
 * The trick is that the cut image still carries the answer. Every transparent pixel is either the
 * background that was flooded away from the border, or an enclosed pocket that `global` cut out.
 * Those two are separable WITHOUT the original: flood the ALPHA channel inward from the border and
 * whatever transparent pixel the flood cannot reach is, by definition, enclosed. Re-fill exactly
 * those with the colour that was removed and you have the flood-only result back.
 *
 * `key` null means the near-white path, so white is what was taken out and white is what goes back.
 * Returns { dataUrl, canvas } or null if nothing was enclosed (i.e. the two variants are identical,
 * so there is nothing for the toggle to switch between).
 */
function fillEnclosedPockets(img, key) {
  try {
    const w = img.naturalWidth || img.width;
    const h = img.naturalHeight || img.height;
    if (!w || !h) return null;
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const g = canvas.getContext('2d', { willReadFrequently: true });
    g.drawImage(img, 0, 0);
    const id = g.getImageData(0, 0, w, h);
    const d = id.data;
    const ALPHA_CUT = 8; // "transparent" — matches bgKey's own notion of removed
    // Flood the transparent region inward from the border. Iterative stack, not recursion: a 1024px
    // logo is up to ~1M pixels and recursion would blow the stack on a large background.
    const seen = new Uint8Array(w * h);
    const stack = [];
    const push = (x, y) => {
      if (x < 0 || y < 0 || x >= w || y >= h) return;
      const i = y * w + x;
      if (seen[i] || d[i * 4 + 3] > ALPHA_CUT) return;
      seen[i] = 1; stack.push(i);
    };
    for (let x = 0; x < w; x++) { push(x, 0); push(x, h - 1); }
    for (let y = 0; y < h; y++) { push(0, y); push(w - 1, y); }
    while (stack.length) {
      const i = stack.pop();
      const x = i % w, y = (i / w) | 0;
      push(x + 1, y); push(x - 1, y); push(x, y + 1); push(x, y - 1);
    }
    const [r, gg, b] = Array.isArray(key) ? key : [255, 255, 255];
    let filled = 0;
    for (let i = 0; i < w * h; i++) {
      if (seen[i] || d[i * 4 + 3] > ALPHA_CUT) continue; // border-connected, or opaque already
      const o = i * 4;
      d[o] = r; d[o + 1] = gg; d[o + 2] = b; d[o + 3] = 255;
      filled++;
    }
    if (!filled) return null; // nothing enclosed → the toggle would do nothing
    g.putImageData(id, 0, 0);
    return { dataUrl: canvas.toDataURL('image/png'), canvas };
  } catch {
    return null;
  }
}

// ── WHERE THE MARK ACTUALLY IS ────────────────────────────────────────────────
// The bounds both auto-trims crop to. Used to be a plain alpha bounding box in each of them
// (duplicated, and they could drift); it is one function now, and it is no longer an EXTREMES
// box, which is the bug Ryan found on 2026-07-29.
//
// A bounding box is the single most outlier-fragile statistic there is: ONE surviving pixel in a
// corner holds the entire box open. Key a JPEG whose background is a solid colour and you get
// exactly that — the flood clears 71% of the frame to alpha 0, but JPEG ringing leaves a sparse
// dusting of specks scattered right out to the edges. On the Wexmoor fire-and-rescue badge (a
// circular mark, true aspect ~0.95, on a black 768×1152 field) those specks were **0.05% of the
// ink** and they kept the box at the full 665×1024 — so the engine reserved a tall box for a round
// logo, painted the circle at ~62% of its height, and Ryan's "100%" was a third of the room it
// looked like it should have. Raising the alpha threshold does NOT fix it: the box only collapses
// above alpha 200, which would start eating real anti-aliased edges.
//
// So the bound is a SPEND, not a shape assumption: walk in from each edge while the ink given up
// stays under `INK_EDGE_BUDGET` of the mark's total ink mass. Sparse residue costs ~nothing to
// discard and goes; anything that is really part of the artwork — a hairline rule under a
// wordmark, a script descender, a trailing ™ — costs more than the budget and stops the walk.
// 0.02% per edge was measured on the Wexmoor badge: it lands on aspect 0.953 against a true
// ~0.95, versus 0.667 today, while retaining 99.95% of the ink. (A 6×6px dot at the far edge of a
// 640px mark is ~0.035% — above the budget, so it survives. That is the case the budget is set
// for; a fraction-of-the-busiest-row rule would have thrown it away.)
//   Weighted by ALPHA rather than counting pixels, so a faint halo row cannot outvote a solid one.
const INK_EDGE_BUDGET = 0.0002;
const INK_ALPHA_FLOOR = 8; // alpha at/below which a pixel is not ink at all
/**
 * @param {Uint8ClampedArray} data RGBA pixels
 * @param {number} w
 * @param {number} h
 * @returns {{minX:number,minY:number,maxX:number,maxY:number,mass:number}|null} null when there is
 *   no ink at all. Bounds are inclusive.
 */
function inkBounds(data, w, h) {
  const colInk = new Float64Array(w);
  const rowInk = new Float64Array(h);
  let mass = 0;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const a = data[(y * w + x) * 4 + 3];
      if (a > INK_ALPHA_FLOOR) { colInk[x] += a; rowInk[y] += a; mass += a; }
    }
  }
  if (mass <= 0) return null;
  const budget = mass * INK_EDGE_BUDGET;
  let minX = 0, maxX = w - 1, minY = 0, maxY = h - 1;
  let acc = 0; while (minX < maxX && acc + colInk[minX] <= budget) { acc += colInk[minX]; minX++; }
  acc = 0;     while (maxX > minX && acc + colInk[maxX] <= budget) { acc += colInk[maxX]; maxX--; }
  acc = 0;     while (minY < maxY && acc + rowInk[minY] <= budget) { acc += rowInk[minY]; minY++; }
  acc = 0;     while (maxY > minY && acc + rowInk[maxY] <= budget) { acc += rowInk[maxY]; maxY--; }
  return { minX, minY, maxX, maxY, mass };
}

// The background keyer. `key` = null → near-white mode (brightness threshold);
// [r,g,b] → solid-colour mode (RGB-distance). `global` chooses WHAT to remove:
//   false → border-connected flood only (keeps enclosed pockets / interior fills)
//   true  → every matching pixel, incl. enclosed pockets (holes cut to the plate)
// autoKey picks the default (white→false, colour→true); the reveal toggle flips it.
// `whiteThresh` is the near-white cutoff — see nearWhiteThresh(). It defaults to the legacy constant
// so any caller that does not know the measured field keeps the old behaviour exactly.
// Returns { dataUrl, canvas, removedFrac } or null when there's nothing useful to key.
export function bgKey(img, key, global, whiteThresh = WHITE_THRESH, field = null) {
  try {
    const iw = img.naturalWidth || img.width;
    const ih = img.naturalHeight || img.height;
    if (!iw || !ih) return null;
    const scale = Math.min(1, 1024 / Math.max(iw, ih));
    const cw = Math.max(1, Math.round(iw * scale));
    const ch = Math.max(1, Math.round(ih * scale));
    const canvas = document.createElement('canvas');
    canvas.width = cw;
    canvas.height = ch;
    const cx = canvas.getContext('2d', { willReadFrequently: true });
    // Nearest-neighbour downscale (smoothing OFF): a smoothed shrink blends internal
    // colour boundaries (orange↔white → opaque tan) into the cutout, which then show
    // up as muddy phantom swatches in the palette read from this canvas. Keep pixels
    // real; the outer silhouette is de-jagged separately by the premult feather below.
    cx.imageSmoothingEnabled = false;
    cx.drawImage(img, 0, 0, cw, ch);
    const imgData = cx.getImageData(0, 0, cw, ch);
    const px = imgData.data;
    const preKey = (global && key) ? px.slice() : null; // WD 474: `restoreInkPockets` puts holes back from this
    // Background test (strict flood) + halo test (defringe band), per mode.
    const st2 = COLOR_KEY_STRICT * COLOR_KEY_STRICT;
    const hl2 = COLOR_KEY_HALO * COLOR_KEY_HALO;
    const dist2 = (i) => {
      const dr = px[i] - key[0], dg = px[i + 1] - key[1], db = px[i + 2] - key[2];
      return dr * dr + dg * dg + db * db;
    };
    /* ── THE WHITE PATH KEYS BY DISTANCE TOO (Ryan 2026-08-04) ────────────────────────────────
     * It used to ask "is every channel at least `whiteThresh`?", which is a CORNER of the colour
     * cube, not a neighbourhood of the background — so a pixel one level short in one channel was
     * a wall. Measured over Ryan's 18 test logos, that is the whole difference between the two
     * paths: the COLOUR path (a 42-unit sphere around the field) leaves 0–14 halo pixels per logo,
     * while every WHITE-path logo came back 99–100% haloed, and Ridge View kept white wedges in
     * its fur notches where the sub-threshold pixels sealed the flood out.
     *   `WHITE_BG_RADIUS` is a strict SUPERSET of the old test — a pixel passing every channel at
     * `field − 17` is at most √3·17 ≈ 29 units from the field — so nothing that used to be removed
     * survives this, and the extra 16 units are what reaches into a notch. Still measured against
     * the field the image actually has, so it stays self-calibrating on a dim or warm white. */
    const F = key || (Array.isArray(field) && field.length >= 3 ? field : [255, 255, 255]);
    const fdist2 = (i) => {
      const dr = px[i] - F[0], dg = px[i + 1] - F[1], db = px[i + 2] - F[2];
      return dr * dr + dg * dg + db * db;
    };
    const bgR = key ? COLOR_KEY_STRICT : WHITE_BG_RADIUS;
    const bg2 = bgR * bgR;
    const isBg = (i) => px[i + 3] > 8 && fdist2(i) <= bg2;
    let removed = 0;
    if (global) {
      // Fill mode: remove the background colour EVERYWHERE — including enclosed
      // pockets (letter counters like the "D" hole, the ring interior) — so a
      // differently-coloured plate shows through the holes.
      for (let p = 0; p < cw * ch; p++) if (isBg(p * 4)) { px[p * 4 + 3] = 0; removed += 1; }
    } else {
      // Keep mode: border-connected flood ONLY, so intentional interior fills
      // (West Elk's white separator lines, letter counters) are preserved.
      const visited = new Uint8Array(cw * ch);
      const stack = [];
      const seed = (x, y) => {
        if (x < 0 || y < 0 || x >= cw || y >= ch) return;
        const p = y * cw + x;
        if (visited[p]) return;
        visited[p] = 1;
        if (isBg(p * 4)) stack.push(p);
      };
      for (let x = 0; x < cw; x++) { seed(x, 0); seed(x, ch - 1); }
      for (let y = 0; y < ch; y++) { seed(0, y); seed(cw - 1, y); }
      while (stack.length) {
        const p = stack.pop();
        px[p * 4 + 3] = 0; // knock out alpha
        removed += 1;
        const x = p % cw;
        const y = (p / cw) | 0;
        seed(x - 1, y); seed(x + 1, y); seed(x, y - 1); seed(x, y + 1);
      }
    }

    // ── DEFRINGE ────────────────────────────────────────────────────────────────────────────────
    //
    // ⚠ TWO PATHS, AND THE COLOUR ONE IS DELIBERATELY UNTOUCHED. Measured over Ryan's 18 test
    // logos (tools/probe-logo-key.mjs), the colour path already leaves 0–14 halo pixels per logo
    // against the white path's 2,700–10,500. It has no problem to fix, and every version of the
    // matte below made it slightly WORSE (Wexmoor 2,509 → 3,450). So it keeps the pass it has
    // always had, and the new one is scoped to the path that was actually failing.
    if (removed > 0 && key) {
      // Extend the removal from the transparent frontier THROUGH the transitional halo
      // (8-connected so diagonal speckles go too), stopping at the logo's real ink. `isHalo`
      // matches the ring just OUTSIDE the strict radius — anything at the exact key colour that
      // survived the strict flood is enclosed and intended — and a depth cap bounds how far it may
      // be chased in, so it only erodes a thin rim, never floods a solid region.
      const MAX_FRINGE_DEPTH = 2;
      const isHalo = (i) => {
        if (px[i + 3] === 0) return false;
        const d = dist2(i);
        return d > st2 && d <= hl2;
      };
      /* ⛔ A BLEND BELONGS TO WHICHEVER SIDE IT IS CLOSER TO (Ryan 2026-08-18, the Hanover white
       * keyline — his 2d finding, "don't take thin ink that is attached to the mark", at the pixel
       * it happens). A THIN keyline embedded IN the keyed field is blend pixels on BOTH sides, so
       * this pass ate it from both directions and left a dotted trace of its 1px core — "the
       * exterior logo outline looks super broken and bad". The halo test alone cannot tell that
       * blend from the JPEG fringe it exists to kill: both sit 42–90 units from the key. What
       * tells them apart is the OTHER side — a keyline blend sits beside surviving real ink and is
       * nearer that ink's colour than the key's; free-floating fringe has no such neighbour, and a
       * key-coloured rim clinging to the mark is nearer the KEY, so both still die. Measured on
       * Hanover: the keyline comes back solid; the moat, counters and open-field cleanup are
       * untouched. */
      const nearerToInkThanKey = (p, dKey) => {
        const x0 = p % cw, y0 = (p / cw) | 0;
        for (let dy = -1; dy <= 1; dy++) {
          for (let dx = -1; dx <= 1; dx++) {
            if (!dx && !dy) continue;
            const nx = x0 + dx, ny = y0 + dy;
            if (nx < 0 || ny < 0 || nx >= cw || ny >= ch) continue;
            const j = (ny * cw + nx) * 4;
            if (px[j + 3] === 0) continue;
            if (dist2(j) <= hl2) continue; // key core or halo — not real ink
            const dr = px[p * 4] - px[j], dg = px[p * 4 + 1] - px[j + 1], db = px[p * 4 + 2] - px[j + 2];
            if (dr * dr + dg * dg + db * db < dKey) return true;
          }
        }
        return false;
      };
      const depth = new Uint8Array(cw * ch);
      const q = [];
      const eat = (x, y, d) => {
        if (x < 0 || y < 0 || x >= cw || y >= ch) return;
        const p = y * cw + x;
        if (px[p * 4 + 3] === 0 || depth[p] !== 0 || !isHalo(p * 4)) return;
        if (nearerToInkThanKey(p, dist2(p * 4))) { depth[p] = d; return; } // the ink's own edge — keep
        depth[p] = d;
        px[p * 4 + 3] = 0;
        removed += 1;
        if (d < MAX_FRINGE_DEPTH) q.push(p);
      };
      const around = (x, y, d) => {
        eat(x - 1, y, d); eat(x + 1, y, d); eat(x, y - 1, d); eat(x, y + 1, d);
        eat(x - 1, y - 1, d); eat(x + 1, y - 1, d); eat(x - 1, y + 1, d); eat(x + 1, y + 1, d);
      };
      for (let p = 0; p < cw * ch; p++) if (px[p * 4 + 3] === 0 && depth[p] === 0) around(p % cw, (p / cw) | 0, 1);
      for (let qi = 0; qi < q.length; qi++) { const p = q[qi]; around(p % cw, (p / cw) | 0, depth[p] + 1); }
    } else if (removed > 0) {
      // ── THE WHITE PATH: MATTE THE ANTI-ALIASED BAND OUT, DON'T ERODE IT ─────────────────────
      //
      // What the flood leaves is the anti-aliased ramp: pixels that are a genuine BLEND of
      // background and ink. The old pass knocked whichever of them looked background-ish to alpha
      // 0 and left the rest fully opaque, which is both halves of what Ryan saw — *"a very slight
      // white thin pixelated outline"* is the part it left, and the pixelation is the part it cut.
      //
      // A blend is not a pixel to keep or drop, it is a pixel with a COVERAGE. So each one gets the
      // alpha its position along the ramp implies, and its colour is un-blended from the background
      // it was mixed with:  P = α·C + (1−α)·F  ⇒  C = (P − (1−α)·F) / α.
      //
      // ⛔ BOUNDED THREE WAYS, and every one of them was earned on this corpus:
      //   · DEPTH. Alpha-from-colour cannot tell a half-covered black pixel from a fully-covered
      //     grey one, so it may only run on the thin band adjacent to the cut, where a blend is
      //     what a pixel almost certainly IS. Three pixels is the width of real anti-aliasing at
      //     this sampling size.
      //   · CONTRAST. If the ink this pixel would be a blend OF is barely darker than the
      //     background, the blend hypothesis is worthless — and acting on it anyway cost Cherry
      //     Valley 21.5% of its artwork in one pass: its dusty-pink leaves sit ~70 units from a
      //     cream field, were read as half-covered black, and went translucent. Below
      //     `MIN_RAMP_CONTRAST` the pixel is left alone. The halo it keeps is, by construction, a
      //     low-contrast one.
      //   · THE LOCAL ENDPOINT. The ramp ends at THIS edge's ink, not at a constant: Ridge View's
      //     navy outline is ~230 units from its white field while a mid-grey mark is ~60, and
      //     normalising both against one number left the outer half of a navy edge at full opacity
      //     wearing a light-grey blend colour — the fringe, still there after the flood widened.
      const MATTE_DEPTH = 1;
      const end2 = WHITE_RAMP_END * WHITE_RAMP_END;
      const depth = new Uint8Array(cw * ch);
      const q = [];
      /** The ink this blend is a blend OF: the furthest-from-background pixel in a small window.
       *  Inside an edge band that is the far endpoint, so coverage is just the pixel's position
       *  between the two — the standard two-endpoint estimate, and it needs no gradient direction. */
      const localInk = (x, y) => {
        let mx = 0;
        for (let dy = -2; dy <= 2; dy++) {
          for (let dx = -2; dx <= 2; dx++) {
            const nx = x + dx, ny = y + dy;
            if (nx < 0 || ny < 0 || nx >= cw || ny >= ch) continue;
            const j = (ny * cw + nx) * 4;
            if (px[j + 3] === 0) continue;
            const v = fdist2(j);
            if (v > mx) mx = v;
          }
        }
        return Math.sqrt(mx);
      };
      const eat = (x, y, d) => {
        if (x < 0 || y < 0 || x >= cw || y >= ch) return;
        const p = y * cw + x;
        const i = p * 4;
        if (px[i + 3] === 0 || depth[p] !== 0) return;
        const d2 = fdist2(i);
        if (d2 >= end2) return; // far from the background on its own — real ink, not a blend
        depth[p] = d;
        const ink = localInk(x, y);
        if (ink - bgR < MIN_RAMP_CONTRAST) { if (d < MATTE_DEPTH) q.push(p); return; }
        const a = Math.max(0, Math.min(1, (Math.sqrt(d2) - bgR) / (ink - bgR)));
        if (a <= 0.004) {
          px[i + 3] = 0;
          removed += 1;
        } else {
          for (let c = 0; c < 3; c++) {
            const un = (px[i + c] - (1 - a) * F[c]) / a;
            px[i + c] = un < 0 ? 0 : un > 255 ? 255 : un;
          }
          const a0 = px[i + 3];
          px[i + 3] = a0 * a;
          removed += (a0 - px[i + 3]) / 255;
        }
        if (d < MATTE_DEPTH) q.push(p);
      };
      const around = (x, y, d) => {
        eat(x - 1, y, d); eat(x + 1, y, d); eat(x, y - 1, d); eat(x, y + 1, d);
        eat(x - 1, y - 1, d); eat(x + 1, y - 1, d); eat(x - 1, y + 1, d); eat(x + 1, y + 1, d);
      };
      for (let p = 0; p < cw * ch; p++) if (px[p * 4 + 3] === 0 && depth[p] === 0) around(p % cw, (p / cw) | 0, 1);
      for (let qi = 0; qi < q.length; qi++) { const p = q[qi]; around(p % cw, (p / cw) | 0, depth[p] + 1); }
    }

    /* WD 474 - A GLOBAL CUT MUST NOT PUNCH PINHOLES THROUGH INK THAT IS MERELY NEAR THE KEY (Carroll's
       near-black shield on its black field: ~90 holes, cream flecks on the cream plate). See the function. */
    if (preKey) removed -= restoreInkPockets(px, preKey, cw, ch, key, COLOR_KEY_HALO);
    const frac = removed / (cw * ch);
    // Nothing keyed (transparent already / no white border) or the whole frame is
    // white (a blank/near-blank image) → not a useful cleanup.
    if (removed === 0 || frac > 0.97) return null;

    // Edge smoothing — de-jag the cut silhouette so it doesn't look pixelated when
    // the plate texture magnifies it. A PREMULTIPLIED 3×3 feather (transparent
    // pixels contribute nothing, so the softened rim takes the LOGO's colour, never
    // a white halo), applied ONLY on the alpha boundary band, so interior colour
    // edges (letters, faces) stay perfectly crisp. Blended at EDGE_SMOOTH.
    if (EDGE_SMOOTH > 0) {
      const src = px.slice();
      const A = (x, y) => src[(y * cw + x) * 4 + 3];
      for (let y = 0; y < ch; y++) {
        for (let x = 0; x < cw; x++) {
          // Boundary test: only feather where transparent and opaque meet in the 3×3.
          let hasT = false, hasO = false;
          for (let dy = -1; dy <= 1; dy++) {
            for (let dx = -1; dx <= 1; dx++) {
              const nx = x + dx, ny = y + dy;
              if (nx < 0 || ny < 0 || nx >= cw || ny >= ch) continue;
              if (A(nx, ny) === 0) hasT = true; else hasO = true;
            }
          }
          if (!hasT || !hasO) continue; // solid interior / open field — leave crisp
          let pr = 0, pg = 0, pb = 0, pa = 0, n = 0;
          for (let dy = -1; dy <= 1; dy++) {
            for (let dx = -1; dx <= 1; dx++) {
              const nx = x + dx, ny = y + dy;
              if (nx < 0 || ny < 0 || nx >= cw || ny >= ch) continue;
              const j = (ny * cw + nx) * 4;
              const a = src[j + 3] / 255;
              pr += src[j] * a; pg += src[j + 1] * a; pb += src[j + 2] * a; pa += src[j + 3];
              n += 1;
            }
          }
          pr /= n; pg /= n; pb /= n; pa /= n;
          const a2 = pa / 255;
          const i = (y * cw + x) * 4;
          const br = a2 > 0.004 ? pr / a2 : src[i];
          const bg = a2 > 0.004 ? pg / a2 : src[i + 1];
          const bb = a2 > 0.004 ? pb / a2 : src[i + 2];
          const s = EDGE_SMOOTH;
          px[i] = src[i] * (1 - s) + br * s;
          px[i + 1] = src[i + 1] * (1 - s) + bg * s;
          px[i + 2] = src[i + 2] * (1 - s) + bb * s;
          px[i + 3] = src[i + 3] * (1 - s) + pa * s;
        }
      }
    }

    cx.putImageData(imgData, 0, 0);

    // Auto-trim transparent margins so the mark FILLS its slot. Placement sizes the
    // logo by its image box, so keyed-out padding (e.g. Elite Edge's wide side
    // margins) would otherwise render the art smaller than the space allows.
    //   `inkBounds` — NOT a plain alpha bbox. This is the trim that a speck of JPEG residue used to
    // defeat outright; see its comment for why the bound is an ink budget.
    const b = inkBounds(px, cw, ch);
    if (!b) return null; // nothing opaque left
    const { minX, minY, maxX, maxY } = b;
    const bw = maxX - minX + 1, bh = maxY - minY + 1;
    if (bw < cw || bh < ch) {
      const cropped = document.createElement('canvas');
      cropped.width = bw;
      cropped.height = bh;
      cropped.getContext('2d').drawImage(canvas, minX, minY, bw, bh, 0, 0, bw, bh);
      wdDebug(`Trimmed margins ${cw}×${ch} → ${bw}×${bh}`);
      // `crop` (WD 483): where this output sits in the source drawn at cw x ch - the tap-fix needs it to put
      // the un-keyed pixels back in exactly the right place.
      return { dataUrl: cropped.toDataURL('image/png'), canvas: cropped, removedFrac: frac, crop: { x: minX, y: minY, w: bw, h: bh, sw: cw, sh: ch } };
    }
    return { dataUrl: canvas.toDataURL('image/png'), canvas, removedFrac: frac, crop: { x: 0, y: 0, w: cw, h: ch, sw: cw, sh: ch } };
  } catch {
    return null; // tainted canvas / readback blocked → skip cleanup silently
  }
}

// Crop transparent margins off a cut-out logo so the mark fills its slot. Mirrors
// bgKey's auto-trim, but stands alone for the ALREADY-transparent path (alpha PNGs /
// keyed canvases) that never runs the keyer. Accepts an <img> or <canvas>. Returns
// { dataUrl, canvas, width, height } of the tight crop, or null when there's no
// meaningful margin (already tight, or fully transparent) so the caller keeps as-is.
function trimTransparent(source) {
  try {
    const iw = source.naturalWidth || source.width;
    const ih = source.naturalHeight || source.height;
    if (!iw || !ih) return null;
    const c = document.createElement('canvas');
    c.width = iw; c.height = ih;
    const cx = c.getContext('2d', { willReadFrequently: true });
    cx.drawImage(source, 0, 0, iw, ih);
    const d = cx.getImageData(0, 0, iw, ih).data;
    const b = inkBounds(d, iw, ih); // ink-budget bounds, not an extremes bbox — see inkBounds
    if (!b) return null; // fully transparent — nothing to crop
    const { minX, minY, maxX, maxY } = b;
    const bw = maxX - minX + 1;
    const bh = maxY - minY + 1;
    if (bw >= iw * 0.99 && bh >= ih * 0.99) return null; // already tight — no-op
    const out = document.createElement('canvas');
    out.width = bw; out.height = bh;
    out.getContext('2d').drawImage(c, minX, minY, bw, bh, 0, 0, bw, bh);
    return { dataUrl: out.toDataURL('image/png'), canvas: out, width: bw, height: bh };
  } catch {
    return null; // tainted canvas / readback blocked → skip trim silently
  }
}

const UPLOAD_ICON =
  '<svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="currentColor" ' +
  'stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
  '<path d="M12 16V4M12 4l-4 4M12 4l4 4"/><path d="M4 16v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2"/></svg>';
